export const STRINGS = {
  // PWA #71 — wording shared across surfaces that surface mixed-state
  // entitlement (subscriptionRemaining=0 + pack/trial>0). Owned here as
  // the single source of truth so future copy edits propagate to all
  // three consumers (Start-Page PlanStatusCard, A-01 PlanStatusCard,
  // A-02 SubscriptionSummaryCard) without drift risk.
  shared: {
    mixedState: {
      depletionLineSubscription: 'Diesen Monat sind alle Abo-Geschichten aufgebraucht',
      reserveLinePack:
        'Zusätzlich verfügbar: {count} Geschichten aus deinem Paket (laufen nicht ab)',
      reserveLinePackOne: 'Zusätzlich verfügbar: 1 Geschichte aus deinem Paket (laufen nicht ab)',
      reserveLineTrial: 'Zusätzlich verfügbar: {count} Probier-Geschichten',
      reserveLineTrialOne: 'Zusätzlich verfügbar: 1 Probier-Geschichte',
    },
  },
  shell: {
    emailBanner: {
      text: 'Fast geschafft! Bitte bestätige deine E-Mail-Adresse, um Geschichten zu erstellen.',
      cta: 'E-Mail erneut senden',
    },
    // Task 3 Commit B (Sebastian ruling 2026-09-09) — TopBar gear
    // menu, audio → book direction. Gear stays as-is for single-
    // world customers (direct navigate to /account). For both-worlds
    // customers, gear becomes a Popover trigger with two entries:
    // "Profil" → /account (same destination the gear had before),
    // "Zu den Büchern" → /book. Rationale for reusing the gear vs a
    // separate text button: the gear already navigates to /account
    // which duplicates the Profil tab in the bottom bar; giving it
    // two named destinations avoids adding a fourth element to a
    // crowded header. A menu with two entries is a menu; the "no
    // Popover for a single entry" argument that killed Task 2's
    // rework does not apply here.
    topBar: {
      menuProfil: 'Profil',
      menuToBooks: 'Zu den Büchern',
    },
    nav: {
      start: 'Start',
      library: 'Bibliothek',
      new: 'Neu',
      inspiration: 'Inspiration',
      account: 'Profil',
    },
    worldSheet: {
      title: 'Wähle eine Welt:',
      description: 'Wähle eine der drei Story-Welten aus, um eine neue Geschichte zu erstellen.',
    },
    nowPlaying: {
      topNavTitle: 'Wird momentan abgespielt',
      ariaLabel: {
        close: 'Now-Playing schließen',
        menu: 'Menü öffnen',
        cover: 'Dreamable Cover',
      },
      menu: {
        goToStory: 'Zur Story',
        share: 'Teilen',
        rate: 'Bewerten',
        delete: 'Löschen',
        disabledHint: 'Kommt in Kürze',
      },
      sheet: {
        title: 'Schlaf-Timer',
        description: 'Wähle, wann die Wiedergabe automatisch enden soll.',
        options: {
          '15min': '15 Minuten',
          '30min': '30 Minuten',
          '1h': '1 Stunde',
          endOfStory: 'Ende der Geschichte',
          off: 'Aus',
        },
        ariaLabel: {
          close: 'Schlaf-Timer schließen',
        },
      },
    },
  },
  worlds: {
    aa: { tagline: 'Spannende Alltagsabenteuer' },
    sb: { tagline: 'Wissen für neugierige Kinder' },
    kp: { tagline: 'Einschlafgeschichten' },
  },
  startPage: {
    // Batch 10 Sub-phase F — single-line interpolation pattern per
    // Decision #7 (Briefing wording wins). Greeting component
    // refactored from 2-line (welcome line + firstName below) to
    // single-line interpolation. Old keys returningLine1/newUserLine1
    // removed; new keys match the new semantic.
    greeting: {
      returningTemplate: 'Hi {firstName}',
      returningFallback: 'Hi',
      newUserHeading: '💡 Willkommen!',
    },
    // F.4 (Cluster 4 Sub-Phase C, 2026-05-08) — "Eure Heldenchronik"
    // card. Verbatim emojis + briefing-locked wording per tracker
    // line 1696-1716. Multi-child handling: top-2 most-active
    // children rendered side-by-side with a `·` separator; 1-child
    // form spells out the level label.
    highlights: {
      heading: '🏆 Eure Heldenchronik',
      childLineSingle: '🦸 {childName}: {levelLabel} (Lvl {levelNumber})',
      childLineMulti: '🦸 {child1Name}: Lvl {N1} · {child2Name}: Lvl {N2}',
      storiesLine: '⚡ {totalStories} Abenteuer gemeistert',
      favoriteWorldLine: '🎯 Lieblings-Welt: {favoriteWorld}',
    },
    planStatus: {
      headline: 'Deine Geschichten-Credits',
      // Sub-phase F cleanup — `subtitleNoCredits` removed (orphan
      // since Sub-phase A added `subtitleZeroOfOne` for the empty
      // branch in derive-plan-status-props.ts).
      subtitleOneAvailable: '1 kostenlose Geschichte verfügbar',
      subtitleNAvailable: '{n} kostenlose Geschichten verfügbar',
      // Batch 10 Sub-phase B — in-place rewrite per Decision #7
      // (Briefing wording wins). Old value 'Jetzt Plan wählen' was
      // verbose; new value matches Briefing K-A-01 + Sebastian Gate-1
      // 2026-04-27. Same key, same consumers, terser copy.
      ctaChoosePlan: 'Plan wählen',
      ctaCreate: 'Jetzt Geschichte erstellen',
      // Batch 10 Sub-phase A — state-aware label discrimination
      // (PWA Open Items #49). Sebastian Gate-1 verbatim lock
      // 2026-04-27 — Decisions #16/#17. Subscription framing wins
      // over Pack which wins over Trial via precedence rule in
      // derive-plan-status-props.ts (Sub-phase B).
      headlineTrial: 'Freie Geschichten',
      headlinePack: 'Geschichten-Paket',
      headlineActiveTemplate: '{tierLabel}-Abo',
      subtitleZeroOfOne: '0 von 1 kostenlosen Geschichten verfügbar',
      // Subscription-Area Polish workstream 2026-04-30 — replaces the
      // ambiguous '{used} von {total} Geschichten diesen Monat' (which
      // didn't disambiguate "5 used of 5 available" vs "5 of 5 still
      // remaining"). The new wording leads with "Noch X" so the
      // remaining count is the prominent number, mirroring the A-02
      // SubscriptionSummaryCard counter for cross-surface consistency.
      subtitleSubscriptionUsage: 'Diesen Monat: Noch {remaining} von {total} Geschichten verfügbar',
      subtitlePackAvailable: '{n} Geschichten verfügbar',
      subtitlePackAvailableOne: '1 Geschichte verfügbar',
    },
    // SI-Inspiration 2026-05-11 (Sebastian Gate-1 Decision Q1 Option
    // A2): StartRoute renders the lead tip from getCurrentTip() —
    // the TipSection card heading + body come from the rotating
    // tip, not these static strings.
    //
    // `heading` is REPURPOSED post-verify (SI-Inspiration follow-up
    // 2026-05-11) as the SectionHeading label rendered ABOVE the
    // rotating TipSection card — singular ("💡 Tipp") because Start-
    // Tab shows only the lead card, mirroring the Inspiration-Tab's
    // plural `STRINGS.inspiration.tipsLabel` ("💡 Tipps") above the
    // 3-card trio.
    //
    // `body` remains deprecated (no consumer); schedule removal
    // post-launch. `linkLabel` drives the "Mehr Tipps im
    // Inspiration-Tab" CTA below the rotating tip body.
    tip: {
      /** Section-heading label rendered above the rotating TipSection card. */
      heading: '💡 Tipp',
      /** @deprecated since SI-Inspiration 2026-05-11 — no consumer. */
      body: 'Probiere heute eine Schlauberger-Geschichte — dein Kind lernt spielerisch etwas Neues!',
      linkLabel: 'Mehr Tipps im Inspiration-Tab',
    },
    // Batch 10 Sub-phase F — newUser welcome card. Heading + body
    // were locked verbatim by Sebastian Gate-1 2026-04-27 with explicit
    // briefing deviation: heading "💫 Los geht's" (briefing-silent —
    // CC-suggested complement to the page-level "💡 Willkommen!"
    // greeting), body shortened from briefing wording (removed
    // "los geht's!" tail to avoid in-card duplication with heading).
    welcome: {
      heading: "💫 Los geht's",
      body: 'Erstelle deine erste personalisierte Geschichte — wähle einfach eine Welt aus!',
    },
    worlds: {
      heading: 'Welten entdecken',
    },
    // Batch 10 Sub-phase F — Decision #7 (Briefing wording wins).
    stories: {
      heading: 'Letzte Geschichten',
      allLink: 'Alle Geschichten in der Bibliothek',
    },
    emptyStories: {
      heading: 'Noch keine Geschichten',
      body: 'Wähle oben eine Welt und erstelle deine erste Geschichte!',
    },
    errors: {
      storiesLoadFailed: 'Geschichten konnten nicht geladen werden.',
      retry: 'Erneut versuchen',
    },
  },
  // Batch 10 — Inspiration-Tab (Briefing Kap. 5 — illustrative chapter
  // content; Sebastian Gate-1 verbatim lock 2026-04-27). Mai-Launch
  // hardcoded composition: TipSection hero + Story-Ideen-Carousel +
  // Feature-Discovery cards. Decision #3 — flat list, no backend
  // tracking, no personalization.
  inspiration: {
    pageHeading: 'Inspiration',
    // SI-Inspiration 2026-05-11 — renamed from `tipOfWeekLabel`
    // (singular "Tipp der Woche"). Variant Y renders a trio of tips
    // + an expander for the remaining pool, so the section heading
    // is plural and cadence-agnostic.
    tipsLabel: '💡 Tipps',
    storyIdeasHeading: 'Story-Ideen für diese Woche',
    featureDiscoveryHeading: 'Hast du schon entdeckt?',
    // Decision #5 — optional Mai-Launch surface; render only if
    // archive-page work fits Sub-phase C, else defer to post-launch.
    archiveLink: 'Vergangene Tipps anzeigen',
  },
  // Batch 12 Sub-phase C — 404 Fallback-Route (Briefing Kap. 11 line
  // 1550-1560). Briefing-locked verbatim copy. illustrationAlt matches
  // the actual `flitzi.jpg` asset content (Flitzi mit Werkzeug);
  // briefing prescribes Flitzi mit Fragezeichen, asset swap tracked
  // as PWA Offen #57.
  notFound: {
    title: 'Hoppla! Diese Seite gibt es nicht.',
    body: 'Vielleicht wurde sie verschoben oder der Link ist fehlerhaft.',
    ctaPrimary: 'Zur Startseite',
    ctaSecondary: 'Zur Bibliothek →',
    illustrationAlt: 'Flitzi mit Werkzeug',
  },
  // Briefing Kap. 11 line 1562-1579 — Error-Boundary fallback for
  // unhandled React JS-errors. Verbatim Briefing-locked copy.
  // Consumed by `pwa/src/components/TechnicalErrorFallback.tsx` via
  // the `<Sentry.ErrorBoundary>` mounted in `pwa/src/main.tsx`.
  errorBoundary: {
    title: 'Oops! Da ist etwas schiefgelaufen.',
    subText: 'Flitzi kümmert sich sofort darum.',
    ctaPrimary: 'Seite neu laden',
    ctaSecondary: 'Zur Startseite →',
    footerPrefix: 'Falls das Problem weiterhin besteht, kontaktiere uns unter ',
    footerEmail: 'support@dreamable.kids',
    illustrationAlt: 'Flitzi mit Werkzeug, der das Problem repariert',
  },
  // P14 Block 4 (Sebastian ruling 2026-09-23) — redeem page copy
  // and the account-screen "Guthaben" line. All error-state strings
  // come from the backend error catalog verbatim; do NOT duplicate
  // them here.
  gift: {
    redeem: {
      pageTitle: 'Gutschein einlösen · Dreamable',
      heading: 'Gutschein einlösen',
      intro: 'Gib deinen Code ein oder prüfe, ob er schon eingetragen ist.',
      inputLabel: 'Gutscheincode',
      inputPlaceholder: 'DREAM-XXXX-XXXX',
      helper: 'Format DREAM-XXXX-XXXX. Groß- und Kleinschreibung ist egal.',
      submitButton: 'Einlösen',
      checkingBody: 'Wir prüfen deinen Code …',
      successHeading: 'Guthaben ist da',
      // {credited_eur} and {new_balance_eur} interpolated via
      // Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" })
      // — same "55,75 €" shape the block-3 mails use.
      successBody:
        '{credited_eur} wurden deinem Konto gutgeschrieben.\nNeues Guthaben: {new_balance_eur}.',
      primaryCta: 'Buch gestalten',
      // Generic fallback for a network / envelope-parse failure —
      // catalog error_codes carry their own user_message which we
      // render verbatim; this string only fires when the backend
      // returns nothing usable at all.
      genericError: 'Etwas ist schiefgegangen. Bitte versuche es erneut.',
    },
  },
  account: {
    greeting: 'Hi {firstName}',
    greetingFallback: 'Hi',
    // Sebastian ruling 2026-09-15 round 4 — back-button labels used
    // by /account/password + /account/delete via
    // `getAccountReturnTarget`. Split from per-route `.back` fields
    // so the two routes share one source of truth for the world-
    // dependent return path.
    shared: {
      backToAccount: '← Zurück',
      backToBook: 'Zurück zu meinen Büchern',
    },
    sections: {
      meinKonto: 'Mein Konto',
      rechtliches: 'Rechtliches',
    },
    // P14 Block 4 (2026-09-23) — single-line balance display below
    // PlanStatusCard, only rendered when balanceCents > 0. Label
    // deliberately "Guthaben" (not "Gutscheinguthaben") because the
    // balance will pay for story packages once that path lands
    // (Sebastian ruling 2026-09-23). {amount} interpolates via
    // Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).
    balance: {
      line: 'Guthaben: {amount}',
    },
    links: {
      meinProfil: 'Mein Profil',
      // D16 (Batch 11 Commit 1) — Briefing K-A-01 menu list. Inserted at
      // position 2 (after meinProfil, before kinderprofile) per CC-suggested
      // placement Sebastian-confirmed 2026-04-26. State-agnostic: link
      // renders for ALL subscription states including trial.
      meinAbo: 'Mein Abo',
      kinderprofile: 'Kinderprofile',
      bibliothek: 'Bibliothek',
      benachrichtigungen: 'Benachrichtigungen',
      passwort: 'Passwort ändern',
      hilfe: 'Hilfe und FAQ',
      datenexport: 'Daten exportieren',
      kontoLoeschen: 'Konto löschen',
      terms: 'Nutzungsbedingungen',
      privacy: 'Datenschutz',
      impressum: 'Impressum',
    },
    logout: 'Abmelden',
    // §356a BGB Widerrufs-Flow (L.3). Button labels are legal-locked.
    withdrawal: {
      // legal-locked (GreenGate §356a) — NEVER edit without legal
      button: 'Vertrag widerrufen',
      confirmButton: 'Widerruf bestätigen',
      // brand-voice
      maskHeading: 'Vertrag widerrufen',
      maskIntro:
        'Deine Angaben sind schon ausgefüllt. Prüf sie kurz und bestätige unten – wir schicken dir sofort eine Eingangsbestätigung per E-Mail.',
      fieldName: 'Name',
      fieldContract: 'Vertrag',
      fieldEmail: 'E-Mail für die Eingangsbestätigung',
      cancel: 'Abbrechen',
      confirmHeading: 'Widerruf eingegangen',
      confirmBody:
        'Wir haben deinen Widerruf erhalten und dir eine Eingangsbestätigung per E-Mail geschickt – mit allen Angaben und dem genauen Zeitpunkt des Eingangs.',
      backToAccount: 'Zurück zum Konto',
      errorNotEligible: 'Dieser Vertrag kann nicht mehr widerrufen werden.',
    },
    subscription: {
      title: 'Dein Abo',
      back: '← Zurück',
      aktuellerPlan: 'Aktueller Plan',
      // Bug PWA-A polish — subdued in-flight indicator shown beside the
      // reactivate CTA while the request is in flight (Sebastian copy-lock,
      // verb-consistent with the "Kündigung zurückziehen" CTA).
      reactivating: 'Kündigung wird zurückgezogen…',
      // D1 — subdued in-flight indicator beside the "Plan-Wechsel rückgängig
      // machen" CTA while the discard request is in flight (Sebastian
      // copy-lock, verb-consistent with the CTA).
      discarding: 'Plan-Wechsel wird rückgängig gemacht…',
      // Subscription-Area Polish workstream 2026-04-30 — replaces the
      // ambiguous '{used} von {total} Geschichten' (couldn't tell
      // "5 used of 5" vs "5 of 5 still remaining" apart). The new
      // wording leads with "Noch X" so the remaining count is the
      // prominent number, mirroring the Start-Page + A-01 PlanStatusCard.
      usage: 'Diesen Monat: Noch {remaining} von {total} Geschichten verfügbar',
      // Refresh-line: when the next monthly batch of stories arrives.
      // Sourced from currentPeriodEnd (always +1 month per Backend #39
      // doctrine, regardless of billingInterval).
      refreshLine: '{count} neue Geschichten ab {date}',
      // Renewal-line: when Stripe charges next. For monthly subs this
      // coincides with refreshLine's date; for annual subs it points
      // ~12 months out via stripeBillingCycleEnd. Both lines render
      // unconditionally for cross-tier consistency (Sebastian explicit
      // Sub-Phase D lock 2026-04-30).
      renewalLine: 'Nächste Abrechnung: {date}',
      // Out-of-credits CTA, rendered between counter row and date
      // rows when subscriptionRemaining === 0. Smooth-scrolls to the
      // BrowseMode section below the card.
      ctaWeiterErstellen: 'Jetzt weiter Geschichten erstellen',
      // Sub-Phase E (S4 parallel sub + pack handling) — additive card
      // rendered between SummaryCard and BrowseMode when the user holds
      // pack credits alongside their active subscription. Pure data-
      // driven from useEntitlement().packRemaining; hidden when 0.
      activePackagesBody: 'Verfügbar: {count} Geschichten aus Paketen',
      activePackagesBodyOne: 'Verfügbar: 1 Geschichte aus Paketen',
      activePackagesSubtext: '(laufen nicht ab)',
      manageStripeCta: 'Abo verwalten bei Stripe',
      cancelLink: 'Abo kündigen',
      // Browse-Mode (Batch 11 Commit 2). Tier-cards + Yearly/Monthly
      // toggle. CTA-state matrix labels (upgrade / downgrade / trial /
      // reactivateFromCancel / cancelDowngrade) land in Commit 3.
      browseMode: {
        // Briefing K-A-02 verbatim
        heading: 'Abos',
        // Briefing K-A-02 verbatim — the "-20%" suffix was removed after
        // the 2026-07-03 pricing flip (annual discount is now 16.7%, not
        // 20%). The number is intentionally NOT shown; the hint below
        // frames the annual value non-numerically, matching the Webflow
        // pricing page wording.
        toggleYearly: 'Jährlich',
        // Briefing K-A-02 verbatim
        toggleMonthly: 'Monatlich',
        // 2026-07-03 pricing-flip follow-up. Shown as a caption below
        // the toggle when the yearly option is active. Verbatim, must
        // match the Webflow pricing page ("2 Monate geschenkt", no
        // dash variants).
        toggleYearlyHint: '2 Monate geschenkt',
        // PWA-B grid headers — explain why tier cards are non-actionable while
        // a pending change is open. {newPlanName} interpolated with the
        // pendingDowngradeTierLabel (same {placeholder} convention as price/
        // storiesPerMonth above).
        pendingDowngradeHeader:
          'Aktuell wechselst du zum {newPlanName} — mach den Wechsel zuerst rückgängig, bevor du neu wählst.',
        pendingCancelHeader:
          'Dein Abo ist gekündigt — zieh die Kündigung zurück, um deinen Plan zu behalten.',
        tier: {
          // Briefing K-A-02 verbatim short-form names (also reflected in
          // the backend PLAN_CATALOG planName field; STRINGS values are
          // the source for the card title — backend planName is the
          // long-form `Entdecker — Monatlich` not appropriate for the
          // card heading).
          entdeckerName: 'Entdecker',
          abenteurerName: 'Abenteurer',
          profiName: 'Geschichten-Profi',
          // Briefing K-A-02 verbatim "{X.XX} EUR/Monat"
          priceLineMonthly: '{price} EUR/Monat',
          // 🆕 CC-suggested per Sebastian 2026-04-26 (Briefing K-A-02
          // silent on yearly-billed inline disclosure; CC-form prevents
          // UX confusion under yearly toggle showing monthly-equivalent
          // price).
          priceLineYearly: '{price} EUR/Monat (Abrechnung jährlich)',
          // Briefing K-A-02 verbatim
          storiesPerMonth: '{count} Geschichten pro Monat',
        },
        cta: {
          // Briefing K-A-02 verbatim — disabled badge on the user's
          // current-plan card.
          currentPlanBadge: 'Dein aktueller Plan',
          // 🆕 Commit 2 placeholder retained for the
          // pending-cancel-target-card-not-current-tier fallback (no
          // briefing-spec'd label for that combo); also used during
          // the worlds-fetch race window when state-matrix derivation
          // can't resolve the card's variant yet.
          choosePlanPlaceholder: 'Plan wählen',
          // ── Briefing K-A-02 state-matrix labels (Commit 3) ──
          // Briefing K-A-02 verbatim — lower-tier card vs user's
          // current-tier (e.g. user on Profi viewing Entdecker).
          upgrade: 'Upgrade',
          // Briefing K-A-02 verbatim — higher-tier card vs user's
          // current-tier (e.g. user on Entdecker viewing Profi).
          downgrade: 'Downgrade',
          // Briefing K-A-02 verbatim — trial-state user on every card.
          trial: 'Abo abschließen',
          // Briefing K-A-02 verbatim — pending-cancel state, every
          // card carries this CTA (Briefing: "Plan wählen und
          // reaktivieren" — combo of pick-tier + reactivate).
          reactivateFromCancel: 'Plan wählen und reaktivieren',
          // Briefing K-A-02 verbatim — pending-downgrade-target-undercut.
          // The user has scheduled a downgrade to tier X; the card for
          // tier X carries this CTA so they can undo the downgrade
          // without leaving A-02.
          cancelDowngrade: 'Downgrade rückgängig machen',
        },
        errors: {
          // 🆕 CC-suggested per Sebastian 2026-04-26 (Browse-Mode
          // surfaces the cached plans-fetch error inline — same tone
          // as existing subscription-state error copy).
          loadFailed: 'Plan-Katalog konnte nicht geladen werden. Bitte versuche es erneut.',
        },
      },
      // Einmalpakete (Batch 11 Commit 4). State-agnostic — always
      // visible regardless of subscription state. Briefing K-A-02
      // line 332-334 + line 347-348.
      packs: {
        // Briefing K-A-02 line 332 verbatim.
        heading: 'Einmalpakete',
        // Briefing K-A-02 verbatim — flat CTA (no state matrix; packs
        // are always orderable).
        cta: 'Kaufen',
        // Briefing K-A-02 line 333-334 — the count IS the card title;
        // backend `planName` ("Geschichtenpaket — 10 Geschichten") is
        // verbose and redundant when paired with a card layout.
        title: '{count} Geschichten',
        // Briefing K-A-02 line 347 verbatim "{N} €... einmalig".
        priceLine: '{amount} € (einmalig)',
        // Briefing K-A-02 line 334 verbatim — "-20%"-Badge on pack-25
        // only, comparing the per-story value to the 10er-Paket.
        discountHint: '-20%',
      },
      lifecycle: {
        pendingCancel: {
          headlineCard: '{tierLabel}',
          highlightPrefix: 'Gekündigt — läuft aus am {date}.',
          highlightBody:
            'Du kannst bis dahin weitere Geschichten erstellen. Deine bestehende Bibliothek bleibt dir auch danach erhalten, solange dein Konto besteht.',
          ctaLabel: 'Kündigung zurückziehen',
          // Compact form for Start-Page + A-01 PlanStatusCard (subtitle slot only)
          compactSubtitle:
            'Gekündigt — läuft aus am {date}. Du kannst bis dahin weitere Geschichten erstellen.',
        },
        pastDue: {
          headlineCard: 'Zahlung fehlgeschlagen',
          highlightPrefix: 'Zahlung fehlgeschlagen.',
          // 🔁 D9 path-(b) per Sebastian 2026-04-26 — copy aligned to
          // Stripe-default Smart Retry window (≈3 Wochen). Previously
          // "innerhalb der nächsten 7 Tage" which mismatched operational
          // reality. Re-evaluation tracker entry: PWA #41 (Closing
          // Commit 7).
          highlightBody:
            'Wir konnten deine letzte Zahlung nicht einziehen. Bitte aktualisiere deine Zahlungsmethode innerhalb der nächsten Wochen.',
          ctaLabel: 'Zahlungsmethode aktualisieren',
          compactSubtitle:
            'Wir konnten deine letzte Zahlung nicht einziehen. Bitte aktualisiere deine Zahlungsmethode.',
        },
        pendingPlanChange: {
          headlineCard: '{tierLabel}',
          highlightPrefix: 'Plan-Wechsel geplant — wirksam ab {date}.',
          highlightBody:
            'Du wechselst auf {newPlanName}. Bis dahin bleibt dein aktueller Plan {currentPlanName} aktiv.',
          ctaLabel: 'Plan-Wechsel rückgängig machen',
          compactSubtitle:
            'Plan-Wechsel geplant — wirksam ab {date}. Du wechselst auf {newPlanName}.',
        },
      },
    },
    cancelModal: {
      headline: 'Abo kündigen?',
      body: 'Du kannst bis zum {date} weiter Geschichten erstellen. Deine bestehende Bibliothek bleibt dir auch danach erhalten, solange dein Konto besteht.',
      primaryCta: 'Kündigung fortsetzen',
      secondaryCta: 'Abbrechen',
    },
    children: {
      title: 'Kinderprofile',
      back: '← Zurück',
      // Briefing line 403 — verbatim "Neues Kinderprofil" (NOT "Kind
      // hinzufügen"). The lucide Plus icon is rendered by the button
      // component itself; the leading "+ " literal was removed in the
      // Batch 8 Library bug-fix commit (2026-04-26) to fix the
      // double-plus rendering bug.
      addCta: 'Neues Kinderprofil',
      // Briefing line 414 — verbatim
      emptyHeading: 'Noch keine Kinderprofile',
      emptyBody: 'Erstelle ein Kinderprofil, um personalisierte Geschichten zu generieren.',
      emptyCta: 'Erstes Kinderprofil',
      ageSuffix: 'Jahre',
      editAriaLabel: 'Profil bearbeiten',
      deleteAriaLabel: 'Profil löschen',
      // A-08 Delete-Child-Confirm — Flag 3 verbatim
      deleteModal: {
        headline: 'Kinderprofil löschen?',
        body: 'Geschichten bleiben in der Bibliothek erhalten, sind aber nicht mehr mit {name} verknüpft.',
        primaryCta: 'Löschen',
        secondaryCta: 'Abbrechen',
      },
      errors: {
        loadFailed: 'Kinderprofile konnten nicht geladen werden.',
        deleteFailed: 'Profil konnte nicht gelöscht werden. Bitte versuche es erneut.',
        networkError: 'Keine Verbindung. Bitte prüfe deine Internetverbindung.',
      },
    },
    childProfile: {
      titleNew: 'Kinderprofil hinzufügen',
      titleEdit: 'Kinderprofil bearbeiten',
      titleMulti: 'Kinderprofile bearbeiten',
      back: '← Zurück',
      selectorLabel: 'Kind wählen',
      submitNew: 'Profil speichern',
      submitEdit: 'Änderungen speichern',
      submitLoading: 'Wird gespeichert…',
      // A-12 (Briefing verbatim two-CTA modal — mode='new' only)
      saveSuccessModal: {
        headline: 'Gespeichert!',
        body: 'Möchtest du ein weiteres Kinderprofil anlegen?',
        primaryCta: 'Weiteres Kind',
        secondaryCta: 'Nein',
      },
      // A-11 dirty-switch ConfirmModal (CC's Gate-1 wording, secondary
      // CTA tweak per Sebastian's Flag 2 — symmetry with primary)
      dirtySwitchModal: {
        headline: 'Änderungen speichern?',
        body: 'Du hast ungespeicherte Änderungen. Möchtest du sie speichern, bevor du wechselst?',
        primaryCta: 'Speichern und wechseln',
        secondaryCta: 'Verwerfen und wechseln',
      },
      errors: {
        loadFailed: 'Profil konnte nicht geladen werden.',
        saveFailed: 'Profil konnte nicht gespeichert werden. Bitte versuche es erneut.',
        networkError: 'Keine Verbindung. Bitte prüfe deine Internetverbindung.',
        listFailed: 'Kinderprofile konnten nicht geladen werden.',
      },
    },
    errors: {
      portalSessionFailed:
        'Verbindung zum Stripe-Portal konnte nicht hergestellt werden. Bitte versuche es erneut.',
      reactivateFailed: 'Reaktivierung fehlgeschlagen. Bitte versuche es erneut.',
      discardFailed:
        'Der Plan-Wechsel ließ sich nicht rückgängig machen — bitte versuche es erneut.',
      subscriptionStateFailed: 'Abo-Status konnte nicht geladen werden. Bitte versuche es erneut.',
    },
    // A-14 Notification-Center (Batch 9C — Briefing line 522-553)
    notifications: {
      title: 'Benachrichtigungen',
      back: '← Zurück',
      markAllRead: 'Alle gelesen',
      newBadge: 'NEU',
      empty: 'Keine Benachrichtigungen',
      pushToggle: {
        label: 'Push-Benachrichtigungen',
        info: 'Erhalte Nachrichten auch wenn die App nicht geöffnet ist.',
      },
      errors: {
        loadFailed: 'Benachrichtigungen konnten nicht geladen werden.',
        // Refined per Flag 3 — clearer full-sentence than "Markieren fehlgeschlagen"
        markReadFailed: 'Konnte nicht als gelesen markiert werden.',
        networkError: 'Keine Verbindung. Bitte prüfe deine Internetverbindung.',
      },
    },
    // A-15 Konto löschen (Batch 9C — Briefing line 555-581 verbatim
    // + Flag 5/6 decisions)
    delete: {
      title: 'Konto löschen',
      subtitle: 'Schade, dass du Dreamable verlässt.',
      warningHeading: 'Beim Löschen passiert:',
      // Sebastian ruling 2026-09-15 round 4 — split into three
      // buckets so the panel renders items that are true for the
      // customer's actual worlds. `always` renders for every
      // customer; `audio` only when `hasAudioWorld`; `book` only
      // when `hasBookWorld`. For an audio-only customer today the
      // rendered list is `[...always, ...audio]` — byte-verbatim to
      // the pre-split five items in the same order (Briefing line
      // 555 preserved). Existing five lines are UNCHANGED; only the
      // book line was authored new (verbatim from Sebastian).
      warningItems: {
        always: ['Konto + Daten dauerhaft gelöscht', 'Alle Geschichten + Profile gelöscht'],
        audio: [
          'Audio/PDF entfernt',
          'Laufende Abos gekündigt',
          'Pakete verfallen ohne Erstattung',
        ],
        book: 'Angefangene Bücher werden gelöscht. Bereits bestellte und bezahlte Bücher werden weiterhin gedruckt und geliefert.',
      },
      errorHeading: 'Diese Aktion kann nicht rückgängig gemacht werden.',
      reasonLabel: 'Warum gehst du?',
      reasonPlaceholder: 'Bitte wählen (optional)',
      reasonOptions: {
        too_little_use: 'Zu wenig genutzt',
        too_expensive: 'Zu teuer',
        alternatives_better: 'Finde Alternativen besser',
        privacy_concerns: 'Datenschutz-Bedenken',
        other: 'Anderer Grund',
      },
      feedbackLabel: 'Gib uns gerne ein Feedback',
      feedbackPlaceholder: 'Optional…',
      // Briefing line 574 verbatim — case-sensitive trigger phrase.
      confirmInputLabel: "Tippe 'KONTO LÖSCHEN' um fortzufahren",
      confirmInputRequired: 'KONTO LÖSCHEN',
      submitButton: 'Konto endgültig löschen',
      submitLoading: 'Wird gelöscht…',
      cancelButton: 'Abbrechen',
      errors: {
        networkError: 'Keine Verbindung. Bitte prüfe deine Internetverbindung.',
        deleteFailed: 'Konto konnte nicht gelöscht werden. Bitte versuche es erneut.',
      },
    },
    // A-16 Datenexport (Batch 9C — Briefing line 583-595 verbatim;
    // mailto prefill per Flag 6)
    export: {
      title: 'Meine Daten exportieren',
      back: '← Zurück',
      body: 'Nach der EU-Datenschutz-Grundverordnung hast du das Recht, deine Daten in einem maschinenlesbaren Format zu erhalten.',
      hint: 'Um einen Export zu erhalten, sende uns bitte eine E-Mail an support@dreamable.kids — wir kümmern uns innerhalb von 7 Tagen darum.',
      cta: 'E-Mail an Support',
      mailtoSubject: 'Datenexport-Anfrage',
      // {email} interpolation at the call-site — see ExportRoute.tsx
      mailtoBodyTemplate:
        'Hallo,\n\nich möchte einen Export meiner Daten beantragen.\nMein Account: {email}\n\nViele Grüße',
    },
    // A-04 Mein Profil (Batch 9C — Briefing-silent, copy locked at
    // Gate-1 Flag 3+4)
    profile: {
      title: 'Mein Profil',
      back: '← Zurück',
      firstNameLabel: 'Vorname',
      lastNameLabel: 'Nachname',
      submit: 'Änderungen speichern',
      submitLoading: 'Wird gespeichert…',
      success: 'Profil aktualisiert',
      errors: {
        required: 'Dieses Feld ist erforderlich.',
        networkError: 'Keine Verbindung. Bitte prüfe deine Internetverbindung.',
        saveFailed: 'Profil konnte nicht gespeichert werden. Bitte versuche es erneut.',
      },
    },
    // A-13 Passwort-Change-Page (Briefing line 497-520 verbatim)
    password: {
      title: 'Passwort ändern',
      infoText: 'Aus Sicherheitsgründen brauchen wir dein aktuelles Passwort.',
      currentLabel: 'Aktuelles Passwort',
      newLabel: 'Neues Passwort',
      confirmLabel: 'Neues Passwort bestätigen',
      showPassword: 'Zeigen',
      hidePassword: 'Verbergen',
      submit: 'Passwort ändern',
      submitLoading: 'Wird gespeichert…',
      // Live ✓/✗ checklist labels (matches backend regex)
      requirements: {
        minLength: 'Mind. 8 Zeichen',
        uppercase: 'Mind. 1 Großbuchstabe',
        digit: 'Mind. 1 Zahl',
      },
      // A-13 Success-Modal (Flag 6 verbatim, single-CTA shadcn Dialog)
      successModal: {
        headline: 'Passwort geändert',
        body: 'Du wurdest auf allen anderen Geräten abgemeldet. Du musst dich dort neu einloggen.',
        cta: 'Schließen',
      },
      errors: {
        required: 'Dieses Feld ist erforderlich.',
        confirmMismatch: 'Die neuen Passwörter stimmen nicht überein.',
        wrongCurrent: 'Aktuelles Passwort ist falsch.',
        weakNewPassword: 'Das neue Passwort erfüllt nicht die Mindestanforderungen.',
        networkError: 'Keine Verbindung. Bitte prüfe deine Internetverbindung.',
        changeFailed: 'Passwort konnte nicht geändert werden. Bitte versuche es erneut.',
      },
    },
  },
  // ── D-09 Bestellübersicht (Batch 11 Commit 5) ───────────────
  // Briefing K-11. The page users see between A-02 plan-pick and the
  // Stripe-hosted Checkout redirect. consentCheckbox + footerText are
  // briefing-LOCKED (Verbatim-Wordings appendix); do not edit.
  checkout: {
    review: {
      // Briefing K-11 verbatim
      title: 'Bestellübersicht',
      // Briefing K-11 verbatim
      subheading: 'Bitte überprüfe deine Bestellung, bevor du zur Zahlung weitergeleitet wirst.',
      // Briefing K-11 verbatim — Back link target /account/subscription
      back: '← Zurück',
      fieldLabels: {
        // Briefing K-11 verbatim (each)
        tariff: 'Tarif:',
        price: 'Preis:',
        duration: 'Laufzeit:',
        storiesPerMonth: 'Geschichten pro Monat:',
        storiesPack: 'Geschichten:',
        includedServices: 'Inkludierte Leistungen:',
        totalAmount: 'Zahlungsbetrag:',
      },
      fieldValues: {
        // Briefing K-11 line 1478 verbatim (Item C strings-audit
        // refinement). Subscription-monthly: auto-renewal disclosure.
        durationSubscriptionMonthly: 'Monatlich, verlängert sich automatisch',
        // Briefing K-11 line 1478 verbatim. Subscription-yearly:
        // 12-month auto-renewal.
        durationSubscriptionYearly: '12 Monate, verlängert sich automatisch',
        // Briefing K-11 line 1478 verbatim. Pack: explicit no-renewal
        // disclosure.
        durationPack: 'Kein Abo, kein Ablaufdatum',
        // Briefing K-A-02 / legacy bestelluebersicht_v2.js price
        // format. {amount} substituted with formatEuroAmount.
        priceMonthly: '{amount} €/Monat',
        // Briefing K-A-02 yearly format: total annual amount with
        // monthly equivalent in parentheses (Item C refinement —
        // legacy reference).
        priceYearly: '{totalAmount} €/Jahr ({perMonthAmount} €/Monat)',
        // Briefing K-A-02 / legacy pack format: one-time charge with
        // explicit "(einmalig)" suffix.
        pricePack: '{amount} € (einmalig)',
        // Pack-only stories field VALUE (the field LABEL is
        // fieldLabels.storiesPack). {count} substituted from
        // PublicPlan.storiesGranted.
        storiesPackValue: '{count} Geschichten',
        // Subscription-only stories field VALUE. {count} from
        // PublicPlan.storiesPerMonth.
        storiesPerMonthValue: '{count}',
        // Final total (bold). {amount} = full billing-period charge
        // (NOT monthly-equivalent — this is what Stripe actually
        // debits).
        totalAmountValue: '{amount} €',
      },
      // Briefing K-11 verbatim (D5) — single static line, all worlds
      // explicitly named per Sebastian's verbatim-rule on this surface
      // (mockup truncated to "Audio, PDF, alle Welten" was rejected at
      // Discovery time).
      includedServicesValue:
        'Audio, PDF, Geschwisterprofile, alle Dreamable-Welten (Abenteuer Alltag, Schlauberger, Kuschelpatrouille)',
      // 🔒 LOCKED — Briefing Verbatim-Wordings appendix. Legal-reviewed
      // §356 BGB withdrawal-waiver consent string. NEVER edit without
      // legal sign-off.
      consentCheckbox:
        'Ich stimme ausdrücklich zu, dass Dreamable sofort mit der Ausführung des Vertrags beginnt, und mir ist bekannt, dass ich mein Widerrufsrecht mit Beginn der Ausführung des Vertrags verliere.',
      // Briefing K-11 verbatim — Dream-Gold primary CTA, disabled
      // until consentCheckbox is checked.
      primaryCta: 'Weiter zur Zahlung',
      // Loading/in-flight CTA label (CC-suggested — briefing-silent on
      // the in-flight state; brand-consistent with other PWA loading
      // labels).
      primaryCtaLoading: 'Wird weitergeleitet…',
      // 🔒 LOCKED — Briefing Verbatim-Wordings appendix. Stripe-handoff
      // disclosure, also references AGB / Datenschutz; legal-reviewed.
      footerText:
        "Mit dem Klick auf 'Weiter zur Zahlung' wirst du zu unserem Zahlungsdienstleister Stripe weitergeleitet. Es gelten unsere Nutzungsbedingungen und unsere Datenschutzerklärung.",
      errors: {
        // 🆕 CC-suggested per Sebastian 2026-04-26 — briefing-silent;
        // tone matches other PWA error-states.
        planNotFound: 'Diesen Tarif gibt es nicht. Bitte wähle einen Tarif aus der Übersicht.',
        // 🆕 CC-suggested per Sebastian 2026-04-26 — generic 4xx/5xx
        // failure during the create-consent-and-checkout call.
        checkoutFailed: 'Checkout konnte nicht gestartet werden. Bitte versuche es erneut.',
        // 🆕 CC-suggested per Sebastian 2026-04-26 (matches existing
        // PWA convention for NetworkError discrimination).
        networkError: 'Keine Verbindung. Bitte prüfe deine Internetverbindung.',
        // CTA label for the plan-not-found error block.
        planNotFoundCta: '← Zurück zur Übersicht',
        // Bug #4 (2026-05-13) — duplicate-active-subscription guard.
        // Sebastian-locked verbatim. Backend error_code:
        // 'subscription_already_active' (HTTP 409). PWA inspects
        // ApiError.errorCode and renders the alreadyActive banner +
        // CTA instead of the generic checkoutFailed copy.
        alreadyActive:
          'Du hast bereits ein aktives Abo. Plan-Änderungen über die Account-Einstellungen.',
        alreadyActiveCta: 'Zu deinem Abo',
      },
    },
    // ── D-10 Bestellung-erfolgreich (Batch 11 Commit 6) ─────────
    // Briefing K-11 D-10. Page users see post-Stripe-Checkout return.
    // Polls /api/credits/entitlement for state-transitions (sub
    // provisioning OR pack credit grant), times out at 30s.
    success: {
      polling: {
        // Briefing K-11 verbatim — subscription branch
        headingSubscription: 'Dein Abo wird aktiviert...',
        // Briefing K-11 verbatim — pack branch
        headingPack: 'Dein Geschichtenpaket wird vorbereitet...',
        // 🆕 CC-suggested per Sebastian 2026-04-26 — briefing-silent
        // bridge copy while waiting; brand-voice consistent.
        subtext: 'Einen Moment bitte — wir richten alles für dich ein.',
      },
      success: {
        // Briefing K-11 verbatim
        heading: 'Geschafft!',
        // Briefing K-11 verbatim — subscription detail
        subscriptionDetail: 'Dein Abo wurde aktiviert: {planName}',
        // Briefing K-11 verbatim — pack detail
        packDetail: 'Dein Geschichtenpaket ist verfügbar: {count} Geschichten',
        // 🆕 CC-suggested per Sebastian 2026-04-26 — bridge copy
        // between detail and primary CTA; brand-voice.
        cta: 'Lass uns deine erste Geschichte erstellen!',
        // Briefing K-11 verbatim
        ctaButton: 'Erste Geschichte erstellen',
        // Briefing K-11 verbatim
        ctaSecondary: 'Zur Übersicht',
      },
      timeout: {
        // Briefing K-11 verbatim
        heading: 'Es dauert länger als erwartet...',
        // Briefing K-11 verbatim
        subtext: 'Versuche es bitte in einigen Minuten erneut, oder kontaktiere den Support.',
        // Briefing K-11 verbatim
        cta: 'Zurück zur Übersicht',
      },
      // Direct-URL / bookmarked / invalid session_id state.
      // Briefing-silent; CC-suggested per Sebastian 2026-04-26.
      invalidSession: {
        heading: 'Etwas ist schief gelaufen',
        subtext:
          'Wir konnten deine Bestellung nicht finden. Bitte versuche es erneut über die Übersicht.',
        cta: 'Zur Übersicht',
      },
    },
    // ── D-11 Bestellung-abgebrochen (Batch 11 Commit 7) ─────────
    // Briefing K-11 D-11. Stripe redirects here when the user clicks
    // "Zurück" on Stripe Checkout or closes the page without completing
    // payment. Single-state page — no polling, no async, no fetch.
    cancel: {
      // Briefing K-11 verbatim
      heading: 'Bestellung abgebrochen',
      // Briefing K-11 verbatim
      body: 'Du hast deine Bestellung abgebrochen — kein Problem. Du wurdest nicht belastet.',
      // Briefing K-11 verbatim — primary CTA returns to home
      primaryCta: 'Zurück zur Übersicht',
      // Briefing K-11 verbatim — secondary CTA returns to A-02
      secondaryCta: 'Erneut versuchen',
    },
  },
  auth: {
    login: {
      title: 'Anmelden',
      emailLabel: 'E-Mail-Adresse *',
      passwordLabel: 'Passwort *',
      showPassword: 'Zeigen',
      hidePassword: 'Verbergen',
      forgotPassword: 'Passwort vergessen?',
      submitButton: 'Anmelden',
      loadingState: 'Wird angemeldet…',
      noAccount: 'Noch kein Konto?',
      signupLink: 'Kostenlos registrieren',
      // Batch 9C A-15 polish (Flag 5) — neutral info-tone banner
      // shown after Account-Delete redirects to login.
      accountDeletedBanner: 'Dein Konto wurde gelöscht.',
    },
    forgot: {
      title: 'Passwort vergessen?',
      description: 'Gib deine E-Mail-Adresse ein und wir schicken dir einen Link zum Zurücksetzen.',
      emailLabel: 'E-Mail-Adresse *',
      submitButton: 'Link senden',
      loadingState: 'Wird gesendet…',
      backToLogin: '← Zurück zur Anmeldung',
      successBody: 'Falls ein Konto mit dieser E-Mail existiert, erhältst du in Kürze einen Link.',
      spamHint: 'Überprüfe auch deinen SPAM-Ordner.',
    },
    reset: {
      title: 'Neues Passwort setzen',
      description: 'Wähle ein neues Passwort für deinen Account.',
      passwordLabel: 'Neues Passwort *',
      passwordConfirmLabel: 'Passwort bestätigen *',
      submitButton: 'Passwort ändern',
      loadingState: 'Wird geändert…',
      invalidTokenError:
        'Der Link ist abgelaufen oder ungültig. Bitte fordere einen neuen Reset-Link an.',
      requestNewLink: 'Neuen Link anfordern',
    },
    passwordStrength: {
      label: 'Stärke',
      weak: 'Schwach',
      medium: 'Mittel',
      strong: 'Stark',
    },
    emailSent: {
      title: 'Fast geschafft!',
      bodyWithEmail:
        'Wir haben dir eine E-Mail an {email} geschickt. Klick auf den Link, um deinen Account zu bestätigen.',
      bodyWithoutEmail:
        'Wir haben dir eine E-Mail geschickt. Klick auf den Link, um deinen Account zu bestätigen.',
      spamHint: 'Überprüfe auch deinen SPAM-Ordner.',
      resendButton: 'E-Mail erneut senden',
      resendingState: 'Wird gesendet…',
      resendConfirmation: 'E-Mail wurde gesendet.',
      backToApp: '← Zur App',
    },
    verify: {
      verifyingState: 'Einen Moment bitte…',
      successTitle: 'E-Mail bestätigt!',
      successBody:
        'Deine E-Mail wurde erfolgreich bestätigt. Du kannst jetzt alle Features nutzen.',
      backToApp: 'Zur App',
      errorTitle: 'Bestätigung fehlgeschlagen',
      errorBody: 'Der Link ist ungültig oder bereits verwendet.',
      requestNewLink: 'Neuen Link anfordern',
    },
    signup: {
      title: 'Registrieren',
      emailLabel: 'E-Mail-Adresse *',
      passwordLabel: 'Passwort *',
      passwordConfirmLabel: 'Passwort bestätigen *',
      passwordMatch: 'Passwörter stimmen überein.',
      legalCheckboxBefore: 'Ich habe die ',
      legalCheckboxTermsLink: 'Nutzungsbedingungen',
      legalCheckboxAnd: ' und die ',
      legalCheckboxPrivacyLink: 'Datenschutzerklärung',
      legalCheckboxAfter: ' gelesen und stimme ihnen zu.',
      submitButton: 'Registrieren',
      loadingState: 'Wird registriert…',
      hasAccount: 'Bereits einen Account?',
      loginLink: 'Einloggen',
    },
    // Bestandskunden-Hinweis (§ 7 Abs. 3 UWG). Shared across all three
    // registration screens: book LightGateOverlay, probe LightGateOverlay,
    // standalone SignupRoute. Pre-checked opt-out box + hint. Verbatim —
    // do not reword without legal re-approval.
    marketingNotice: {
      checkboxLabel:
        'Dreamable darf mir Tipps und Angebote zu eigenen Büchern und Geschichten per E-Mail schicken.',
      hint: 'Du kannst jederzeit widersprechen, hier, über den Link in jeder Mail oder per Mail an support@dreamable.kids. Dafür entstehen dir keine Kosten außer den üblichen Übermittlungskosten.',
    },
    errors: {
      emailRequired: 'E-Mail-Adresse erforderlich.',
      emailInvalid: 'Ungültige E-Mail-Adresse.',
      passwordRequired: 'Passwort erforderlich.',
      passwordTooShort: 'Mindestens 8 Zeichen.',
      passwordRequirementsNotMet: 'Dein Passwort erfüllt noch nicht alle Anforderungen.',
      passwordConfirmRequired: 'Bitte bestätige das Passwort.',
      passwordMismatch: 'Passwörter stimmen nicht überein.',
      invalidCredentials: 'E-Mail oder Passwort ist falsch.',
      loginThrottled:
        'Zu viele Anmeldeversuche. Bitte warte 5 Minuten und versuche es dann noch einmal.',
      forgotPasswordThrottled:
        'Zu viele Anfragen für einen neuen Link. Bitte versuche es in einer Stunde noch einmal.',
      duplicateEmail: 'Diese E-Mail-Adresse ist bereits registriert.',
      legalRequired: 'Bitte stimme den Nutzungsbedingungen und der Datenschutzerklärung zu.',
      genericBackend: 'Etwas ist schiefgelaufen. Bitte versuche es später erneut.',
      networkError: 'Keine Verbindung. Bitte prüfe deine Internetverbindung.',
      // Backend #42 (2026-05-01): surfaces from AuthService's prelaunch
      // allowlist gate during the soft-launch window. Verbatim Sebastian
      // lock — wording is identical across backend catalog (server-side
      // user_message) and this PWA STRINGS entry (client-side display).
      prelaunchLocked: 'Dreamable startet bald. Du erhältst eine Einladung wenn es soweit ist.',
    },
  },
  onboarding: {
    welcome: {
      title: 'Willkommen bei Dreamable!',
      body: 'In wenigen Schritten erstellen wir die erste personalisierte Geschichte für dein Kind.',
      cta: "Los geht's",
      kaptnKuschelAlt: "Käpt'n Kuschel",
    },
    // Marketing attribution package (2026-07-17) — SignupSourceCard.
    // Card copy stays short and light: this is a friendly one-tap
    // ask, not a survey. Dismissable via "Später beantworten".
    signupSource: {
      title: 'Wie hast du von uns gehört?',
      helper: 'Eine kurze Frage, die uns hilft, besser zu werden.',
      selectPlaceholder: 'Bitte wählen…',
      submit: 'Speichern',
      skip: 'Später beantworten',
      options: {
        instagram: 'Instagram',
        tiktok: 'TikTok',
        whatsapp_friends: 'WhatsApp / Freunde',
        recommendation: 'Empfehlung (Kita, Bekannte)',
        google_search: 'Google / Suche',
        ai_assistant: 'KI-Assistent (ChatGPT, Claude, Gemini)',
        press: 'Presse / Artikel',
        other: 'Sonstiges',
      },
    },
    world: {
      titleTemplate: 'Wähle eine Welt für {childName}s erste Geschichte:',
      titleFallback: 'Wähle eine Welt für die erste Geschichte:',
      aaName: 'Abenteuer Alltag',
      aaDescription:
        'Spannende Geschichten aus dem Alltag deines Kindes — auf dem Spielplatz, im Kindergarten, bei Oma.',
      aaIllustrationAlt: 'Feuerwehrauto-Illustration',
      sbName: 'Schlauberger',
      sbDescription:
        'Wissensgeschichten für neugierige Kinder — mit echten Fakten spielerisch verpackt.',
      sbIllustrationAlt: 'Schlauberger-Fuchs-Illustration',
      kpName: 'Kuschelpatrouille',
      kpDescription:
        'Ruhige Einschlafgeschichten für entspannte Bettzeiten — mit Käpt\u0027n Kuschel zum Einschlafen.',
      kpIllustrationAlt: 'Käpt\u0027n Kuschel-Illustration',
      heroSubtextTemplate: 'Mit {childName} als Held',
      heroSubtextFallback: 'Für die erste Geschichte',
    },
    howItWorks: {
      title: 'So entstehen eure Geschichten',
      subtitleTemplate:
        'Du erstellst gleich in {worldLabel} Deine erste Geschichte — so funktioniert diese Welt und die anderen zwei Welten.',
      blockAaKp: {
        heading: 'Abenteuer Alltag und Kuschelpatrouille',
        subLine: 'In beiden Welten funktioniert es gleich:',
        card1: {
          title: 'Deine eigene Idee',
          body: 'Gib ein Thema oder eine Szene ein. Dreamable verwandelt es in eine persönliche Geschichte.',
        },
        card2: {
          title: 'Zufallsmodus',
          body: 'Keine Eingabe, keine Inspiration? Ein Klick — und Dreamable erstellt automatisch eine personalisierte Überraschungsgeschichte.',
        },
      },
      blockSb: {
        heading: 'Schlauberger',
        subLine: 'Erst wählst du, was Schlauberger erkunden soll — dann, wie er es erzählt.',
        subsectionATitle: 'So wählst du das Thema:',
        itemA1: {
          title: 'Eigene Frage stellen',
          body: 'Tippe deine eigene Frage ein. Schlauberger findet die Antwort.',
        },
        itemA2: {
          title: 'Kategorie wählen',
          body: "Such dir eine Kategorie aus. Schlauberger schlägt dir 6 Fragen vor — wähl eine aus, oder klick auf 'Weitere Fragen' für neue Vorschläge.",
        },
        itemA3: {
          title: 'Zufallsmodus',
          body: "Schlauberger schlägt dir 6 Fragen quer durch alle Themen vor — wähl eine aus, oder klick auf 'Weitere Fragen' für neue Vorschläge.",
        },
        subsectionBTitle: 'Und so erzählt er sie:',
        cardB1: {
          emoji: '📖',
          title: 'Sachgeschichte',
          body: 'Schlauberger erzählt eine spannende Wissensgeschichte zum Zuhören. (ca. 3-6 Minuten)',
        },
        cardB2: {
          emoji: '💡',
          title: 'Kurz erklärt + Wow-Fakt',
          body: 'Schlauberger erklärt es kurz und knackig — mit Wow-Fakt und Probier-Tipp!',
        },
      },
      inspirationHint: 'Mehr Ideen? Im Inspirations-Tab findest du Vorschläge für jede Welt.',
      ctaButton: "Los geht's →",
    },
    pushConsent: {
      title: 'Verpasse nichts!',
      valueProp1: 'Erhalte Tipps für noch bessere Geschichten',
      valueProp2:
        'Erfahre sofort, wenn deine Geschichte fertig ist — kein Warten, kein Nachschauen',
      valueProp3:
        'Wir erinnern dich, wenn dein Kind schon lange keine neue Geschichte bekommen hat',
      primaryCta: 'Ja, ich möchte informiert werden',
      primaryCtaIos: 'Alles klar, weiter',
      secondaryLink: 'Später entscheiden',
      iosInstallBody:
        'Um Benachrichtigungen zu erhalten, installiere die App auf deinem Home-Bildschirm.',
      iosInstallSubBody: "Tippe auf das Teilen-Symbol in Safari, dann 'Zum Home-Bildschirm'.",
    },
    childProfile: {
      title: 'Kinderprofil',
      backLink: '← Zurück',
      kaptnContextBody:
        'Hallo! Ich bin Käpt\u0027n Kuschel. Damit wir persönliche Geschichten erzählen können, brauchen wir ein paar Infos über dich und dein Kind. Die Informationen werden nicht verkauft und nicht öffentlich angezeigt.',
      privacyLinkLabel: 'Zur Datenschutzerklärung',
      parentFirstNameLabel: 'Vorname (Elternteil)',
      parentLastNameLabel: 'Nachname (Elternteil)',
      submitButton: 'Fertig — lass uns zaubern!',
      submitLoading: 'Wird gespeichert…',
      discardChangesConfirm: 'Änderungen verwerfen?',
      errors: {
        parentUpdateFailed: 'Profil konnte nicht aktualisiert werden. Bitte versuche es erneut.',
        childCreateFailed: 'Kinderprofil konnte nicht erstellt werden. Bitte versuche es erneut.',
        networkError: 'Keine Verbindung. Bitte prüfe deine Internetverbindung.',
        emailNotVerified: 'Bitte bestätige deine E-Mail-Adresse, bevor du fortfährst.',
      },
    },
  },
  /**
   * Child-profile field labels / placeholders / tooltips shared
   * across Onboarding (`/onboarding/child_profile`) and Account
   * (`/account/child_profile`) routes. Extracted in Batch 9B Commit 2
   * when ChildProfileFields became the shared form sub-component.
   */
  children: {
    fields: {
      childName: {
        label: 'Name des Kindes',
        placeholder: 'z.B. Max',
        tooltip: 'Dies ist der Name, der in den Geschichten verwendet wird.',
      },
      nickname: {
        label: 'Spitzname',
        placeholder: 'z.B. Maxi',
        tooltip: 'Zusätzlicher Kosename, den Charaktere in Geschichten verwenden können.',
      },
      birthdate: {
        label: 'Geburtsdatum',
      },
      age: {
        label: 'Alter',
        suffix: 'Jahre',
      },
      gender: {
        label: 'Geschlecht',
        placeholder: 'Bitte wählen',
        optionBoy: 'Junge',
        optionGirl: 'Mädchen',
        optionDiverse: 'Divers',
      },
      kuscheltier: {
        label: 'Kuscheltier',
        placeholder: 'z.B. Hase',
        tooltip: 'Das Kuscheltier deines Kindes kann in Geschichten vorkommen.',
      },
      interests: {
        label: 'Interessen',
        placeholder: 'z.B. Feuerwehr; Klettern; Dinosaurier',
        tooltip:
          'Was begeistert dein Kind? Z.B. Feuerwehr; Klettern; Tiere. Mehrere Einträge mit Semikolon trennen.',
      },
      bezugspersonen: {
        label: 'Bezugspersonen',
        placeholder: 'z.B. Mama; Papa; Opa Hans; Oma Lisa',
        tooltip:
          'Wichtige Menschen im Leben deines Kindes (z.B. Mama; Opa Hans). Mehrere Einträge mit Semikolon trennen.',
      },
      orte: {
        label: 'Orte',
        placeholder: 'z.B. Spielplatz; Kita; Omas Garten',
        tooltip:
          'Lieblingsorte deines Kindes (z.B. Spielplatz; Oma\u0027s Garten). Mehrere Einträge mit Semikolon trennen.',
      },
      sonstiges: {
        label: 'Sonstiges',
        placeholder: 'Alles was wir wissen sollten…',
        tooltip:
          'Alles was wir noch wissen sollten (Allergien, aktuelle Situation, Besonderheiten)',
      },
      infoTooltipAriaLabel: 'Weitere Informationen',
      errors: {
        required: 'Dieses Feld ist erforderlich.',
        maxLength: 'Maximal {max} Zeichen.',
        invalidDate: 'Bitte gib ein gültiges Datum ein.',
        futureDate: 'Das Geburtsdatum kann nicht in der Zukunft liegen.',
        tooOld: 'Dreamable ist für Kinder bis 12 Jahre gedacht.',
      },
    },
  },
  // Batch 8 — Bibliothek (Briefing Ch 3 line 614-688)
  library: {
    title: 'Meine Bibliothek',
    back: '← Zurück',
    subtitleTemplate: '{count} Geschichten',
    searchPlaceholder: 'Geschichten durchsuchen...',
    chips: {
      all: 'Alle',
      // Briefing line 621 verbatim with the gold-star prefix
      favorites: '⭐ Favoriten',
      ariaLabel: 'Welt-Filter',
    },
    childFilter: {
      triggerLabel: 'Kind',
      allLabel: 'Alle Kinder',
      countSuffix: 'Geschichten',
    },
    yearFilter: {
      triggerLabel: 'Jahr',
      allLabel: 'Alle Jahre',
    },
    monthFilter: {
      triggerLabel: 'Monat',
      allLabel: 'Alle Monate',
      months: [
        'Januar',
        'Februar',
        'März',
        'April',
        'Mai',
        'Juni',
        'Juli',
        'August',
        'September',
        'Oktober',
        'November',
        'Dezember',
      ],
    },
    storyCard: {
      readAction: 'Lesen',
      audioAriaLabel: 'Audio abspielen',
      pdfAriaLabel: 'PDF öffnen',
      deleteAriaLabel: 'Geschichte löschen',
      favoriteAriaLabel: 'Favorit umschalten',
      // Bug #3 Part 1 (2026-05-12): status-pill indicators for failed
      // assets. Library list previously rendered failed-audio identically
      // to not_requested (icon hidden) — user only discovered the failure
      // on click-through.
      // Bug #3 Part 2 (2026-05-13): creditRefunded label added with the
      // automatic refund logic. Mint-toned pill renders alongside the
      // audio/pdf failure pill when a refund has been issued.
      statusPill: {
        audioFailed: 'Audio fehlt',
        pdfFailed: 'PDF fehlt',
        creditRefunded: 'Credit zurückerstattet',
      },
    },
    // Library-empty (zero stories AND no filters) — Briefing line 674
    // (Flag 5 verbatim)
    empty: {
      heading: 'Hier entstehen bald deine Geschichten.',
      body: 'Erstelle deine erste personalisierte Geschichte für dein Kind.',
      cta: 'Erste Geschichte erstellen',
    },
    // Filter-empty (filters active, result empty) — Briefing line 675
    filterEmpty: {
      heading: 'Keine Geschichten gefunden.',
      body: 'Versuch einen anderen Filter.',
      cta: 'Filter zurücksetzen',
    },
    // Delete-Story-Confirm-Modal (Briefing line 683-687 verbatim)
    deleteModal: {
      headline: 'Geschichte löschen?',
      // {title} interpolated at the call-site. Long titles (>60 chars)
      // are truncated to keep the modal layout balanced — see
      // LibraryRoute JSDoc on the truncation helper.
      bodyTemplate: '"{title}" wird dauerhaft gelöscht. Das kann nicht rückgängig gemacht werden.',
      primaryCta: 'Ja, löschen',
      secondaryCta: 'Abbrechen',
    },
    scrollTrigger: {
      idle: 'Weitere laden...',
      loading: 'Wird geladen...',
    },
    errors: {
      loadFailed: 'Bibliothek konnte nicht geladen werden.',
      networkError: 'Keine Verbindung. Bitte prüfe deine Internetverbindung.',
      deleteFailed: 'Geschichte konnte nicht gelöscht werden. Bitte versuche es erneut.',
      favoriteToggleFailed: 'Favorit konnte nicht aktualisiert werden.',
    },
  },
  builder: {
    shared: {
      themePlaceholderAA: 'z.B. Feuerwehr, Bauernhof, Ritterburg...',
      themePlaceholderKP: 'z.B. Sterne, Mond, Traumreise...',
      themePlaceholderSB: 'z.B. Warum ist der Himmel blau?',
      themePlaceholderRandom: 'Wir wählen für dich ein Thema aus',
      randomToggleLabel: 'Zufallsgeschichte',
      randomToggleLabelActive: 'Zufallsgeschichte aktiv',
      childSelectorLabel: 'Kind',
      childSelectorPlaceholder: 'Kind auswählen',
      childSelectorErrorLoad: 'Kinder konnten nicht geladen werden.',
      storydurationSelectorLabel: 'Storydauer',
      storydurationDefault: 'Standard (empfohlen)',
      storydurationShort: 'Kurz',
      storydurationLong: 'Lang',
      optionsLabel: 'Optionen',
      pdfToggleLabel: 'PDF-Version der Geschichte erstellen',
      audioToggleLabel: 'Hörbuch-Version (Audio) erstellen',
      voiceSelectorHeader: '🎙️ Stimme wählen',
      voiceMale: 'Männlich',
      voiceFemale: 'Weiblich',
      submitButton: 'Geschichte erstellen',
      submittingButton: 'Wird erstellt...',
      themaLabel: 'Thema',
      themaLabelOptional: 'Thema (optional)',
      charCounter: '{current}/{max}',
    },
    aa: {
      pageTitle: 'Abenteuer Alltag',
    },
    kp: {
      pageTitle: 'Kuschelpatrouille',
    },
    sb: {
      pageTitle: 'Schlauberger',
      tabs: {
        custom: 'Eigene Frage',
        random: 'Zufall',
        category: 'Kategorie',
      },
      modes: {
        story: 'Sachgeschichte',
        explanation: 'Kurzerklärung',
      },
      modeSelectorLabel: 'Erzählform',
      modeDescriptions: {
        story: 'Längere, narrative Erklär-Geschichte',
        explanation: 'Kurze Erklärung + Wow-Fakt + Probier-mal',
      },
      questionInputLabel: 'Stelle deine Frage',
      questionInputPlaceholder: 'Warum ist der Himmel blau?',
      questionInputHint: 'Empfohlen ab 3 Jahren',
      categoriesLabel: 'Wähle eine Kategorie',
      questionsLabel: 'Fragen in dieser Kategorie',
      moreQuestionsButton: 'Weitere Fragen',
      categories: {
        alltag_ernaehrung: 'Alltag und Ernährung',
        tiere_natur_umwelt: 'Tiere, Natur und Umwelt',
        technik_erfindungen: 'Technik und Erfindungen',
        mensch_koerper: 'Mensch und Körper',
        universum_wissenschaft: 'Universum und Wissenschaft',
        welt_kultur: 'Welt und Kultur',
      },
      questionsEmpty: 'Keine Fragen in dieser Kategorie.',
    },
    errorBanners: {
      limitTrial: {
        title: 'Dein Freikontingent ist aufgebraucht — aber die Abenteuer müssen nicht aufhören!',
        body: 'Schön dass dir die Probegeschichten gefallen haben! Wähle ein Abo für regelmäßige Geschichten oder ein Einmalpaket ganz ohne Bindung.',
        ctaPrimary: 'Abo abschließen',
        ctaSecondary: 'Einmalpaket kaufen',
      },
      limitSubscriptionMonth: {
        title: 'Dein Monatskontingent ist aufgebraucht',
        body: "Ab {date} gibt's wieder neue Geschichten in deinem Abo. Oder erweitere dein Abo für mehr Geschichten pro Monat — oder kauf ein Einmalpaket wenn du nicht warten möchtest.",
        ctaPrimary: 'Einmalpaket kaufen',
        ctaSecondary: 'Abo upgraden',
        ctaTertiary: 'Abo-Details ansehen',
      },
      limitSubscriptionExpired: {
        title: 'Dein Abo ist beendet',
        body: 'Dein Abo ist am {date} ausgelaufen. Abonniere erneut für regelmäßige Geschichten oder kauf ein Einmalpaket.',
        ctaPrimary: 'Abo erneuern',
        ctaSecondary: 'Einmalpaket kaufen',
      },
      noChild: {
        title: 'Kein Kinderprofil',
        body: 'Lege zuerst ein Kinderprofil an, um Geschichten zu erstellen.',
        cta: 'Kinderprofil anlegen',
      },
      emailUnverified: {
        title: 'E-Mail nicht bestätigt',
        body: 'Bitte bestätige deine E-Mail-Adresse, um Geschichten erstellen zu können.',
        cta: 'E-Mail erneut senden',
        resendLoadingLabel: 'Wird gesendet...',
        resendSuccessBody: 'E-Mail wurde gesendet. Bitte prüfe deinen Posteingang.',
        rateLimitedHint: 'In ein paar Sekunden erneut versuchen',
      },
    },
  },
  storyOutput: {
    loading: {
      aa: {
        preparing: 'Deine Geschichte wird vorbereitet…',
        writing: 'Deine Geschichte wird geschrieben…',
        audio: 'Audio wird erstellt…',
        pdf: 'PDF wird erstellt…',
        almostDone: 'Fast geschafft…',
      },
      kp: {
        preparing: 'Deine Geschichte wird vorbereitet…',
        writing: 'Deine Geschichte wird geschrieben…',
        audio: 'Audio wird erstellt…',
        pdf: 'PDF wird erstellt…',
        almostDone: 'Fast geschafft…',
      },
      sb: {
        preparing: 'Deine Frage wird wissenschaftlich geprüft…',
        researching: 'Fakten werden recherchiert…',
        writing: 'Deine Geschichte wird geschrieben…',
        audio: 'Audio wird erstellt…',
        pdf: 'PDF wird erstellt…',
        almostDone: 'Fast geschafft…',
      },
      errors: {
        // Still consumed by useStoryJobStream.ts (line 124, 155)
        // for SSE connection-lost + generic-failure paths. The
        // OutputRoute terminal failure UI now uses StoryErrorBlock
        // (Batch 12 Sub-phase B) — `errors.generationFailed` is no
        // longer rendered there but stays as fallback copy passed
        // through `error` state.
        connectionLost: 'Die Verbindung wurde unterbrochen. Bitte versuche es erneut.',
        generationFailed:
          'Beim Erstellen deiner Geschichte ist etwas schiefgelaufen. Bitte versuche es erneut.',
      },
      // Sub-phase B cleanup — `loading.cta.{back, retry}` removed.
      // Old consumer (OutputRoute terminal failure UI) replaced by
      // StoryErrorBlock variant CTAs (briefing-locked verbatim copy
      // in `errorStates.*`).
      // Timer-driven labels (Batch 6.1.1) — shown by a local easing timer in
      // useStoryJobStream, decoupled from SSE event arrival. The older
      // loading.{aa,kp,sb} sub-trees above are orphaned after this batch and
      // will be removed in the Phase 2 cleanup commit.
      timer: {
        aa: {
          creating: 'Deine Geschichte wird gerade erstellt…',
          writing: 'Deine Geschichte wird geschrieben…',
          almostDone: 'Fast geschafft…',
        },
        kp: {
          creating: 'Deine Geschichte wird gerade erstellt…',
          writing: 'Deine Geschichte wird geschrieben…',
          almostDone: 'Fast geschafft…',
        },
        sb: {
          checking: 'Deine Frage wird wissenschaftlich geprüft…',
          researching: 'Fakten werden recherchiert…',
          writing: 'Deine Geschichte wird geschrieben…',
          almostDone: 'Fast geschafft…',
        },
      },
    },
    // Batch 12 Sub-phase B — Story-Output error states (Briefing Kap. 8
    // line 1181-1213). 3 of 4 briefing-listed states ship in B-1
    // (technical / storyNotFound — limitBlock reuses
    // STRINGS.builder.errorBanners.limitTrial.* per Sebastian Gate-1
    // 2026-04-27). Timeout deferred to post-launch (tracker PWA Offen
    // #56). Variant routing via getErrorVariant() in
    // pwa/src/lib/errors.ts.
    errorStates: {
      technical: {
        title: 'Oops! Da ist etwas schiefgelaufen.',
        body: 'Flitzi kümmert sich sofort darum — bitte versuche es gleich nochmal. Dir wurden keine Geschichten abgezogen.',
        ctaPrimary: 'Erneut versuchen',
        illustrationAlt: 'Flitzi mit Werkzeug',
      },
      storyNotFound: {
        title: 'Diese Geschichte gibt es nicht mehr.',
        body: 'Vielleicht wurde sie gelöscht oder der Link ist fehlerhaft.',
        ctaPrimary: 'Zur Bibliothek',
        illustrationAlt: 'Flitzi mit Werkzeug',
      },
    },
    page: {
      meta: {
        today: 'Heute',
        worldAa: 'AA',
        worldKp: 'KP',
        worldSb: 'SB',
      },
      favorite: {
        add: 'Zu Favoriten hinzufügen',
        remove: 'Aus Favoriten entfernen',
      },
      shareIconLabel: 'Teilen',
      audio: {
        ready: '🎵 Audio anhören',
        generating: '🎵 Audio wird erstellt…',
        failed: '🎵 Audio nicht verfügbar',
        mp3Download: 'mp3 herunterladen',
        loading: 'Audio wird geladen…',
        schlafTimer: 'Schlaf-Timer',
        speedLabel: 'Speed',
        download: 'Download',
        ariaLabel: {
          play: 'Abspielen',
          pause: 'Pausieren',
          skipBack: '15 Sekunden zurück',
          skipForward: '15 Sekunden vor',
          scrub: 'Position in der Audioaufnahme',
          speed: 'Wiedergabegeschwindigkeit',
          schlafTimer: 'Schlaf-Timer einstellen',
          download: 'Audio herunterladen',
        },
      },
      pdf: {
        ready: '📄 PDF herunterladen',
        generating: '📄 PDF wird erstellt…',
        failed: '📄 PDF nicht verfügbar',
        // Briefing-silent. Sebastian Gate-1 verbatim lock 2026-04-27 —
        // matches '📄 PDF wird erstellt…' generating pattern. Shown
        // during the on-click signed-URL fetch (Batch 12 Sub-phase A).
        loading: '📄 PDF wird geladen…',
        // Click-fetch error path (Batch 12 Sub-phase A). Hybrid pattern —
        // backend `ApiError.envelope.user_message` is preferred; this
        // PWA fallback is used only when the envelope is absent
        // (NetworkError, non-ApiError exceptions, missing user_message).
        errors: {
          loadFailed: 'Das PDF konnte nicht geladen werden. Bitte versuche es erneut.',
          // Same value as STRINGS.storyOutput.errorStates.technical.ctaPrimary —
          // both briefing-locked verbatim. Local copy for namespace cohesion
          // (avoids cross-feature import).
          retry: 'Erneut versuchen',
        },
        // PWA #16 Sub-Phase 16.6 — full-screen in-app PDF viewer route.
        // Used by PdfViewerRoute (Homescreen-PWA branch only — browser
        // mode opens native viewer in a new tab and never visits this
        // route). Back-button label is the spoken-aloud aria copy; the
        // visible chevron is icon-only.
        viewer: {
          backLabel: 'Zurück zur Geschichte',
          downloadLabel: 'PDF herunterladen',
          loading: 'PDF wird geladen…',
        },
      },
      failureBanner: {
        audioOnly:
          'Hinweis: Das Audio konnte leider nicht erstellt werden. Du kannst die Geschichte trotzdem lesen oder als PDF speichern.',
        pdfOnly:
          'Hinweis: Das PDF konnte leider nicht erstellt werden. Du kannst die Geschichte trotzdem lesen oder anhören.',
        both: 'Hinweis: Audio und PDF konnten leider nicht erstellt werden. Du kannst die Geschichte trotzdem lesen.',
      },
      // Welt-Label strings rendered at the top of the Story-Output
      // story-card (PWA #16 Sub-Phase 16.3 Phase 4a-d). Lower-case
      // emojis match Sebastian Gate-1 2026-04-29 spec.
      weltLabels: {
        aa: '🌤️ Abenteuer Alltag',
        kp: '🌙 Kuschelpatrouille – Helden der Nacht',
        sbSachgeschichte: '📖 Schlauberger Wissensgeschichten',
        sbKurzerklaerung: '💡 Schlauberger Wissensblatt',
      },
      aaLearningQuestions: {
        heading: 'Dein Kind will mehr wissen?',
        cta: 'Entdecke die Antworten in der Schlauberger-Welt →',
      },
      sbWowFact: {
        heading: '✨ Wow Fact',
      },
      sbTryThis: {
        heading: '🔬 Probier mal!',
      },
      sbParentBox: {
        heading: 'Für Eltern – hättet ihr’s gewusst?',
        sourcesLabel: 'Quellen:',
      },
      footer: {
        newStory: 'Neue Geschichte',
        library: 'Bibliothek',
      },
      rating: {
        headline: 'Wie hat dir die Geschichte gefallen?',
        placeholder:
          'Optional: Hat dir was nicht besonders gefallen? Was können wir besser machen?',
        submit: 'Bewertung absenden',
        submitting: 'Wird gesendet…',
        retry: 'Nochmal versuchen',
        thanks: 'Danke für dein Feedback!',
        alreadyRatedNote: 'Deine Anmerkung:',
        ariaLabel: {
          starN: 'Bewerte mit {n} von 5 Sternen',
          textarea: 'Optionale Anmerkung',
        },
        errors: {
          submitFailed: 'Bewertung konnte nicht gespeichert werden. Bitte versuche es erneut.',
        },
      },
      share: {
        triggerPrimary: {
          standard: '📖 Geschichte teilen',
          sbExplanation: '🧠 Wissensblatt teilen',
        },
        sheet: {
          titleStandard: 'Geschichte teilen',
          titleSbExplanation: 'Wissensblatt teilen',
          subtitle: 'Wähle eine Nachricht, die zu dir passt:',
          sendeUeber: 'Sende über:',
          loading: 'Wird geladen…',
          channels: {
            whatsapp: 'WhatsApp',
            email: 'E-Mail',
            copy: 'Kopieren',
            moreApps: 'Weitere Apps',
          },
          toast: 'Link kopiert! ✅',
          ariaLabel: {
            variantSelected: 'Ausgewählt',
          },
          errors: {
            loadFailed: 'Geschichte konnte nicht geladen werden.',
          },
        },
      },
      errors: {
        loadFailed: 'Die Geschichte konnte nicht geladen werden. Bitte versuche es erneut.',
        favoriteFailed: 'Favorit konnte nicht gespeichert werden. Bitte versuche es erneut.',
      },
      back: 'Zurück',
    },
  },
  // Series feature (B3). German copy, middot only (no en/em dashes).
  catalog: {
    builder: {
      modeLabel: 'Erzählform',
      single: 'Einzelgeschichte',
      singleSub: 'Eine Wissensgeschichte zu deiner Frage',
      catalog: 'Wissenswelten',
      catalogSub: 'Mehrteilige Themenreihen, Folge für Folge freischalten',
    },
    browse: {
      heading: 'Wissenswelten',
      subline: 'Mehrteilige Wissensreihen, Folge für Folge freischalten und anhören.',
      loadFailed: 'Die Wissenswelten konnten nicht geladen werden. Bitte versuche es erneut.',
    },
    library: {
      badge: 'Wissenswelt',
      parentWorld: 'Schlauberger',
      progress: '{unlocked} von {total} Folgen',
      open: 'Welt öffnen',
    },
    detail: {
      clusterLabel: 'Altersfassung',
      clusterAge3_4: 'bis 4 Jahre',
      clusterAge5_7: '5–7 Jahre',
      childLabel: 'Kind',
      voiceMale: 'männlich',
      voiceFemale: 'weiblich',
      episodeListen: 'anhören',
      episodeRead: 'lesen',
      episodeLocked: 'gesperrt',
      unlock: 'Freischalten · 1 Credit',
      noCreditsHint: 'Keine Credits · Plan upgraden',
      creditsLine: 'Du hast {credits} Credits.',
      unlockFailed: 'Die Folge konnte nicht freigeschaltet werden. Bitte versuche es erneut.',
      subline: '{unlocked} von {total} Folgen freigeschaltet',
      loadFailed: 'Diese Wissenswelt konnte nicht geladen werden. Bitte versuche es erneut.',
      readFailed: 'Der Text konnte nicht geladen werden. Bitte versuche es erneut.',
      playFailed: 'Das Hörbuch konnte nicht gestartet werden. Bitte versuche es erneut.',
      readClose: 'Schließen',
      back: 'Zurück zu den Wissenswelten',
      // Paket 3 — whole-world unlock + Gesamt-Hörbuch surface.
      unlockAll: 'Ganze Wissenswelt freischalten',
      combinedHint:
        'Wenn alle Folgen freigeschaltet sind, kannst du die ganze Wissenswelt am Stück anhören.',
      combinedTrigger: 'Gesamte Wissenswelt anhören',
    },
  },
  retrofit: {
    audioButton: 'Audio hinzufügen',
    pdfButton: 'PDF hinzufügen',
    audioPending: 'Audio wird erstellt ...',
    pdfPending: 'PDF wird erstellt ...',
    errorFallback: 'Das hat gerade nicht geklappt. Bitte versuch es gleich nochmal.',
    sheet: {
      // Header / option labels reused verbatim from the AA/KP/SB builder's
      // VoiceSelector (STRINGS.builder.shared.voiceSelectorHeader /
      // voiceMale / voiceFemale) so the retrofit sheet feels identical
      // to the creation-flow voice picker. Do NOT duplicate the strings
      // here; import them at the call site.
      confirm: 'Audio erstellen',
    },
  },
  series: {
    builder: {
      modeLabel: 'Erzählform',
      modeSingle: 'Einzelgeschichte',
      modeSingleSub: 'Eine Geschichte, jetzt',
      modeSeries: 'Serie',
      modeSeriesSub: 'Mehrteilige Staffel mit Cliffhangern',
      themaLabel: 'Thema der Staffel',
      themaPlaceholder:
        'Worum soll die Staffel gehen? Thema, Lieblingsfiguren, Schauplatz oder eine Idee für den roten Faden …',
      episodeCountLabel: 'Folgenzahl',
      episodeCount5: '5 Folgen',
      episodeCount10: '10 Folgen',
      planButton: 'Staffel planen',
      planLoading: 'Staffel wird geplant ...',
      planHeading: 'Dein Staffelplan',
      variantEpisodeCount: '{count} Folgen',
      confirmTitleLabel: 'Staffel-Titel',
      confirmOptionsLabel: 'Optionen für die ganze Staffel',
      confirmPdfToggle: 'PDF-Version je Folge',
      confirmAudioToggle: 'Hörbuch-Version je Folge',
      confirmVoiceHeader: '🎙️ Stimme',
      creditHint: '{count} Folgen · 1 Credit je Folge · du hast {credits} Credits.',
      creditHintShort:
        'Du hast {credits} Credits. Die letzten {missing} Folgen werden im nächsten Monat verfügbar.',
      createButton: 'Staffel erstellen',
      error: 'Die Staffel konnte nicht geplant werden. Bitte versuche es erneut.',
    },
    season: {
      subline: '{world} · {child} · {done} von {total} Folgen erstellt',
      sublineNoChild: '{world} · {done} von {total} Folgen erstellt',
      creditsLine: 'Du hast {credits} Credits.',
      episodeListen: 'anhören',
      episodeRead: 'lesen',
      episodeGenerate: 'Erstellen · 1 Credit',
      episodeGenerating: 'wird erstellt …',
      generating: 'Folge {n} von {total} wird erstellt …',
      episodePlanned: 'geplant',
      noCreditsHint: 'Keine Credits · Plan upgraden',
      loadFailed: 'Die Staffel konnte nicht geladen werden. Bitte versuche es erneut.',
      generateFailed: 'Die Folge konnte nicht erstellt werden. Bitte versuche es erneut.',
    },
    output: {
      breadcrumb: '{title} · Folge {n} von {total}',
      backToSeason: 'Zur Staffel-Übersicht',
      nextEpisode: 'Nächste Folge erstellen (1 Credit)',
      toSeason: 'Zur Staffel-Übersicht',
      newSeason: 'Neue Staffel planen',
      finaleMarker: 'Staffelfinale',
    },
    library: {
      pill: 'Serien',
      badge: 'Serie',
      progress: '{done} von {total} Folgen',
      open: 'Staffel öffnen',
    },
    // B5: batch generation ("alle Folgen auf einmal").
    batch: {
      trigger: 'Alle Folgen auf einmal erstellen',
      triggerRemaining: 'Restliche Folgen erstellen',
      running: 'Folge {current} von {total} wird erstellt …',
      batchRunning: 'Folgen werden erstellt …',
      batchRunningHint: 'Alle Folgen werden nacheinander erstellt. Das dauert ein paar Minuten.',
      stoppedCredits:
        '{done} von {total} Folgen erstellt. Für die restlichen {remaining} Folgen brauchst du mehr Credits.',
      stoppedCreditsRetry: 'Weitere Folgen erstellen',
      failed: 'Beim Erstellen der Folgen ist etwas schiefgelaufen.',
      retry: 'Erneut versuchen',
      startError: 'Die Sammel-Generierung konnte nicht gestartet werden. Bitte versuche es erneut.',
    },
    // B5: combined-season audio ("Gesamt-Hörbuch").
    combined: {
      heading: 'Gesamt-Hörbuch',
      allEpisodes: 'Alle {count} Folgen am Stück',
      pending: 'Gesamt-Hörbuch wird erstellt …',
      independenceWindow: 'Alle Folgen erstellt · Gesamt-Hörbuch wird noch erzeugt …',
      failed: 'Das Gesamt-Hörbuch konnte nicht erstellt werden.',
      trigger: 'Gesamt-Hörbuch erstellen',
      triggerError: 'Das Gesamt-Hörbuch konnte nicht gestartet werden. Bitte versuche es erneut.',
    },
  },
  // P5-4 Commit A — book flow strings. Populated per slice.
  book: {
    header: {
      // Aria-label for the account-icon exit in BookHeader. Matches
      // the audio Kopfleiste's inline "Einstellungen" at
      // TopBar.tsx:56 verbatim; keyed here per §8 G8 (all
      // user-facing strings via STRINGS).
      accountAriaLabel: 'Einstellungen',
      // Sebastian ruling 2026-09-09 — Task 2 (BookHeader auth
      // affordance). Symmetric text affordances: anonymous customer
      // sees "Anmelden", authenticated customer sees "Abmelden".
      // One click either way. Anmelden navigates to /auth/login
      // with the current URL as returnTo (resume case); Abmelden
      // fires authStore.logout() + navigate('/auth/login') with no
      // returnTo (logout means "I'm done for now"). A menu becomes
      // right later, when the four-tab book area brings Meine
      // Bücher / Konto / orders — not now.
      anonymousLoginLink: 'Anmelden',
      logoutLink: 'Abmelden',
      // Task 3 Commit A (Sebastian ruling 2026-09-09) — both-worlds
      // switcher, book → audio direction. Direct text link, left of
      // the Anmelden/Abmelden affordance, rendered only when the
      // customer has BOTH worlds (`user.hasBookWorld &&
      // user.hasAudioWorld`). Label is the DESTINATION, not the
      // current world — Sebastian reversed his earlier "carries the
      // current world as its label" framing after review: a button
      // reading "Bücher" that leads away from books is mislabelled,
      // and the book PWA already says where you are through the
      // Schrittleiste. Landing: `/library`.
      //
      // Sebastian ruling 2026-09-24 (mobile menu slice) — renamed
      // from "Zu den Geschichten" to "Hörgeschichten" for consistent
      // product naming in both the desktop link bar and the new
      // mobile menu.
      switcherToAudio: 'Hörgeschichten',
      // Sebastian ruling 2026-09-15 — Meine Bücher list. Header link
      // rendered in BookHeader when the customer is authenticated
      // (independent of hasBookWorld: a customer with zero orders
      // reaches the empty state via this link).
      meineBuecherLink: 'Meine Bücher',
      // Sebastian ruling 2026-09-24 — mobile menu affordance. Below
      // the md breakpoint the header links (Meine Bücher +
      // Hörgeschichten + Abmelden) collapse into a hamburger icon
      // that opens a Popover. The icon carries this aria-label; the
      // menu items reuse the existing link strings.
      menuAriaLabel: 'Menü',
      // Sebastian ruling 2026-09-24 — mobile menu + desktop link.
      // "Passwort ändern" moved from BookFooter into (a) the mobile
      // menu and (b) the desktop link bar. Rationale for adding to
      // the desktop link bar: for book-only customers,
      // `OnboardingGuard` blocks /account so the footer link was
      // their sole entry point to /account/password. Removing it
      // from the footer without an equivalent header affordance
      // would leave book-only desktop customers no way to change
      // their password from inside the book flow. Same string as
      // the retired footer key `book.footer.changePasswordLink`.
      changePasswordLink: 'Passwort ändern',
    },
    // Sebastian ruling 2026-09-15 round 3, revised 2026-09-24 —
    // book-shell footer. Was two admin links (change password + delete
    // account); "Passwort ändern" moved into the new BookHeader
    // mobile menu + desktop link bar in the 2026-09-24 slice, so the
    // footer now carries only Konto löschen — a rare-use, high-
    // consequence surface Sebastian ruled belongs low in the flow.
    // Reachable by book-only customers because OnboardingGuard
    // whitelists /account/delete (`ONBOARDING_BYPASS_PATHS`).
    footer: {
      deleteAccountLink: 'Konto löschen',
    },
    // Sebastian ruling 2026-09-15 — Meine Bücher list + discard.
    // All strings released verbatim; do NOT rephrase.
    meineBuecher: {
      pageHeading: 'Meine Bücher',
      emptyHeading: 'Noch kein Buch angelegt.',
      emptyBody: 'Hier findest du später alle Bücher, an denen du arbeitest.',
      newBookButton: 'Neues Buch anlegen',
      capReachedHelper:
        'Du hast drei Bücher offen. Bestell eins davon oder verwirf es, dann kannst du ein neues anlegen.',
      discardButton: 'Verwerfen',
      // Sebastian ruling 2026-09-15 (round 2) — card secondary line.
      // Two forms: with cast names, or date-only when the cast is
      // still empty. The date format is `formatGermanShortDate` from
      // `@/lib/format-german-date` — same helper `useEntitlement`
      // uses; no new date shape introduced.
      cardCastAndDate: (castNames: string, dateStr: string) =>
        `${castNames} — angelegt am ${dateStr}`,
      cardDateOnly: (dateStr: string) => `angelegt am ${dateStr}`,
      discardDialog: {
        heading: 'Dieses Buch verwerfen?',
        body: 'Damit sind die Geschichte, die Figuren und alle Bilder weg. Das lässt sich nicht rückgängig machen.',
        confirm: 'Ja, verwerfen',
        cancel: 'Abbrechen',
      },
    },
    // photo_rejected screen — coordination commit with sister session
    // 2026-08-06. Customer copy is PLACEHOLDER per Sebastian's brief;
    // shape given verbatim:
    //   "Auf dem Foto sind {n} Personen zu sehen. Bitte lade ein Foto
    //   hoch, auf dem nur {name} zu sehen ist."
    // Tracker `[book-photo-rejected-copy-authorship]` awaits final
    // wording.
    // ═════════════════════════════════════════════════════════════════
    // photo-rejected screen — Sebastian authorship 2026-08-12.
    //
    // Source of truth: `docs/geschichtenbuch/P2_Text_und_Prompt_Entsc
    // heidungen_v2_2026-08-05.md` §"Wenn ein Foto nicht angenommen
    // werden kann". Two cases (multi_face vs sanitizer_failed) — the
    // wire's `kind` discriminator selects the block. `{castName}` is
    // interpolated from `envelope.intake.derived.rejection.castName`.
    // No "Porträtfoto" or "Ganzkörperfoto" wording anywhere: the
    // rejected-photo tile stands beside the message and removes the
    // guessing (Sebastian ruling 2026-08-12).
    //
    // `bodyFallback` fires when the wire carries `kind:
    // 'photo_multi_face'` but the tile-side signing failed
    // (`rejectedPhotoUrl === null`) or when a future envelope-shape
    // gap leaves the rejection payload undefined — the disabled-guard
    // fix (line 146 in PhotoRejectedScreen) keeps the CTA live in that
    // case, so the fallback is a display concern only, never a flow
    // concern.
    // ═════════════════════════════════════════════════════════════════
    photoRejected: {
      // multi_face case — mehrere Gesichter auf einem Foto.
      multiFaceHeading: 'Auf dem Foto von {castName} sind mehrere Gesichter.',
      multiFaceBody:
        'Für die Figur brauchen wir ein Bild, auf dem nur {castName} zu sehen ist. Lade bitte ein neues hoch.',
      // sanitizer_failed case — server-seitige Verarbeitungsfehler.
      // Not surfaced today (backend routes sanitizer failures to
      // qa_human_queue since 2026-08-12), but declared so a future
      // wire that emits `kind: 'photo_sanitize_failed'` renders
      // Sebastian-authored copy without a strings edit.
      sanitizerFailedHeading: 'Wir konnten das Foto von {castName} nicht verarbeiten.',
      sanitizerFailedBody:
        'Das lag an uns. Versuch es bitte noch einmal, mit demselben Bild oder einem anderen.',
      // heic_convert_failed case — Sebastian ruling 2026-08-12
      // (Option 1 follow-up): a HEIC file we cannot decode is
      // customer-actionable (pick another photo), so the backend
      // routes it to photo_rejected with `kind:
      // 'heic_convert_failed'` rather than to qa_human_queue.
      // Heading reuses the sanitizer_failed line byte-for-byte; the
      // body differs by one word — "Nimm bitte ein anderes Bild"
      // rather than "mit demselben Bild oder einem anderen" — because
      // for a HEIC that failed to decode, "same image" won't help.
      heicConvertFailedHeading: 'Wir konnten das Foto von {castName} nicht verarbeiten.',
      heicConvertFailedBody: 'Das lag an uns. Nimm bitte ein anderes Bild.',
      // Fallback body when the wire carries kind but the tile-side
      // signing failed OR a shape gap leaves the rejection undefined.
      bodyFallback: 'Wir konnten das Foto nicht verwenden. Bitte lade ein neues Foto hoch.',
      // Aria labels for the rejected-photo tile + fresh-upload slot.
      rejectedTileAria: 'Das Foto, das wir nicht annehmen konnten',
      slotAriaEmpty: 'Neues Foto hochladen',
      // Uploading + generic error strings for the swap flow.
      uploadingHeading: 'Wir laden dein neues Foto hoch.',
      swapErrorPlaceholder: 'Das hat nicht geklappt. Wir versuchen es nochmal.',
      retryCta: 'Nochmal versuchen',
      // Delete-photo CTA (Sebastian ruling 2026-08-12, wave commit 2).
      // Only rendered when `rejection.whichPhoto === 'fullBody'` — the
      // face slot is mandatory per §A3.2 E-19 and the backend rejects
      // face-deletes with `book_photo_delete_face_forbidden`.
      deleteFullBodyCta: 'Foto entfernen',
      deleteErrorPlaceholder: 'Das hat nicht geklappt. Wir versuchen es nochmal.',
    },
    // 2026-10-14 — per-figure block-derivation rejection. Shown when
    // the server has flipped the order to `block_derivation_rejected`
    // because one or more cast members' vision derivation failed. The
    // Personen screen reads `envelope.intake.derived.rejections` (plural
    // keyed by castKey) and renders the matching entry's heading + body
    // above the affected figure. All three strings APPROVED verbatim
    // (Sebastian 2026-10-14).
    blockDerivationRejected: {
      heading: 'Bei einer Figur hat es nicht geklappt',
      tierBody:
        'Auf dem Foto von [[name]] konnten wir kein Tier erkennen. Gegenstände wie Autos oder Kuscheltiere beschreibst du am besten in deiner Geschichte, dann zeichnen wir sie mit. Entferne [[name]] hier oder nimm ein anderes Foto.',
      personBody:
        'Das Foto von [[name]] konnten wir nicht verwenden. Nimm bitte ein anderes Foto, auf dem das Gesicht gut zu sehen ist.',
      // Sebastian ruling 2026-10-14 — approved verbatim CTA label for
      // the per-figure removal affordance on the rejected screen.
      removeCta: 'Entfernen',
    },
    // Book-shell verification reminder — 5a Commit 4 (Sebastian
    // ruling 2026-08-06). A quiet line + resend action, no gate, no
    // modal. Renders inside BookShell whenever the authenticated
    // customer has NOT yet verified his email.
    //
    // PLACEHOLDER wording per Sebastian's correction 2026-08-06 —
    // "Turn it around, say what he gets, not what he loses." The
    // Commit-3 draft "Ohne den kannst du dein Buch später nicht
    // bestellen." was rejected as a threat at the moment the
    // customer just handed over photos. The placeholder below is in
    // that register; Sebastian's authorship replaces it.
    shell: {
      verifyReminderLine:
        'Bestätige noch kurz deine E-Mail-Adresse — dann bleibt dein Buch mit deinem Konto verknüpft und du findest es überall wieder.',
      // F4 (Sebastian release 2026-10-16) — probe-scoped verify
      // banner shown ONLY on /figuren. Keeps the "Nochmal senden"
      // resend action unchanged; wording swaps the "Buch" + "überall
      // wieder" promise for a probe-specific sentence.
      verifyReminderLineProbe:
        'Bestätige noch kurz deine E-Mail-Adresse. Dann bleibt dein Figurenbild mit deinem Konto verknüpft.',
      verifyResendCta: 'Nochmal senden',
      verifyResentAck: 'E-Mail ist unterwegs.',
    },
    schrittleiste: {
      // Two bars per J-23, mirrored in the Schrittleiste component
      // structure (Bau-Spez v3 §7 line 391).
      //
      // Bar A (before payment): five marks in order.
      // Bar B (after payment): two marks — Bestellung (done) plus
      //   Freigabe (open during production, active at customer_review).
      //
      // Both bars carry a "Bestellung" mark and each holds its own copy
      // of the label. Duplication is deliberate: if a future ruling
      // renames one bar's copy, the change lands on one field without
      // a refactor.
      //
      // Label "Freigabe" (not "Druckfreigabe") per v3 §7 shorter form;
      // "Druck-" is implied by Bar B's post-payment context.
      barA: {
        ariaLabel: 'Schritte, vor der Zahlung',
        // Position-5 Commit 6b.4 (Sebastian ruling 2026-09-02) — post-
        // cut order of the five marks:
        //   Personen · Geschichte · Figuren · Vorschau · Bestellung
        //
        // The label position mapping lives in
        // Schrittleiste.tsx `barAMarkLabel` — key names on this object
        // do not encode positions. `figuren` replaces the old
        // `titelbild` key. `Figuren` is the customer-visible label:
        // "Personen" is where the customer TELLS us who is in the
        // book; "Figuren" is where he SEES how they look.
        personen: 'Personen',
        geschichte: 'Geschichte',
        figuren: 'Figuren',
        vorschau: 'Vorschau',
        bestellung: 'Bestellung',
      },
      barB: {
        ariaLabel: 'Schritte, nach der Zahlung',
        bestellung: 'Bestellung',
        freigabe: 'Freigabe',
      },
    },
    // Schritt 1 · Personen — Textdatei v2 §Schritt 1 lines 163-244.
    //
    // Registered up front so Commit 1 primitives + later assembly
    // + Light-Gate commits touch only pwa/src/features/book/.
    //
    // Wortlaut-Herkunft: `docs/geschichtenbuch/P2_Text_und_Prompt_
    // Entscheidungen_v2_2026-08-05.md` §Schritt 1. Sechs Beispieltexte
    // (~700 Zeichen) sind verbatim; Autorschaft Sebastian per
    // Textdatei v2 lines 203-204.
    personen: {
      // Block 2 commit 1 (Sebastian ruling 2026-08-31, final wording
      // 2026-08-31 evening). Values character-for-character as
      // Sebastian ruled. `hauptpersonSubtitle` +
      // `personenNotizNameFallback` are DELETED per the same ruling —
      // Hauptperson block no longer exists; the "no name" case uses
      // `personenNotizLabelOhneName` instead of substituting a
      // placeholder noun into `{Name}` (a placeholder is wrong for
      // an animal).
      // ═══ ONE-QUESTION-AT-A-TIME FLOW — Sebastian ruling 2026-09-24 ═══
      //
      // Block 1 rebuild. One question visible at a time; the next
      // appears only once the current one is answered. Previously
      // answered questions collapse to compact "✓ Antwort · Ändern"
      // rows above. Single-choice questions auto-advance on tap.
      // After the photo is confirmed, the person is complete and
      // the card collapses. Animals get an extra species question
      // between Q1 and Q_name (Sebastian ruling 2026-09-24 — one
      // new question rather than a backend change to drop `species`).

      // Block 1 heading + single intro line. Sebastian ruling: the
      // "1." SchrittBlock ordinal is hidden for this block (hideNumber
      // prop) — the customer sees the question through affordances,
      // not through a numbered heading. Blocks 2 + 3 keep theirs.
      personenHeading: 'Wer kommt in deinem Buch vor?',
      personenIntroLine:
        'Füge alle Menschen und Tiere hinzu, die im Buch erkennbar vorkommen sollen. Wer nur am Rand auftaucht, braucht keinen Eintrag.',

      // Q1 — the role picker. Three tap-to-advance buttons. Kind +
      // Erwachsener both map to `personType='person'` at the store
      // level; the distinction only drives Q_name wording. Tier maps
      // to `personType='tier'` and triggers the extra Q_species step.
      qRoleTitle: 'Wen möchtest du hinzufügen?',
      qRoleKind: 'Ein Kind',
      qRoleErwachsener: 'Einen Erwachsenen',
      qRoleTier: 'Ein Tier',

      // Q_species — Tier only, sits between Q1 and Q_name so the name
      // question can address the animal by its species-role (a dog
      // vs a cat vs a pony). Sebastian ruling 2026-09-24: "Species
      // first, then name, is the natural order."
      qSpeciesTitle: 'Was für ein Tier ist es?',
      qSpeciesPlaceholder: 'z. B. Hund, Katze',

      // Q_name — three variants keyed by role. The label + placeholder
      // change; the underlying field is the same `name` string.
      // Erwachsener uses a role-label (Mama, Opa Peter) not a first
      // name — matches how children actually address adults in
      // storytelling.
      qNameKindTitle: 'Wie heißt das Kind?',
      qNameKindPlaceholder: 'Vorname',
      qNameErwachsenerTitle: 'Wie nennen die Kinder diese Person?',
      qNameErwachsenerPlaceholder: 'z. B. Mama oder Opa Peter',
      qNameTierTitle: 'Wie heißt das Tier?',
      qNameTierPlaceholder: 'z. B. Bello',

      // Q_age — `{Name}` substituted at render time from the entry's
      // `name` field (already set at this step).
      qAgeTitle: 'Wie alt ist {Name}?',
      qAgePlaceholder: 'Alter in Jahren',

      // Q_gender — three tap-to-advance buttons. Keineangabe is a
      // valid choice, not a "skip"; the story processor uses this
      // value in the composed cast description regardless of choice.
      qGenderTitle: 'Ist {Name} männlich oder weiblich?',
      qGenderMale: 'männlich',
      qGenderFemale: 'weiblich',
      qGenderNone: 'keine Angabe',

      // Q_photo — the hardest step (Sebastian ruling 2026-09-24).
      // Analytics event `book_first_photo_step_reached` fires the
      // first time any cast entry reaches this question to measure
      // drop-off before vs at the step.
      qPhotoTitle: 'Zeig uns {Name}',
      qPhotoCta: 'Foto hinzufügen',
      qPhotoBelow: 'Ein Foto von vorne, das Gesicht gut erkennbar.',
      qPhotoLimits: 'Bis 10 MB. Fotos direkt vom Handy funktionieren.',
      qPhotoTipsToggle: 'Tipps für ein gutes Foto',

      // Collapsed-card summary — replaces the pre-flow-rewrite
      // heading-plus-card treatment. "✓ Anna, 6 Jahre · Ändern ·
      // Entfernen". Sebastian ruling 2026-09-24: Entfernen appears
      // on every card including the first (delete guard removed
      // from `book-configurator-store.ts`).
      collapsedEdit: 'Ändern',
      collapsedRemove: 'Entfernen',
      collapsedAgeSuffix: 'Jahre',
      // F8 (Sebastian release 2026-10-16) — alt text for the small
      // photo thumbnail added to CollapsedCard so a probe-transferred
      // customer can see that the photo they uploaded on /figuren
      // actually made it into the book. Sebastian authored the string
      // with the `[[name]]` placeholder convention — distinct from
      // the sibling `{Name}` pattern used by qAgeTitle / qGenderTitle
      // / qPhotoTitle (`.replace('{Name}', …)`) at
      // PersonenFlow.tsx:1393-1395 / :1345-1347 / :1448-1450 /
      // :1506-1508. CollapsedCard substitutes `[[name]]` → entry.name
      // via a straight `.replace('[[name]]', entry.name)` at render
      // time; the string literal in this catalog stays byte-identical
      // to Sebastian's release.
      collapsedPhotoAlt: 'Foto von [[name]]',

      // Optional fields on the collapsed card — Sebastian ruling
      // 2026-09-24 (round 2): "Beziehung ergänzen" REMOVED — as a
      // link on the collapsed card nobody would notice it, and the
      // story generator needs the relationship to know who belongs
      // to whom. Relationship is now its own flow question
      // (`qRelationTitle` below) for every person except the first.

      // Q_relation — Sebastian ruling 2026-09-24 (round 2, revised).
      // Its own flow question, asked AFTER Q_photo (post-K2 confirm),
      // for every cast entry AT INDEX > 0 (first entry has no one to
      // relate to; skipped). Photo is the core of each person; the
      // relationship is a lighter add-on that completes the entry.
      // Two buttons — Weiter (enabled when input non-empty) ·
      // Überspringen (advances without setting personenNotiz).
      // Compact-answered row shows the text or "(übersprungen)" if
      // empty; Ändern re-opens.
      //
      // Placeholder is role-specific (Sebastian ruling 2026-09-24):
      //   Kind → "z. B. die große Schwester von Mats"
      //   Erwachsener → "z. B. die Mama von Mats"
      //   Tier → "z. B. gehört zu Mats"
      qRelationTitle: 'Wer ist {Name} für die anderen?',
      qRelationPlaceholderKind: 'z. B. die große Schwester von Mats',
      qRelationPlaceholderErwachsener: 'z. B. die Mama von Mats',
      qRelationPlaceholderTier: 'z. B. gehört zu Mats',
      qRelationSkipped: '(übersprungen)',

      // Common flow buttons — Sebastian ruling 2026-09-24 (round 2).
      //
      // `qCommonWeiter` — advances the current text question. Rendered
      // below every text-input question (name, species, age, relation);
      // enabled when the input is valid. Enter in the field does the
      // same thing. Tap-choice questions (role, gender) advance
      // on tap and do NOT render a Weiter button.
      //
      // `qCommonSkip` — appears on Q_relation only (the only skippable
      // question). Advances without touching personenNotiz.
      //
      // `qCommonFertig` — closes edit-review mode after a customer
      // tapped Ändern on an already-completed person. Disabled until
      // every field is complete (so a removed-photo edit blocks
      // Fertig until re-picked). "Doch nicht" NEVER appears during
      // an edit — the only way to delete an existing person is the
      // explicit Entfernen link on the collapsed card.
      qCommonWeiter: 'Weiter',
      qCommonSkip: 'Überspringen',
      qCommonFertig: 'Fertig',

      // After each person — Sebastian ruling 2026-09-24. Fires after
      // the photo K2 closes and the card collapses. "Ja, noch jemand"
      // spawns a fresh entry at Q1. "Nein, alle dabei, weiter zur
      // Geschichte" fires `markAnswered(1)` — Block 2 (Freitext)
      // appears. After 9 people, only the continue button remains
      // (BOOK_CONFIG_CONSTANTS.max_cast_entries).
      addAnotherTitle: 'Kommt noch jemand im Buch vor?',
      addAnotherYes: 'Ja, noch jemand',
      addAnotherNo: 'Nein, alle dabei, weiter zur Geschichte',

      // Mid-entry escape hatch — Sebastian ruling 2026-09-24. Shows
      // as a small link during the flow of an ADDITIONAL person (not
      // the first). Tapping discards the half-finished entry via
      // `removeCast(key)` and returns focus to the "Kommt noch
      // jemand?" prompt on the previous person's card.
      midEntryCancel: 'Doch nicht',
      // Block 2 follow-up (Sebastian ruling 2026-09-09, test-book
      // finding): the pre-follow-up label "Hinzufügen" did not name
      // what gets added. New label spells out both roles (Person +
      // Tier) so the customer sees the affordance for what it is.
      // The visible "+" is the AddCard component's `<Plus />` icon,
      // not part of this string.
      addPersonCardLabel: 'Weitere Person oder Tier hinzufügen',

      // Block 2 · 2.1 · Three-step permanent explainer at the top of
      // the Personen screen (Sebastian ruling 2026-09-09). Renders
      // three tiles side-by-side on desktop, stacked on phone. Icons
      // = camera / pen / parcel (lucide-react). Sits above every
      // block; never collapses. Every string here is verbatim from
      // the Block 2 spec.
      dreiSchritte: {
        heading: 'In drei Schritten zu eurem Buch',
        tile1Title: 'Personen anlegen',
        tile1Body:
          'Für jede Person: Name, Alter, Geschlecht und ein Foto. Frontal, mit gut erkennbarem Gesicht.',
        tile2Title: 'Geschichte beschreiben',
        tile2Body:
          'Erzähl, worum es gehen soll. Ein Anlass, ein Erlebnis oder eine erfundene Idee.',
        tile3Title: 'Ansehen und bestellen',
        tile3Body:
          'Du siehst jede Seite, bevor gedruckt wird. Als Hardcover, Layflat oder Pappbuch.',
      },

      // Block 2 · 2.3 · Hint above the first person (once, not per
      // person). Rendered as plain prose above the cast list — NOT
      // inside a HintBox with a builder-field-style border, because
      // that reads as an input field (Block 2 · 2.5 P-48 finding).
      // Two paragraphs; blank line between them in the spec = a
      // paragraph break in the DOM.
      // Sebastian ruling 2026-09-13 — second sentence is true because
      // ProtagonistYearDerivationService picks the OLDEST human cast
      // member under 18 (excluding animals) and the frozen story
      // artefact's ALTER section calibrates the SPRACHE band from
      // BUCHHELD_JAHRE. See src/book/story-generation/protagonist-
      // resolver.ts (resolveProtagonist, oldest_under_18 case) +
      // src/book/story-generation/frozen_hashes.ts (## ALTER section
      // in every V12 hash). If that derivation rule ever changes
      // (e.g. first-child-wins, customer-picked protagonist), this
      // sentence has to change with it.
      firstPersonHintP1:
        'Trag zuerst die Kinder ein, danach alle anderen. Die Sprache der Geschichte richtet sich nach dem ältesten Kind.',
      firstPersonHintP2:
        'Schreib bei den Erwachsenen die Bezeichnung statt des Vornamens: „Mama" statt „Sandra", „Opa Peter" statt „Peter". So weiß die Geschichte, wer zu wem gehört, und erzählt es richtig.',

      // Block 2 · 2.4 · Screen-reader labels for the Name + Alter
      // inputs on the per-person edit form. Previously hardcoded as
      // literals in PersonenRoute JSX; moved to STRINGS per the file
      // convention.
      nameAriaLabel: 'Name',
      alterAriaLabel: 'Alter',

      // Sebastian ruling 2026-09-23 (launch-day feedback) — heading
      // above the per-person edit form. Cast[0] in edit shows
      // "Erste Person anlegen"; cast[N>0] shows "Weitere Person
      // anlegen" (mirrors the "Weitere Person oder Tier hinzufügen"
      // AddCard wording sitting below the list). The heading makes
      // clear the customer is entering ONE person at a time and adds
      // the next via the button below.
      personEditHeadingFirst: 'Erste Person anlegen',
      personEditHeadingAdditional: 'Weitere Person anlegen',

      gattungPerson: 'Person',
      gattungTier: 'Tier',
      geschlechtMaennlich: 'männlich',
      geschlechtWeiblich: 'weiblich',
      geschlechtKeineAngabe: 'keine Angabe',
      // Position-2 Schritt B (Sebastian ruling 2026-09-11) — concrete
      // species input, shown only when gattung === 'tier'. Required:
      // the Weiter gate stays disabled until the field is filled, same
      // treatment as Alter. 30-char cap + CharacterCounter, matching
      // the surrounding length-limited fields.
      speciesLabel: 'Welches Tier?',
      speciesPlaceholder: 'Hund, Katze, Pony …',
      speciesError: 'Bitte sag uns, welches Tier das ist.',
      // Sebastian ruling 2026-09-21 (V9 bind) — the per-person free-text
      // field now feeds into the pre-model's PERSONEN line as the
      // dash-suffix `description`, and V9 Rule 5 uses it to derive
      // rolle + narrate relationships in the summary. Label + placeholder
      // + helper line reframed around relationships accordingly. The
      // helper line renders as muted prose immediately under the field.
      personenNotizLabel: 'In welcher Beziehung steht {Name} zu den anderen? (freiwillig)',
      personenNotizLabelOhneName:
        'In welcher Beziehung steht diese Person zu den anderen? (freiwillig)',
      personenNotizPlaceholder: 'z. B. die kleine Schwester von Mats',
      personenNotizHelper: 'Dann erzählt die Geschichte richtig, wer zu wem gehört.',
      titelwunschTitle: 'Hast du einen Titelwunsch?',
      titelwunschSubtitle: 'Wenn du keinen hast, erstellen wir einen Vorschlag.',
      titelwunschSummaryEmpty: 'Wir erstellen einen Vorschlag',
      namePlaceholder: 'Name',
      altersPlaceholder: 'Alter in Jahren',
      // R3 (Sebastian ruling 2026-08-31 evening) — block 1 summary
      // lists cast names in cast order, joined by
      // `personenSummarySeparator`, no trailing period. Entries whose
      // name hasn't been typed yet are skipped. Fallback
      // `personenSummaryOhneNamen` replaces the entire line when
      // NO entry has a name yet (customer never sees a count).
      personenSummarySeparator: ', ',
      personenSummaryOhneNamen: 'Noch ohne Namen',

      // Foto-Regeln verbatim — Textdatei v2 lines 170-172. Four lines
      // that sit AT the upload slot (Bau-Spez v3.1 §6 line 259
      // verbatim). "Eine Person pro Bild" — not "Ein Kind pro Bild"
      // per Änderungslog line 427.
      fotoRules: {
        onePerImage: 'Eine Person pro Bild',
        faceVisible: 'Gesicht frei und gut erkennbar',
        noSunglassesCap: 'keine Sonnenbrille, keine Kappe',
        everydayBetterThanStudio: 'Alltagsfotos sind besser als Studiofotos',
      },
      // Limits line — Sebastian authorship 2026-08-12, sits under
      // EVERY empty upload slot (face + Ganzkörper + reupload after
      // rejection). Source of truth: customer-text file "Limits am
      // Upload-Slot". Formats are deliberately NOT named — HEIC is
      // now converted server-side via heic-convert, so listing
      // "JPG/PNG/WebP" is noise the customer does not need.
      fotoLimitsLine: 'Bis 10 MB. Fotos direkt vom Handy funktionieren.',
      // Privacy note at Q_photo (Sebastian-approved verbatim copy,
      // 2026-10-27). Rendered by PersonenFlow `PhotoQuestion` as a
      // small info box ABOVE the FotoUploadSlot on every cast entry's
      // photo step. The three bullets must stay character-for-
      // character identical to the approved wording; sentence 3
      // depends on the BOOK_UPLOADS_BUCKET region (europe-west3 =
      // Frankfurt) — Sebastian confirms separately before commit.
      // Link opens the public Datenschutzerklärung on the marketing
      // domain in a new tab.
      fotoPrivacyHeading: 'Dein Foto ist bei uns sicher',
      fotoPrivacyUse: 'Wir nutzen dein Foto nur, um daraus deine Figur zu zeichnen.',
      fotoPrivacyDelete: 'Sobald du die Figur annimmst, löschen wir das Foto von unseren Servern.',
      fotoPrivacyStorage: 'Gespeichert wird es in der EU, in Frankfurt.',
      fotoPrivacyLinkText: 'Mehr dazu in unserer Datenschutzerklärung',
      fotoPrivacyLinkHref: 'https://www.dreamable.kids/datenschutz',
      // Client-side upload-validation copy — Sebastian authorship
      // 2026-08-12, source: customer-text file `oversize` / `wrong_
      // format` blockquotes. Both interpolate `{castName}` for
      // context; the wrong-format message names the accept list
      // because it fires on the truly unusual formats we cannot take
      // (gif / bmp / heic-not-detected / heif-not-detected — heic +
      // heif are otherwise accepted via magic-byte detection at the
      // sanitizer). Displayed inline at the slot, replaces today's
      // silent-generic error class (Befund at
      // upload path).
      uploadOversize:
        'Das Bild von {castName} ist zu groß. Wir nehmen Bilder bis 10 Megabyte — meistens hilft es, das Bild kleiner zu speichern oder ein anderes zu nehmen.',
      uploadWrongFormat:
        'Das Bild von {castName} hat ein Format, das wir nicht annehmen. Wir nehmen JPG, PNG und WebP.',
      // Sebastian ruling 2026-10-28 — pick-time byte-read failure.
      // Fires when the browser hands us a `<input type="file">` File
      // whose `arrayBuffer()` rejects or resolves to zero bytes (the
      // Android Chrome lazy content-URI case: Google Photos / Drive /
      // WhatsApp pickers can return a reference that is already stale
      // by the time we try to read it). Same inline position as
      // uploadOversize / uploadWrongFormat; same `{castName}`
      // interpolation; distinct from wrongFormat because the file's
      // declared MIME was fine, we just could not read its bytes.
      uploadReadFailed:
        'Das Foto von {castName} konnte nicht geladen werden. Bitte versuche es mit einem anderen Foto.',
      // Upload-slot chrome — TWO states per Sebastian ruling 2026-08-06
      // (Option 3): no upload happens during Schritt 1 because the
      // signed-upload endpoint is JWT-gated (see tracker
      // `[book-photos-no-preauth-upload]`). The slot's `picked` state
      // shows a local preview from `URL.createObjectURL(file)`; the
      // Light-Gate overlay in Commit 3 owns the "uploading" progress
      // UI at bulk-upload time.
      slotEmptyLabel: 'Foto hinzufügen',
      slotPickedRemoveLabel: 'Entfernen',
      slotAriaEmpty: 'Foto auswählen',
      slotAriaPicked: 'Foto ausgewählt',
      // Sebastian ruling 2026-09-18 — quiet hint next to the small,
      // CSS-cropped thumbnail (`FotoUploadSlot.tsx`, `picked` state) so
      // the customer knows he's looking at an excerpt. The K2 dialog
      // (which fires immediately on upload after the same ruling) then
      // shows the full image.
      slotPickedDetailHint: 'Das ist nur ein Ausschnitt. Wir zeigen dir gleich das ganze Foto.',
      // Sebastian ruling 2026-09-23 — HEIC slot copy (two lines).
      //
      // `slotPreviewConverting` — shown while `previewHeicInBrowser`
      // is decoding the picked HEIC in a Web Worker (libheif-js).
      // Measured at 358 ms full pixel decode for a 12 MP iPhone HEIC
      // on a MacBook desktop; ~1.5 s extrapolated for a throttled
      // phone. 5 s hard timeout.
      //
      // `slotHeicPicked` — final fallback state when native decode
      // AND the worker path both fail (or timeout, or the file is
      // over the 30 MP guard). NOT an error styling — the wording
      // is calm confirmation. Submission still works: the ORIGINAL
      // File goes to GCS untouched, backend sanitizer does the
      // authoritative HEIC → JPEG conversion. Wording revised
      // 2026-09-23 late — Sebastian ruling — from the iPhone-specific
      // "iPhone-Fotos kannst du erst nach dem Hochladen sehen" to a
      // generic per-image line, since any HEIC over the guard or
      // any decode fault (not only iPhone HEIC) can land here.
      slotPreviewConverting: 'Vorschau wird erstellt …',
      slotHeicPicked:
        'Foto ausgewählt. Vorschau ist bei diesem Bild nicht möglich, es wird trotzdem verwendet.',
      // Sebastian ruling 2026-09-18 — replaces the pre-2026-09-18
      // silent removal of the "+ Weitere Person oder Tier hinzufügen"
      // tile at cast.length === max_cast_entries. Rendered in-place so
      // the customer knows why the affordance is gone. Reads
      // `BOOK_CONFIG_CONSTANTS.max_cast_entries` for the number.
      personenCapReachedNote:
        'Mehr als neun Personen und Tiere passen nicht in ein Buch. Wenn du eine austauschen willst, entferne eine und leg eine neue an.',
      // Sebastian ruling 2026-09-19 — quiet line above the disabled
      // "Speichern" button on the per-person edit form
      // (`PersonenEditForm` in `PersonenRoute.tsx`). Names the fields
      // that are still empty, in the order name → age → photo →
      // species. Sex is NOT a Save-gating field today (`Geschlecht`
      // defaults to `'keineAngabe'`), so it does not appear in the
      // list. Two sentence templates — singular ("Es fehlt noch das
      // Foto.") and plural ("Es fehlen noch <fields>."), with the
      // fields list already composed by the caller. Field labels are
      // stored as separate keys so wording stays changeable.
      saveMissingSentenceOne: (field: string): string => `Es fehlt noch ${field}.`,
      saveMissingSentencePlural: (fields: string): string => `Es fehlen noch ${fields}.`,
      saveMissingFieldName: 'der Name',
      saveMissingFieldAge: 'das Alter',
      // Sebastian ruling 2026-09-19 — sex is a required choice on
      // person entries (three options, no default). Missing when
      // `entry.personType === 'person' && entry.geschlecht === ''`.
      // Not gated on animal entries (they keep 'keineAngabe' as a
      // default). Position in the sentence: after age, before photo.
      saveMissingFieldSex: 'das Geschlecht',
      saveMissingFieldPhoto: 'das Foto',
      saveMissingFieldSpecies: 'die Tierart',

      // Weitere Personen block — Textdatei v2 lines 174-176 verbatim.
      weitereTitle: 'Wer kommt noch vor?',
      weitereSubtitle: 'Bis zu acht Personen und Tiere.',
      weitereAddCardLabel: '+ Person hinzufügen',
      // Personen-Karte edit/remove/mittelpunkt strings.
      karteEditLabel: 'Ändern',
      karteRemoveLabel: 'Entfernen',
      karteMittelpunktBadge: 'Mittelpunkt',
      karteAriaLabel: '{name}, {age} Jahre',

      // Mittelpunkt block — Textdatei v2 lines 178-180 verbatim.
      // Bau-Spez v3.1 §6 line 261: chips, mehrfach oder keine wählbar.
      mittelpunktTitle: 'Wer steht im Mittelpunkt?',
      mittelpunktSubtitle: 'Antippen. Du kannst auch mehrere wählen oder keinen.',

      // Freitext block — Textdatei v2 lines 182-187 verbatim. Uses
      // "deinem" not "eurem" per §0 (Kundentexte via Textdatei); the
      // Bau-Spez v3.1 §6 line 262 draft "eurem" is superseded.
      freitextTitle: 'Worum soll es in deinem Buch gehen?',
      // Block 2 · 2.7 · Sub-line REPLACE (Sebastian ruling 2026-09-09).
      freitextSubtitle:
        'Beschreib die Geschichte so genau wie möglich: Orte, Ereignisse, wer dabei ist und wie die Personen zueinander stehen. Je mehr du erzählst, desto besser wird euer Buch.',
      // Diktier-Hinweis — Textdatei v2 line 187 verbatim.
      freitextDictateHint: '🎤 Lieber erzählen als tippen? Auf deiner Tastatur ist ein Mikrofon.',
      // Erfundenes-Erlaubnis — Textdatei v2 line 195 verbatim. Sits
      // neben dem Freitextfeld per Textdatei line 194.
      freitextInventedAllowed:
        'Es muss nichts Erlebtes sein. Du kannst auch eine Geschichte erfinden.',

      // Inspirationszeile — Textdatei v2 lines 189-192 + line 201.
      // Sechs Anlässe als Zeile ohne Klickfunktion; je Anlass ein
      // click-triggered Panel mit dem Beispieltext (Sebastian ruling
      // 2026-08-05, four-answers). Anlass-Klappzeile ist gestrichen
      // (v3.1 §0.1 L-16).
      //
      // Rowlead — Textdatei v2 line 201 verbatim, "Wie ausführlich?
      // Ein Beispiel ansehen". Das ist der wichtigste Text auf dem
      // Schirm nach dem Streichen des Vorlagen-Konzepts (Sebastian
      // 2026-08-05): die Beispieltexte sind das einzige Element, das
      // dem Kunden zeigt, wie viel er schreiben soll.
      inspirationRowLead: 'Wie ausführlich? Ein Beispiel ansehen',
      // Block 2 · 2.8 · Four real occasions kept, Geburt + Weihnachten
      // DROPPED (Sebastian ruling 2026-09-09). Order stays reading-
      // order: Einschulung → Geburtstag → Urlaub → Erster Kita-Tag.
      anlaesse: {
        einschulung: 'Einschulung',
        geburtstag: 'Geburtstag',
        urlaub: 'Urlaub',
        ersterKitaTag: 'Erster Kita-Tag',
      },
      // Vier reale Beispieltexte + drei erfundene (Sebastian ruling
      // 2026-09-09). Reale: `einschulung` + `ersterKitaTag` unverändert,
      // `geburtstag` KOMPLETT ERSETZT (die "familie unterwegs" Version
      // benannt die Grosseltern nicht persönlich; die neue Version
      // nennt Rita/Bärbel/Peter/Werner/Emma/Paul), `urlaub` mit einer
      // einzigen Ersetzung "und wir beide" → "und Mama und Papa".
      // Geburt + Weihnachten entfernt.
      beispieltexte: {
        urlaub:
          'Wir waren zwei Wochen in Südfrankreich, direkt am Meer. Dabei waren Ben, damals 3, seine kleine Schwester Ida mit sieben Monaten, und Mama und Papa. Ben ist mit seinem Koffer zwischen den Palmen hergelaufen, als wäre er schon hundertmal da gewesen. Morgens sind er und Papa Baguette holen gegangen, Ida im Kinderwagen dazwischen. Am Strand hat er eine Sandburg mit seinem blauen Eimer gebaut, und im Pool ist er mit Schwimmflügeln herumgepaddelt. Am meisten hat ihm das grüne Elektro-Wägelchen gefallen, mit dem wir über den Platz gefahren sind. Er durfte lenken. Am letzten Abend ist er neben dem Karussell Trampolin gesprungen, bis er nicht mehr konnte.',
        einschulung:
          'Lina ist im September in die Schule gekommen, sie war gerade 6. Morgens war sie so aufgeregt, dass sie kaum frühstücken konnte. Die Schultüte hatte Oma gebastelt, rosa, mit Pferden drauf, und Lina hat sie den ganzen Weg selbst getragen. Ihre kleine Schwester Emma, 3, ist an Papas Hand mitgelaufen und hat gestaunt. In der Schule waren überall Kinder mit bunten Tüten, aber Lina hat ihre nie aus der Hand gegeben. Bei der Feier hat die Lehrerin sie begrüßt, und im Klassenzimmer hat sie sich gleich gemeldet. Nachmittags haben wir die Tüte im Garten ausgepackt. Unser Hund Paul hat sich ein Band geschnappt und ist damit weggerannt.',
        geburtstag:
          'Jonas ist im Mai vier geworden, und wir waren an dem Tag mit der ganzen Familie unterwegs. Vormittags auf dem Markt, mit Oma Rita, Oma Bärbel, Opa Peter und Opa Werner, seiner kleinen Schwester Emma und unserem Hund Paul. Danach gab es Frühstück in einem Café mit Garten. Zu Hause kamen dann noch seine Freunde dazu, und es gab Kuchen in Form eines Feuerwehrautos, den Jonas zuerst gar nicht anschneiden lassen wollte. Nachmittags sind wir alle zusammen durch die Stadt gelaufen und mit einem Bus gefahren, in dem man oben sitzen kann. Jonas saß ganz vorn. Abends waren wir noch essen, und er ist am Tisch fast eingeschlafen.',
        ersterKitaTag:
          'Mia ist mit knapp drei in die Kita gekommen. Am Morgen davor hat sie ihren Rucksack dreimal aus- und wieder eingeräumt. Wir sind zu Fuß hingelaufen, es war noch kühl, und sie hat die ganze Zeit meine Hand gehalten. Vor der Tür stand ihre Erzieherin und hat sie mit Namen begrüßt, das hat geholfen. Der Abschied war dann kurz, sie ist einmal umgedreht und dann reingegangen. Ich habe draußen noch eine Weile gestanden. Als ich sie mittags abgeholt habe, kam sie mit einem gemalten Bild angerannt und wollte gar nicht gleich mit. Abends hat sie beim Essen die Namen von vier Kindern aufgezählt, die sie sich schon gemerkt hatte.',
      },
      // Block 2 · 2.8 · Three invented examples (Sebastian ruling
      // 2026-09-09). Each has a `kurz` (short-form single sentence)
      // and an `ausführlich` (long-form paragraph). The panel shows
      // both, `kurz` above `ausführlich`. Character-verbatim from the
      // Block 2 spec.
      invented: {
        superheld: {
          label: 'Superheld',
          kurz: 'Lena erlebt einen Tag als Superheldin.',
          ausfuehrlich:
            'Lena erlebt einen Tag als Superheldin. Morgens entdeckt sie in ihrem Schrank einen Umhang, den es am Abend vorher noch nicht gab. Als sie ihn umlegt, kann sie plötzlich springen, so hoch sie will. Auf dem Weg zum Spielplatz hört sie ein Miauen und holt eine Katze vom Dach eines Schuppens. Später hilft sie einem Mann, dessen Einkäufe über die Straße gerollt sind, und hält dabei mit einer Hand einen ganzen Einkaufswagen fest. Am Nachmittag rettet sie im Park einen Drachen aus einem Baum. Abends hängt der Umhang wieder im Schrank, und nur Lena weiß, warum er nach Wind riecht.',
        },
        polizist: {
          label: 'Polizist',
          kurz: 'Til erlebt einen Abenteuertag als Polizist.',
          ausfuehrlich:
            'Til erlebt einen Abenteuertag als Polizist. Morgens zieht er die Uniform an und fährt zur Wache, wo er ein Funkgerät bekommt und das Streifenauto kennenlernt. Dann kommt ein Notruf aus dem Zoo: Ein Elefant steckt im Zaun seines Geheges fest, und Til öffnet gemeinsam mit dem Tierpfleger das Gatter. Später regelt er an einer großen Kreuzung den Verkehr, und im Supermarkt hilft er einem Jungen, der seine Mama nicht mehr findet. Am Abend fährt er stolz nach Hause und erzählt allen davon.',
        },
        // Block 3 correction (Sebastian test-book finding 2026-09-09):
        // renamed weltall → astronaut so all three invented tiles are
        // roles (Superheld / Polizist / Astronaut), not a mix of
        // roles + destinations. The `kurz` + `ausfuehrlich` texts are
        // unchanged — they still describe Mia's day in space, just
        // under a role-name tile.
        astronaut: {
          label: 'Astronaut',
          kurz: 'Mia fliegt für einen Tag ins Weltall.',
          ausfuehrlich:
            'Mia fliegt für einen Tag ins Weltall. Sie zieht den Raumanzug an, steigt in die Rakete und schaut beim Start aus dem kleinen Fenster, wie die Erde immer kleiner wird. In der Raumstation schwebt sie durch die Gänge und trinkt aus einem Beutel, weil das Wasser sonst davonfliegen würde. Draußen repariert sie zusammen mit einer Astronautin eine Antenne. Auf dem Mond sammelt sie einen Stein ein und nimmt ihn mit nach Hause.',
        },
      },
      // InfoPanel a11y — Panel-body aria-label per Anlass.
      inspirationPanelAriaLabel: 'Beispieltext {anlass}',
      inspirationTriggerAriaLabel: 'Beispieltext zu {anlass} öffnen',
      inspirationPanelCloseLabel: 'Schließen',

      // Sprache block — Textdatei v2 lines 237-238 verbatim.
      // Sebastian ruling 2026-08-05, four-answers item 3: real
      // Auswahlfeld with German preselected. Wenn nur Deutsch heute
      // ausgeliefert wird, ist das eine Liste mit einem Eintrag,
      // kein deaktiviertes Feld.
      spracheTitle: 'In welcher Sprache?',
      spracheGermanLabel: 'Deutsch',

      // Weiter-Knopf + Beruhigungszeile — Textdatei v2 lines 240-244
      // verbatim. Beruhigungszeile steht nur hier und in
      // Wartemomenten (Textdatei line 243, v3.1 §6 line 274).
      // Block 2 · 2.9 · Button label REPLACE at BOTH places (under
      // the inputs AND inside the LightGate registration overlay).
      // Sebastian ruling 2026-09-09: the customer's next screen is
      // Geschichte (summary_review), not the Titelbild step which
      // sits three states further along the flow.
      weiterCta: 'Weiter zur Geschichte',
      beruhigungszeile: 'Alles ist gespeichert. Du kannst jederzeit später weitermachen.',

      // ═════════════════════════════════════════════════════════════
      // Light-Gate overlay — Sebastian authorship 2026-08-06,
      // wording overhaul 2026-09-07.
      //
      // Two shape rules that survived the overhaul, recorded here so
      // future authors don't undo them:
      //   1. `lightgateRegisterCta` is `Weiter zum Titelbild`, not
      //      `Weiter` — same label as the button the customer just
      //      pressed. He is continuing one action, not starting a
      //      second.
      //   2. Error lines end with `Versuch es nochmal` instead of
      //      `Bitte nochmal versuchen`. At this point the customer
      //      has handed over his data and photos and can do nothing
      //      about a failed step. Telling him to try again puts our
      //      problem on him — the shorter imperative reads as "the
      //      button below will do it", not "you are being asked".
      //
      // 2026-09-07 wording overhaul (Sebastian ruling): the overlay
      // stops narrating what the backend is doing. `lightgateUploadingHeading`
      // renamed to `photoUploadOverlayHeading` with text "Danke. Wir
      // legen dein Buch an." — same message during upload AND
      // create-order, one truthful line about the customer outcome.
      // `lightgateUploadingProgress` (the "{n} von {total}" counter)
      // deleted entirely — knowing "3 von 8 hochgeladen" is not
      // information the customer can act on and it made a wait
      // screen busier. `lightgateUploadErrorPlaceholder` renamed to
      // `photoUploadOverlayError` with text "Etwas hat nicht
      // geklappt. Versuch es nochmal." — generic because both an
      // upload failure and a photo-slot-boundary failure now surface
      // through the same string.
      // ═════════════════════════════════════════════════════════════
      lightgateTitle: 'Fast geschafft.',
      // Sebastian correction 2026-08-06 to §A4 "Wording aus Nutzer-
      // wert, nicht aus Schutzbedürfnis". Earlier CC draft was
      // process description ("Wir speichern deine Angaben und
      // legen dein Buch an. Danach zeichnen wir dein Titelbild.")
      // — replaced with the value-forward line the spec's own
      // example points at ("Damit dein Buchheld gespeichert
      // bleibt…").
      lightgateSubtitle: 'Damit dein Buch gespeichert bleibt und du jederzeit weitermachen kannst.',
      lightgateEmailLabel: 'E-Mail',
      // Sebastian ruling 2026-09-23 — register view only. The login
      // sub-view of the LightGate uses `lightgateLoginPasswordLabel`
      // below; a customer signing in with an existing account is
      // NOT creating a password. Prior to the split both sub-views
      // shared this key, which asked login users to "set a new
      // password" during a sign-in — dishonest and confusing.
      lightgatePasswordLabel: 'Neues Passwort festlegen',
      lightgateLoginPasswordLabel: 'Passwort',
      // Sebastian ruling 2026-09-23 (launch-day feedback) — the static
      // `lightgatePasswordHint` line ("Mindestens 8 Zeichen, ein
      // Großbuchstabe und eine Zahl.") that used to sit under the
      // password field was deleted along with its overlay reference.
      // Replaced by `PasswordRequirementsChecklist` with
      // `pendingTone="error"` so unmet rules render in red per-rule,
      // not as a single grey static sentence. Copy source is now
      // `STRINGS.account.password.requirements.{minLength,uppercase,digit}`.
      lightgateTermsCheckbox: 'Ich stimme den Nutzungsbedingungen und der Datenschutzerklärung zu.',
      // Block 2 · 2.9 · SAME wording as `weiterCta` above (Sebastian
      // ruling 2026-09-09: "at BOTH places, the button under the
      // inputs AND the button inside the registration overlay").
      lightgateRegisterCta: 'Weiter zur Geschichte',
      lightgateRegisterErrorPlaceholder:
        'Das hat nicht geklappt. Prüf bitte deine E-Mail-Adresse und dein Passwort. Falls du schon ein Dreamable-Konto hast, melde dich damit an.',
      // Sebastian ruling 2026-09-09 — Task 1 (login-in-overlay).
      // A returning customer whose session expired lands anonymous at
      // /book, sees the register form. The link below the register
      // primary button lets him switch the overlay to the login phase
      // instead of failing on "email already taken". Overlay-internal
      // phase change; NEVER navigates to /auth/login (up to sixteen
      // File objects in IndexedDB + configurator text state in
      // localStorage; leaving the screen is an unnecessary bet on
      // mobile Safari memory pressure).
      lightgateHasAccountLink: 'Du hast schon ein Konto? Anmelden',
      lightgateLoginHeading: 'Willkommen zurück',
      lightgateLoginCta: 'Anmelden',
      // Deliberately vague — naming which of the two fields was wrong
      // is both unhelpful to the customer and a small information
      // leak about which email addresses exist in the system
      // (Sebastian ruling 2026-09-09).
      lightgateLoginError: 'Das hat nicht geklappt. Versuch es noch einmal.',
      lightgateNoAccountLink: 'Noch kein Konto? Jetzt anlegen',
      photoUploadOverlayHeading: 'Danke. Wir legen dein Buch an.',
      photoUploadOverlayError: 'Etwas hat nicht geklappt. Versuch es nochmal.',
      lightgateCreatingOrderHeading: 'Wir legen dein Buch an.',
      lightgateCreateOrderErrorPlaceholder:
        'Dein Buch konnte nicht angelegt werden. Wir versuchen es nochmal.',
      lightgateRetryCta: 'Nochmal versuchen',
      // Per-person pre-upload readability error (Sebastian ruling
      // 2026-10-28). Interpolates `{name}` with the name the customer
      // entered for the affected person. CTA closes the overlay and
      // routes the customer back to that person's photo slot.
      lightgatePhotoUnreadable:
        'Das Foto von {name} konnte nicht geladen werden. Bitte wähl es noch einmal aus.',
      lightgatePhotoUnreadableCta: 'Foto neu wählen',
      // Returning-customer branch (2026-08-06, Case 2). A logged-in
      // customer (audio subscriber starting a book, or a returning
      // book customer without a pending order) skips the register
      // form entirely per §A4 "Bestandskunden überspringen das Gate."
      // No title "Fast geschafft.", no subtitle, no terms, no
      // beruhigungszeile — the overlay shows only the phase heading
      // (photoUploadOverlayHeading → lightgateCreatingOrderHeading)
      // and any error/retry. Radix's DialogDescription is still
      // rendered (sr-only) so screen readers get a spoken hint of
      // what's happening. Wording aligned 2026-09-07 to match the
      // visible overlay heading ("Wir legen dein Buch an.") — the
      // pre-2026-09-07 text mentioned "Fotos hochladen" which the
      // sighted overlay no longer says.
      lightgateReturningA11yPlaceholder: 'Wir legen dein Buch an.',

      // ═════════════════════════════════════════════════════════════
      // K2 dialog — Sebastian authorship 2026-08-06.
      // ═════════════════════════════════════════════════════════════
      k2DialogTitle: 'Person bestätigen',
      k2DialogDescription: 'Stimmen die Angaben, oder möchtest du etwas ändern?',
      k2ConfirmCta: 'Passt so',
      // Sebastian ruling 2026-09-18 — pre-ruling `k2EditDetailsLink:
      // 'Details anpassen'` closed the dialog and returned the
      // customer to the edit form. New meaning: drop the picked
      // photo and reopen the OS file picker on the same slot in one
      // motion. Key renamed so any lingering caller reading the old
      // string trips the type checker.
      k2ReplacePhotoLink: 'Anderes Foto wählen',

      // Per-block "Weiter" button label + save-editing-cast label.
      // Neubau, no Textdatei source — matches the Sub-line of the
      // block Weiter idiom ("Weiter zu <next>").
      blockWeiterLabel: 'Weiter',
      blockPersonSaveLabel: 'Speichern',

      // Mittelpunkt summary when nobody is marked — Textdatei v2 line
      // 180 explicitly allows zero selections.
      mittelpunktSummaryNone: 'Niemand markiert',

      // SchrittBlock a11y — the numbered block wrapper primitive.
      // "Ändern" is the answered-state edit affordance.
      blockEditLabel: 'Ändern',
    },
    // ══════════════════════════════════════════════════════════════════
    // PROTOTYPE – texts not approved
    //
    // Geschichtenschritt stations prototype (feat/story-step-stations-
    // prototype, Sebastian GO 2026-10-08). All strings below live under
    // this block ONLY and are flagged as unapproved placeholder wording.
    // Approved reuses:
    //   - STRINGS.book.personen.freitextDictateHint (dictate tip)
    //   - STRINGS.book.personen.beispieltexte.* (4 erlebt examples)
    //   - STRINGS.book.personen.invented.{superheld,polizist,astronaut}
    //
    // Four new invented example texts (feuerwehr, piraten, zauberwald,
    // dinosaurier) are approved by Sebastian 2026-10-08 verbatim and
    // live here so the production invented block stays untouched by the
    // prototype branch.
    // ══════════════════════════════════════════════════════════════════
    geschichtenschrittPrototype: {
      // Round 3 (Sebastian click-mockup 2026-10-08 evening) — the
      // approved layout:
      //   step 1  two side-by-side image cards (erlebt | erfunden)
      //   step 2 erfunden  "Eigene Idee" tile first, then 2-col grid of 8
      //   step 3 erfunden  themed heading + prefilled field + examples box
      //   step 2 erlebt    free textarea + examples box

      // Step 1 — path choice.
      pathChoiceHeading: 'Worum soll es in eurem Buch gehen?',
      pathChoiceSubline: 'Wähle eins von beiden.',
      pathChoiceErlebtTitle: 'Etwas, das ihr erlebt habt',
      pathChoiceErlebtExamples: 'Beispiele: Urlaub, Einschulung, Geburtstag, Ausflug',
      pathChoiceErfundenTitle: 'Ein erfundenes Abenteuer',
      pathChoiceErfundenExamples: 'Beispiele: Feuerwehr, Weltraum, Piraten, Superhelden',
      pathChoiceErlebtImgAlt: 'Familie schaut aufs Meer',
      pathChoiceErfundenImgAlt: 'Kind als Astronaut im Weltraum',

      // Erfunden step 2 — dynamic question heading (name-count branches)
      // and tile labels. The "Eigene Idee" tile sits at the top on its
      // own row; the other 8 live in a 2-col grid.
      adventureQuestionOne: 'Welches Abenteuer will {name} erleben?',
      adventureQuestionFew: 'Welches Abenteuer wollen {names} erleben?',
      adventureQuestionMany: 'Welches Abenteuer wollt ihr erleben?',
      eigeneIdeeTitle: 'Eigene Idee',
      eigeneIdeeSubline: 'Du schreibst selbst, worum es gehen soll.',
      orSearchAdventureLead: 'Oder sucht euch ein Abenteuer aus:',
      erfundenTiles: {
        feuerwehr: 'Feuerwehr-Abenteuer',
        weltraum: 'Weltraum-Abenteuer',
        piraten: 'Piraten-Abenteuer',
        superhelden: 'Superhelden-Abenteuer',
        polizei: 'Polizei-Abenteuer',
        zauberwald: 'Zauberwald-Abenteuer',
        dino: 'Dino-Abenteuer',
        unterwasser: 'Unterwasser-Abenteuer',
        eigene: 'Eigene Idee',
      },

      // Erfunden step 3 — theme word per tile and per-tile prefill
      // sentences. The `erfundenThemes` map drives (a) the themed field
      // heading via `erfundenFieldHeadingTemplate` and (b) the themed
      // examples heading via `examplesHeadingThemed`. The per-tile
      // sentences in `erfundenSentenceTemplates` were approved by
      // Sebastian 2026-10-09 10:15 — each one is theme-specific, so the
      // only remaining placeholder is `{Namen}`. `eigene` has no
      // sentence (empty field).
      erfundenThemes: {
        feuerwehr: 'Feuerwehr',
        weltraum: 'Weltraum',
        piraten: 'Piraten',
        superhelden: 'Superhelden',
        polizei: 'Polizei',
        zauberwald: 'Zauberwald',
        dino: 'Dino',
        unterwasser: 'Unterwasser',
      },
      erfundenSentenceTemplates: {
        feuerwehr:
          'Ein Feuerwehr-Abenteuer mit {Namen}: Alarm auf der Wache, mit Blaulicht durch die Stadt, hoch hinauf auf der Drehleiter und am Ende eine Rettung, die alle feiern.',
        weltraum:
          'Ein Weltraum-Abenteuer mit {Namen}: Countdown und Start der Rakete, schwerelos durch die Raumstation, große Sprünge auf dem Mond und ein Stern als Andenken für zu Hause.',
        piraten:
          'Ein Piraten-Abenteuer mit {Namen}: Segel setzen auf dem Piratenschiff, eine Schatzkarte in der Flaschenpost, eine Insel mit Palmen und eine Kiste voller Schätze.',
        superhelden:
          'Ein Superhelden-Abenteuer mit {Namen}: ein Umhang mit geheimen Kräften, Rettungen in der ganzen Stadt und ein Geheimnis, das sonst niemand kennt.',
        polizei:
          'Ein Polizei-Abenteuer mit {Namen}: Dienstbeginn auf der Wache, mit dem Streifenwagen zum ersten Einsatz, Spuren sichern und am Ende ein Gummibärchen-Dieb, der nicht davonkommt.',
        zauberwald:
          'Ein Zauberwald-Abenteuer mit {Namen}: ein verborgener Weg hinter dem Garten, sprechende Tiere, leuchtende Pilze und ein Rätsel, das gelöst werden will.',
        dino: 'Ein Dino-Abenteuer mit {Namen}: eine geheime Tür in die Urzeit, ein Ritt auf einem freundlichen Triceratops, riesige Farne und ein lautes Brüllen in der Ferne.',
        unterwasser:
          'Ein Unterwasser-Abenteuer mit {Namen}: abtauchen im gelben U-Boot, singende Wale, leuchtende Fische in der Tiefe und ein Meerestier, das Hilfe braucht.',
      },

      // Field headings per screen (round-3 ruling: one heading per screen,
      // appears inside the SchrittBlock body).
      erlebtFieldHeading: 'Was habt ihr erlebt?',
      erfundenFieldHeadingTemplate: 'Euer {Theme}-Abenteuer',
      eigeneFieldHeading: 'Eure eigene Idee',

      // Textarea placeholders per path.
      erlebtPlaceholder: 'Erzähl, was ihr erlebt habt: wo, was passiert ist und wer dabei war.',
      erfundenPlaceholder: 'Erzähl, worum es in eurem Abenteuer gehen soll.',

      // Erfunden step 3 — line under the field (approved).
      erfundenFieldMoreHint: 'Du kannst noch mehr dazuschreiben, musst aber nicht.',

      // Shared Beispiele box heading + right-aligned hint. The themed
      // variant (approved Sebastian 2026-10-09 10:15) is used on the
      // erfunden-field screen when a theme tile is selected — the box
      // shows exactly one matching example and the heading names the
      // theme. `eigene` and `erlebt` continue to use `examplesHeading`.
      examplesHeading: 'Beispiele',
      examplesHeadingThemed: 'So kann eine ausführliche {Thema}-Geschichte aussehen',
      examplesTapHint: 'Zum Lesen antippen',

      // Confirm when the user has edited the prefilled sentence and
      // taps another tile: switching would overwrite the edit.
      tileReplaceConfirmLabel: 'Dein Text wird durch den neuen Satz ersetzt. Wirklich ersetzen?',

      // Prototype invented examples. Four approved 2026-10-08 round 1
      // (feuerwehr, piraten, zauberwald, dinosaurier). Two added
      // 2026-10-09 10:15 (weltraum = Oskar; unterwasser = Jakob) —
      // these two carry label + ausfuehrlich only; `kurz` is omitted
      // because the erfunden Beispiele box only renders `label` + the
      // first-sentence teaser + `ausfuehrlich`. The production
      // `STRINGS.book.personen.invented.astronaut` (Mia) is still used
      // by InspirationRow in the Personen step, so the production
      // string stays untouched; the geschichtenschritt example list
      // no longer references it.
      inventedPrototype: {
        feuerwehr: {
          label: 'Feuerwehr',
          kurz: 'Emil verbringt einen Tag bei der Feuerwehr.',
          ausfuehrlich:
            'Emil verbringt einen Tag bei der Feuerwehr. Morgens bekommt er auf der Wache einen echten Helm und eine Jacke, die ihm bis zu den Knien reicht. Plötzlich geht die Glocke, und alle rutschen die Stange hinunter zum großen roten Löschfahrzeug. Mit Blaulicht fahren sie zu einem Baum, in dem eine Katze festsitzt, und Emil darf mit der Drehleiter nach oben. Am Nachmittag üben sie auf dem Hof mit dem Schlauch und spritzen dabei aus Versehen den Chef der Wache nass. Zum Abschied darf Emil einmal die Sirene anschalten.',
        },
        weltraum: {
          label: 'Weltraum',
          ausfuehrlich:
            'Am Abend wünscht sich Oskars kleine Schwester Mila einen eigenen Stern, und Oskar verspricht ihr, einen mitzubringen. Im Garten steigt er in seinem weißen Raumanzug mit dem durchsichtigen Helm, durch den man sein Gesicht sieht, in eine silberne Rakete mit rotem Spitzdach, zählt von zehn herunter und startet in den Himmel. Von oben sieht er die Erde als blaue Kugel, bevor er am Saturn ankommt und dort auf den Ringen wie auf einer Rutsche herabrutscht. Ein freundlicher Komet mit glitzerndem Schweif zeigt ihm den Weg weiter zum Mond, wo Oskar mit großen Sprüngen über die Oberfläche hüpft und Fußspuren hinterlässt. Mit einem Fernrohr entdeckt er einen kleinen, schimmernden Stern, der ganz allein ist, und fängt ihn vorsichtig mit einem Glas ein. Mit dem Glas im Arm fliegt er in seiner Rakete zurück und landet sanft im Garten. Zu Hause schenkt er Mila das leuchtende Glas, worauf das ganze Zimmer funkelt und Mila und Mama staunen.',
        },
        piraten: {
          label: 'Piraten',
          kurz: 'Frieda segelt einen Tag mit einem Piratenschiff.',
          ausfuehrlich:
            'Frieda segelt einen Tag mit einem Piratenschiff. Am Hafen wartet ein Schiff mit schwarzen Segeln, und der Kapitän setzt ihr einen Hut mit Feder auf. Auf dem Meer fischt sie eine Flasche mit einer alten Schatzkarte aus dem Wasser. Sie steuern eine kleine Insel mit Palmen an, wo ein Papagei ihnen den Weg zum großen Felsen zeigt. Dort graben sie eine Kiste aus, voller Muscheln, bunter Steine und einer goldenen Münze. Bei Sonnenuntergang segeln sie zurück, und Frieda darf das Steuerrad halten.',
        },
        zauberwald: {
          label: 'Zauberwald',
          kurz: 'Noah entdeckt hinter dem Garten einen Zauberwald.',
          ausfuehrlich:
            'Noah entdeckt hinter dem Garten einen Zauberwald. Ein schmaler Weg führt zwischen Bäumen hindurch, die leise flüstern, und am Boden leuchten kleine Pilze. Ein Fuchs mit einem roten Halstuch bittet ihn um Hilfe: Die Eule hat den Schlüssel zu ihrem Baumhaus verloren. Gemeinsam suchen sie am Bach, unter Blättern und in einem hohlen Baumstamm, bis Noah ihn im Moos glitzern sieht. Zum Dank lädt die Eule beide zu Beerenkuchen ins Baumhaus ein. Auf dem Heimweg schenkt der Fuchs Noah eine Feder, die im Dunkeln schimmert.',
        },
        dinosaurier: {
          label: 'Dinosaurier',
          kurz: 'Leni reist für einen Tag zu den Dinosauriern.',
          ausfuehrlich:
            'Leni reist für einen Tag zu den Dinosauriern. Durch eine alte Tür im Museum kommt sie in einen Urwald mit riesigen Farnen. Ein kleiner Triceratops wird sofort ihr Freund und lässt sie auf seinem Rücken reiten. Am See trinken die langhalsigen Saurier, und Leni darf einem den Hals streicheln. Als es in der Ferne laut brüllt, verstecken sich die beiden hinter einem Felsen, bis der große Dinosaurier vorbeigestapft ist. Durch die Tür kommt Leni zurück ins Museum und hat noch Sand an den Schuhen.',
        },
        unterwasser: {
          label: 'Unterwasser',
          ausfuehrlich:
            'Jakob ist 10, seine Mama ist Meeresforscherin. In den Ferien darf er zum ersten Mal mit auf ihr Forschungsschiff. Jakob trägt die ganze Geschichte über sein dunkelblaues T-Shirt.\nMitten auf dem Ozean steigen die beiden in ein kleines gelbes U-Boot mit runden Fenstern und tauchen ab.\nPlötzlich schwimmt eine riesige Buckelwal-Mutter mit ihrem Kalb am Fenster vorbei. Die Wale singen, und das U-Boot summt mit.\nDas Kalb hat sich in einem alten Fischernetz verfangen. Jakob entdeckt es zuerst.\nMit dem Greifarm des U-Boots schneidet Jakob das Netz vorsichtig auf, Mama steuert ganz ruhig.\nDas Kalb ist frei und stupst zum Dank mit der Nase an das Fenster.\nDie Wale führen das U-Boot tiefer hinab, wo Quallen und Fische im Dunkeln leuchten wie Sterne.\nZurück an der Oberfläche springt die Wal-Mutter vor dem Sonnenuntergang aus dem Wasser, als wollte sie Danke sagen.\nAm Abend trägt Jakob an Deck ins Logbuch des Schiffs ein: Heute ein Walkind gerettet.\nTon: staunend, spannend, voller Meer und Licht.',
        },
      },

      // Common nav
      weiterLabel: 'Weiter',
      zurueckLabel: 'Zurück',
      summaryEmpty: 'Noch nichts erzählt',
    },
    // Commit A — the Vorschau screen 4 namespace, registered up front
    // so later slices touch only pwa/src/features/book/.
    //
    // Source-of-truth verbatim texts come from
    // `docs/geschichtenbuch/P2_Text_und_Prompt_Entscheidungen.md`
    // (Schritt 4 · Vorschau block, lines 226-263). Structural mapping
    // to compositions A and B is in Bau-Spez v3 §6, lines 317-352.
    //
    // Two compositions share the same key set — the route decides
    // which subset to render (composition A while the title image is
    // large, composition B while an inner page is large). The
    // Warteschirm keys apply to `preview_rendering`; the rest apply to
    // `preview_ready`.
    //
    // Templating uses the house `{key}` placeholder convention, e.g.
    // `positionPage: 'Seite {seite} · {n} von 5'`. Consumers replace
    // via string.replace at render time.
    preview: {
      // Block 5 · 5.4 (Sebastian ruling 2026-09-09) — eyebrow line
      // for the preview screen. Rendered via the Eyebrow primitive
      // above the h1 heading.
      eyebrow: 'EURE VORSCHAU',
      // Block 5 · 5.4 (Sebastian ruling 2026-09-09) — heading REPLACED
      // ("Deine Figuren und vier Seiten aus dem Buch zur Vorschau" →
      // "Eure Figuren und vier Seiten aus dem Buch"). "Deine" → "eure"
      // and the redundant "zur Vorschau" dropped.
      heading: 'Eure Figuren und vier Seiten aus dem Buch',
      // Block 5 · 5.4 (Sebastian ruling 2026-09-09) — intro paragraph
      // REPLACED. New wording ties the preview to what happens after
      // ("Dann entsteht euer vollständiges Buch mit allen Seiten.")
      // instead of naming a page count (a decision constant that
      // could drift).
      headingSub:
        'Hier siehst du, wie eure Figuren aussehen, und vier Seiten aus verschiedenen Stellen eurer Geschichte. Gefällt es dir genauso gut wie uns? Dann entsteht euer vollständiges Buch mit allen Seiten.',

      // Positional line under the big view on PreviewScreen (before
      // payment). The customer sees the BARE anchor image here — no
      // title cartouche, no cover treatment. "Eure Figuren" is the
      // correct label at this point: it names what the image shows
      // (the personalized figures). Sebastian ruling 2026-09-12
      // explicitly rules that positionCover STAYS "Eure Figuren"
      // even though thumbCoverLabel (below) is now "Euer Cover" —
      // these are two different things at two different points in
      // the flow, not one thing with two labels. The 2026-08-12
      // "same wording as the tiles" invariant does NOT apply here
      // because the tile caption downstream (post-payment wait
      // screen) shows the anchor WITH the cartouche painted on top,
      // whereas the pre-payment big view shows the anchor WITHOUT.
      // positionPage interpolates the tile's positional letter +
      // actual pageNo (distributed selection means pageNo is NOT
      // the tile index — for a 20-page book, tile B sits at pageNo 7).
      // Block 5 · 5.5 (Sebastian ruling 2026-09-09) — captions BELOW
      // each image use "Seite {pageNo} im Buch" (page number follows
      // the actual scene index, not fixed A/B/C/D letters).
      positionCover: 'Eure Figuren',
      // {pageNo} = the actual pageNo from the envelope
      // (1, 7, 13, 19 for the standard distributed selection).
      positionPage: 'Seite {pageNo} im Buch',
      // Sebastian ruling 2026-09-24 — on-image counter pill in a
      // corner of the big view, e.g. "1 von 5". Added after iPhone
      // testing showed customers who did not scroll below the fold
      // assumed there was only one image. The pill counts through
      // the actual slot count (anchor + previewTiles.length).
      // {current} = 1-based slot index of the active slot.
      // {total} = total number of slots (1 + previewTiles.length).
      positionCounter: '{current} von {total}',

      // Title-field block — composition A only, v3 §6 line 330.
      // Editable HERE and only here (D-30). save-on-blur, no loading
      // state; the overlay updates locally + immediately.
      titleFieldLabel: 'Titel',
      // v3 §6 line 330 sub-line.
      titleFieldHint: 'Der Titel steht auch auf dem Buchrücken.',
      // Sebastian ruling 2026-09-01 (PWA #52 fix, Commit 4b). Frozen
      // verbatim — do NOT reuse the ef12a09 wording. Shown beneath
      // the title input when the blur-save fails; replaces the
      // regular hint line while visible. Clears on the next blur-
      // save attempt (useTitleEdit calls setError(null) on entry).
      titleSaveError: 'Der Titel konnte nicht gespeichert werden. Bitte versuche es noch einmal.',

      // Stille Zeile below the tile row — composition A, v3 §6 line
      // 331. Frozen customer copy per
      // `P2_Text_und_Prompt_Entscheidungen.md` line 250, verbatim
      // (with the mockup's "auf euch" corrected to "auf dich" per
      // J-20 / v3 G11). Not templated: the 16 = book length (20 per
      // D-27) minus preview pages (4 per D-23), and an E-18 dropout
      // does NOT change the book length. Book length is a product
      // constant, not an envelope-derived value.
      restseiten: 'Noch 16 Seiten warten auf dich.',

      // Kontroll-Versprechen box — composition A, v3 §6 line 332.
      // Verbatim from P2_Text_und_Prompt_Entscheidungen.md lines
      // 256-258 (Sebastian authorship, S-8 in v3 §9). Rendered as a
      // Hinweiskasten in Navy (v3 §5 Bausteine "Hinweiskasten").
      // Block 5 · 5.7 (Sebastian ruling 2026-09-09) — white block
      // body REPLACED. Heading drops its trailing period per the
      // spec formatting; body switches "30 bis 60 Minuten" → "etwa
      // fünf Minuten" (measured production times, no more mail
      // language) and "lässt du es ändern" → "kannst du es ändern"
      // (customer subject).
      kontrollversprechenHeading: 'Du siehst das ganze Buch, bevor es gedruckt wird',
      kontrollversprechenBody:
        'Nach der Bestellung entstehen alle Seiten. Nach etwa fünf Minuten siehst du dein vollständiges Buch, jede einzelne Seite so, wie sie gedruckt wird. Passt etwas nicht, kannst du es ändern. Gedruckt wird erst, wenn du das Buch freigibst.',

      // Block 5 · 5.8 (Sebastian ruling 2026-09-09) — the "Zum
      // Nachlesen" section sits BELOW the first order button.
      // Heading + two Klappzeile rows. Row 1 opens the summary
      // (single body render); row 2 opens ALL twenty scene texts
      // together (one click, not per-scene expanders). A second
      // gold order button appears BELOW the rows only when one of
      // the two rows is open.
      zumNachlesenHeading: 'Zum Nachlesen',
      klappGeschichteLabel: 'Die Geschichte',
      klappAllePagesLabel: 'Alle Seiten im Text',

      // Sebastian ruling 2026-09-01 (revised copy layout).
      // Above the tile row — stand-alone line. Frozen verbatim.
      figureLineAboveTiles: 'So sehen eure Figuren aus.',
      // Heading above the "fertige Seite" example image. Frozen
      // verbatim. Sebastian ruling 2026-09-24 — heading updated to
      // "Beispiel aus einem anderen Buch: So sieht eine fertige Seite
      // aus". iPhone testing surfaced that everyone read the image as
      // a page from THEIR OWN book; the ribbon (below) plus the
      // preface makes the "not-yours" framing unmissable.
      exampleImageHeading: 'Beispiel aus einem anderen Buch: So sieht eine fertige Seite aus',
      // Sebastian ruling 2026-09-24 — diagonal ribbon painted across
      // one corner of the example image, mirroring the heading's
      // "not-yours" framing. Two independent affordances so a
      // scanning customer can't miss it.
      exampleRibbon: 'Beispiel',
      // Sebastian ruling 2026-09-01 (revised copy layout) — sits
      // BETWEEN the example heading and the example image itself.
      // Explains what the finished page carries (text in a light
      // panel). Frozen verbatim.
      examplePanelExplainer:
        'Im fertigen Buch steht der Text auf jeder Seite in einem hellen Feld. Du kannst es verschieben und die Größe ändern.',
      // Sebastian ruling 2026-09-01 — alt text for the example image.
      // Image is `pwa/public/Eiffelturm.jpg` (Sebastian ruling
      // 2026-09-17 — a real finished-book page with the text panel,
      // replaces the earlier `preview-example.png` placeholder).
      exampleImageAlt: 'Beispiel einer fertigen Buchseite mit Text in einem hellen Feld',

      // Footer stille Zeile — composition B (page slot). Sebastian
      // authorship 2026-08-13, verbatim. Replaces the pre-2026-08-13
      // pair `a71Line` + `kostprobe`. Folds the taste-of-layout, four-
      // preview-pages, and post-payment-edit meanings into one line
      // and settles the stale "Die endgültige Druckseite wird noch
      // gesetzt" claim (post-2026-08-13 rulings: no redraws before
      // payment; failed_final scenes deliver their best attempt).
      previewFooter:
        'Das ist ein Vorgeschmack auf das Layout deines Buchs und vier einzelne Seiten. Nach der Bestellung kannst du jede Seite noch anpassen.',

      // 6-3 Bildschirm-5 landing (Sebastian ruling 2026-08-15). The
      // gold button on PreviewScreen navigates to /book/:orderId/bestellung.
      // No caption line beneath it — the button now goes somewhere.
      // No back-link — Schrittleiste already navigates, and a second
      // navigation element that goes somewhere different is a way to
      // confuse people, not help them (Sebastian ruling 2026-08-15).
      // Retired keys: `orderCta`, `orderCtaDisabledNote`, `backLink`.
      // Tracker closed: [book-preview-bestellung-disabled-note].
      previewOrderCta: 'Buch bestellen',

      // Per-page retry — composition B, v3 §6 line 343 + line 350.
      // `renderState === 'retry_available'` shows this block. Rot als
      // Tinte per v3 §3 line 151 ("Rot folgt dem Knopf"): tile border,
      // heading text, and outline-button carry `text-error` /
      // `border-error`. Verbatim from
      // P2_Text_und_Prompt_Entscheidungen.md lines 261-262.
      // {seite} = the inner page number that failed (1..4).
      retryHeading: 'Seite {seite} ist nicht fertig geworden.',
      retryCta: 'Nochmal versuchen',

      // Wartebildschirm — `preview_rendering`, v3 §6 line 352.
      // Verbatim from P2_Text_und_Prompt_Entscheidungen.md lines
      // 232-235. Position counts inner pages only (never includes
      // the title image, which existed since step 2 per v3 §6 line
      // 352). Total is dynamic on the same E-18 signal as
      // positionCover / positionPage: a dropped page shrinks it.
      waitHeading: 'Wir malen die ersten Seiten.',
      // {n} = current inner page being rendered, {total} = count of
      // inner pages the envelope carries.
      waitPosition: 'Seite {n} von {total}',
      waitTeaserLead: 'Einen Moment noch. Wusstest du das schon?',

      // A11y + tile labels. `thumbCoverLabel` is used as both the
      // a11y label and the visible cover-tile label. `thumbPageLabel`
      // interpolates szeneLetter (positional A/B/C/D) and pageNo
      // (actual page number from the envelope, distributed selection).
      bigViewAriaLabel: 'Große Ansicht',
      thumbRowAriaLabel: 'Kachelreihe',
      // Block 5 · 5.5 (Sebastian ruling 2026-09-09) — page-tile labels
      // switched from "Szene {szeneLetter} · im Buch Seite {pageNo}"
      // to "Seite {pageNo} im Buch".
      //
      // Sebastian ruling 2026-09-17 (supersedes 2026-09-12) —
      // thumbCoverLabel is "Eure Figuren". Every surface that
      // currently consumes this string renders the BARE anchor image
      // (figures only, no cartouche): the pre-payment preview screen
      // (big-view a11y alt + tile-row thumbnail label) AND the post-
      // payment wait screen (figcaption). The 2026-09-12 split
      // between positionCover ("Eure Figuren", bare) and
      // thumbCoverLabel ("Euer Cover", intended for anchor+cartouche)
      // anticipated a with-cartouche render (I5) that does not exist
      // in the customer surface yet, so the split labelled the same
      // bare image with two different words. Re-unified here. When
      // the cartouche-composited cover render does land, introduce a
      // new key for that specific surface — do NOT rename this one
      // back.
      thumbCoverLabel: 'Eure Figuren',
      // {pageNo} = actual pageNo from the envelope.
      thumbPageLabel: 'Seite {pageNo} im Buch',
      // Sebastian ruling 2026-08-12 — placeholder tile copy. Verbatim.
      // Rendering: spinner + this text ("motion belongs on the
      // rendering state"). Failed: static + this text ("nothing is
      // happening, a spinner would lie").
      tileRendering: 'Vorschaubild wird gerade erstellt und ist gleich sichtbar.',
      // Sebastian ruling 2026-09-07 evening — pre-payment preview
      // tile failure. Replacement for the earlier "Diese Seite
      // konnten wir nicht zeichnen." wording. Post-payment ReviewScreen
      // needs its OWN key with the support-address wording (that's
      // failure point #11 in the 2026-09-07 error inventory) — not
      // built in this Type A slice because ReviewScreen has no
      // 'failed' CustomerRenderState today; NEW key when the shape
      // lands.
      tileFailed: 'Ein Bild ist nicht fertig geworden. Versuch es nochmal.',
      // Sebastian ruling 2026-08-26 — verbatim. Fires when the tile
      // state is 'blocked' (backend: preview_rendering + scene has no
      // assembled_prompt). Static, no spinner, no CTA. The promise in
      // this copy ("Melde dich bei uns...") depends on the backend
      // PREVIEW_BLOCKED_TILE_SERVED log line reaching someone.
      tileBlocked:
        'Diese Seite konnten wir nicht zeichnen. Melde dich bei uns, dann kümmern wir uns darum.',
      navPrev: 'Vorherige Ansicht',
      navNext: 'Nächste Ansicht',
    },

    // 6-3 Bildschirm 5 · Bestellung (Sebastian ruling 2026-08-15).
    // Two-question flow when both dimensions offer >1 choice. Each
    // question auto-hides when only one option is available (Bau-Spez
    // J-11 "eine Frage nach der anderen" honestly observes that a
    // question with one answer is not a question).
    // Sebastian ruling 2026-09-08 late-evening: three-binding launch
    // (Hardcover 4900 + Layflat 5900 + Pappbuch 6900-available-false)
    // added tile-description copy on each BuchartCard and the Q2 auto-
    // hide + auto-pick when only one format is orderable for the
    // chosen binding (today: exactly one 21×21 unit per binding).
    // Retired keys from the preview block: `orderCta`,
    // `orderCtaDisabledNote`, `backLink` (see previewOrderCta note
    // above for the ruling). Closes trackers
    // `[book-preview-bestellung-disabled-note]` and
    // `[pwa-status-to-step-add-checkout-pending]`.
    bestellung: {
      // Block 6 · 6.1 · Eyebrow + heading for the tile-selection
      // screen (Sebastian ruling 2026-09-09). Rendered above the h1
      // via the Eyebrow primitive.
      eyebrow: 'EURE BESTELLUNG',
      heading: 'Wie soll euer Buch aussehen?',
      // Legacy chrome (pre-Block-6). Kept in the map so tests that
      // referenced the earlier flow don't crash while the flow is
      // being reworked; consumer moved to `heading` above.
      title: 'Bestellung',
      // Block 6 · 6.4 · FAQ heading above the accordion. Sebastian
      // ruling 2026-09-09.
      faqHeading: 'Häufige Fragen',
      // Sebastian ruling 2026-09-07 evening — banner rendered above
      // the Buchart/Format questions WHEN envelope.status is
      // 'checkout_pending' (the customer returned from Stripe's hosted
      // page without completing). Renders inline in BestellungScreen
      // above the Buchart heading, navy text on a soft-beige panel
      // per Bau-Spez §5 "Bildschirm-Hinweis" (not an error surface —
      // this is informational, the customer's data is intact and the
      // flow resumes cleanly on the next "Jetzt bestellen" click,
      // which the createBookCheckout duplicate-guard resolves
      // idempotently).
      returnFromCheckoutBanner:
        'Deine Bestellung ist noch nicht abgeschlossen. Du kannst sie jetzt fertig machen.',
      // Sebastian ruling 2026-09-09 — verbatim banner copy for the two
      // Stripe rejected/expired-checkout outcomes. Rendered at status=
      // preview_ready when envelope.lastCheckoutOutcome !== null. The
      // declined copy carries a second sentence on its own line
      // ("Versuch es mit einer anderen Karte."); the `\n` between the
      // sentences is preserved as authored and rendered with
      // `whitespace-pre-line` so the visual break survives.
      //
      // NEVER unify the two — Sebastian released them as distinct
      // copies. Declined ("Zahlung ist nicht durchgegangen") speaks to
      // a payment-method failure the customer can act on. Expired
      // ("Bestellung nicht abgeschlossen") speaks to a session that
      // timed out, no directive needed — the standard "Jetzt
      // bestellen" flow is the recovery path.
      checkoutDeclinedBanner:
        'Deine Zahlung ist nicht durchgegangen. Dein Buch liegt weiter für dich bereit.\nVersuch es mit einer anderen Karte.',
      checkoutExpiredBanner:
        'Du hast die Bestellung nicht abgeschlossen. Dein Buch liegt weiter für dich bereit.',
      // Sebastian ruling 2026-09-07 evening — screen-local fallback
      // when the "Jetzt bestellen" click fails and the error has no
      // envelope (network failure, malformed response, pre-response
      // abort). When the envelope DOES carry user_message, that
      // wording is rendered verbatim per Bau-Spez §8 T6 — this
      // string is the null-envelope catch, not a replacement for
      // catalog wordings.
      checkoutOpenFailedFallback: 'Wir konnten die Bezahlung nicht öffnen. Versuch es nochmal.',
      // Block 6 · 6.2 · Three fixed tiles for the launch (Sebastian
      // ruling 2026-09-09). Retires the Q1 Buchart / Q2 Format
      // two-question flow; the customer picks one of three tiles
      // directly. Order in the UI: Hardcover / Layflat / Pappbuch.
      // Pappbuch renders greyed + not clickable — "Ab Herbst
      // verfügbar" spelt out on the tile itself.
      //
      // Prices below are the SPEC-VISIBLE prices per Block 6.2
      // (49 / 59 / 69 €). The backend `orderableSalesUnits` is the
      // source of truth for the actual price shown on the review
      // screen; if the backend catalog differs, the review price
      // wins and the tile shows the spec price for a beat before
      // the customer moves on.
      buchartLayflat: 'Layflat',
      buchartPappbuch: 'Pappbuch',
      buchartHardcover: 'Hardcover',
      tileHardcoverTitle: 'Hardcover',
      tileHardcoverPrice: '49 €',
      tileHardcoverDescription: 'Klassisch gebunden, fester Einband.',
      tileLayflatTitle: 'Premium Layflat',
      tileLayflatPrice: '59 €',
      tileLayflatDescription: 'Liegt flach auf, wenn ihr zusammen lest. Kein Biegen der Seiten.',
      tilePappbuchTitle: 'Pappbuch',
      tilePappbuchPrice: '69 €',
      tilePappbuchDescription:
        'Fester Einband und dicke, feste Seiten. Hält viel aus und macht jeden Tag mit.',
      // Sebastian ruling 2026-09-25 (Webflow shipping-price update) —
      // small-type line under each format-card price, approved
      // verbatim. `{shipping}` is substituted at render time with the
      // formatted euro string from `envelope.shippingCents` so a
      // future config change cannot leave this hint stale. Rendered
      // today as "inkl. MwSt., zzgl. 7,50 € Versand".
      tileShippingHint: 'inkl. MwSt., zzgl. {shipping} Versand',
      // Sebastian ruling 2026-10-07 — label shown on the right side of
      // a Buchart tile when the tile is disabled (its productClass has
      // no entry in orderableSalesUnits). Replaces the price string on
      // disabled tiles; a tile that cannot be bought shows no price.
      // Generic rule — triggers for ANY productClass that disappears
      // from orderableSalesUnits, not hardcover-specific.
      tileComingSoon: 'Bald verfügbar',
      // Legacy Q1/Q2 flow strings — kept in place because tests
      // still reference them and they are the STRINGS keys the
      // BuchartCard / FormatCard / AnsweredCollapse primitives read.
      // Not consumed by the new 3-tile flow (BuchartTiles).
      buchartHeading: 'Welche Buchart?',
      buchartHardcoverDescription:
        'Fester Einband mit klassischer Bindung. Das robustere Buch für Kinder, die selbst blättern.',
      buchartLayflatDescription:
        'Die Seiten liegen beim Aufschlagen flach, das Bild läuft ohne Knick über die ganze Doppelseite.',
      buchartPappbuchDescription: 'Dicke Seiten aus Karton. Hält viel aus und macht jeden Tag mit.',
      // Answered-collapse "Layflat · Ändern" line.
      aendernLabel: 'Ändern',
      // Format question.
      formatHeading: 'Welches Format?',
      // Format card body: renders `{format}` (e.g. "21×21") and price.
      // The price display uses German number format (comma, €-suffix)
      // built in BestellungScreen from priceCents; no template here.
      // Price display + total.
      versandLabel: 'Versand',
      gesamtLabel: 'Gesamt',
      // Block 6 · 6.3 · Gold Weiter button — reads "Weiter zur
      // Bestellübersicht" (Sebastian ruling 2026-09-09). Names the
      // destination (Bestellübersicht = the review + §312g
      // acknowledgment screen), not just "Weiter". Customer flow:
      // pick a tile → click Weiter → review screen with the §312g
      // acknowledgment checkbox → Kaufen → Stripe redirect.
      weiter: 'Weiter zur Bestellübersicht',
      // Legacy "Jetzt bestellen" button label.
      //
      // 🚫 DO NOT use this string on an order button that triggers a
      // payment obligation. § 312j Abs. 3 BGB requires the button label
      // on the final order step to name the payment obligation
      // ("zahlungspflichtig bestellen" or a semantically equivalent
      // wording). "Jetzt bestellen" alone does NOT satisfy that
      // requirement. Sebastian ruling 2026-09-09 (Bestellübersicht
      // slice): kept in place because pre-existing FAQ + email copy
      // references the phrase, but any button that fires a payment
      // must use the Bestellübersicht flow (Weiter → review screen →
      // Kaufen on `book.bestellung.review.cta`). If a future flow
      // needs a shorter payment-obligation label, add a NEW key with
      // wording that names the obligation ("Kostenpflichtig bestellen"
      // etc.) — do not re-purpose this one.
      jetztBestellenCta: 'Jetzt bestellen',
      // Redirect notice — customer clicks Kaufen on the review screen,
      // PWA POSTs /checkout, sets window.location. Short line for the
      // in-between moment before the redirect resolves.
      redirectingNote: 'Wir leiten dich zur Zahlung weiter.',
      // Bestellübersicht review screen (Sebastian ruling 2026-09-09).
      // Verbatim frozen wording — DO NOT rewrite, shorten, or tone-
      // adjust per Root-CLAUDE.md Verbatim Content Rule.
      //
      // Flow: format-select screen shows "Weiter" (above), click →
      // this review screen renders inline. Layout order: back, heading,
      // summary card (labelBook → title, labelFormat → composed
      // "Hardcover, 21 × 21 cm", labelPrice, labelShipping, labelTotal
      // with vatNote directly beneath), exclusionHeading, exclusionBody,
      // acknowledgmentCheckbox, cta ("Kaufen").
      //
      // acknowledgmentCheckbox is the §312g Abs. 2 Nr. 1 BGB
      // personalization-acknowledgment — the STRINGS key is what the
      // backend persists onto BookOrder.personalizationAcknowledgmentWordingKey
      // (see BOOK_PERSONALIZATION_ACKNOWLEDGMENT_WORDING_KEY in the
      // book-checkout service). A wording change here MUST land
      // alongside a wording-versioning decision (tracker
      // [withdrawal-consent-wording-not-versioned]).
      //
      // cta ("Kaufen") satisfies § 312j Abs. 3 BGB — the button on
      // the final order step must name the payment obligation.
      // "Kaufen" is the released choice; do NOT swap it back to
      // "Jetzt bestellen" (see the trap comment on jetztBestellenCta
      // above).
      review: {
        heading: 'Deine Bestellung',
        labelBook: 'Dein Buch',
        labelFormat: 'Format',
        labelPrice: 'Buch',
        labelShipping: 'Versand',
        labelTotal: 'Gesamt',
        // P14 pay-with-balance (Sebastian ruling 2026-09-24 block 5).
        // Balance row appears BELOW the total when balance > 0. Value
        // is rendered as "-20,00 €" (leading minus, same colour as
        // other amounts — no gold). "Zu zahlen" row is always shown
        // when balance is applied, including "0,00 €" on full cover.
        labelBalance: 'Guthaben',
        labelAmountToPay: 'Zu zahlen',
        vatNote: 'inkl. 7 % Umsatzsteuer',
        labelDelivery: 'Lieferung',
        deliveryText:
          'Wir drucken dein Buch, sobald du es freigegeben hast. Danach ist es in 4 bis 8 Werktagen (Montag bis Freitag) bei dir.',
        // Sebastian ruling 2026-11-02 — Austria launch. Only the "4
        // bis 8" window differs from the DE text; every other
        // character is identical. The Bestellübersicht picks the
        // delivery line by the chosen country.
        deliveryTextAt:
          'Wir drucken dein Buch, sobald du es freigegeben hast. Danach ist es in 4 bis 11 Werktagen (Montag bis Freitag) bei dir.',
        exclusionHeading: 'Dein Buch wird eigens für dich hergestellt',
        exclusionBody:
          'Nach der Bestellung entstehen alle Seiten. Vor dem Druck siehst du dein ganzes Buch und kannst die Texte noch anpassen. Weil wir jedes Bild nach deinen Angaben zeichnen und es kein zweites Exemplar gibt, kannst du dein Buch nach der Bestellung nicht widerrufen.',
        acknowledgmentCheckbox:
          'Ich habe verstanden, dass mein Buch persönlich für mich hergestellt wird und deshalb vom Widerrufsrecht ausgenommen ist (§ 312g Abs. 2 Nr. 1 BGB).',
        // Review-screen submit button. Two variants (Sebastian approval
        // 2026-10-20 follow-up):
        //   - Stripe path (no balance OR partial balance): "Weiter zur
        //     Zahlung" — the customer is leaving our site for Stripe,
        //     not placing a §312j order yet (that happens on the
        //     Stripe page). Approved verbatim.
        //   - Balance-only (balance covers 100 %, no Stripe hop):
        //     "Zahlungspflichtig bestellen" — §312j wording because
        //     the click IS the chargeable order (balance is a payment
        //     instrument). Approved verbatim.
        cta: 'Weiter zur Zahlung',
        ctaBalanceOnly: 'Zahlungspflichtig bestellen',
        back: 'Ändern',
        error:
          'Die Bestellung konnte gerade nicht gestartet werden. Bitte versuche es in einem Moment noch einmal.',
        // P14 pay-with-balance (Sebastian decision 2026-10-20). Dreamable-
        // Gutschein field rendered above the price summary. Redeems the
        // code into the user's balance via the dedicated review-screen
        // endpoint (same validation + rate-limit as /einloesen; same
        // user re-entry returns a soft info message instead of an
        // error).
        giftCode: {
          label: 'Dreamable-Gutschein',
          placeholder: 'DREAM-XXXX-XXXX',
          button: 'Einlösen',
          hint: 'Einen Rabattcode gibst du im nächsten Schritt bei der Zahlung ein.',
          alreadyOnAccount:
            'Dieser Gutschein ist schon auf deinem Konto. Das Guthaben ist in der Übersicht abgezogen.',
        },
        // P14 pay-with-balance — fully-covered path shipping form
        // (Sebastian ruling 2026-09-24 Q1 option a + label rulings).
        // Country is now a two-option toggle (DE + AT) per Sebastian
        // ruling 2026-11-02 — Austria launch; Deutschland preselected,
        // never a disabled select (ruling 2026-09-24 ruling 3 stands —
        // the toggle, not the plain text, is the right affordance now
        // that there are two options).
        shippingAddress: {
          heading: 'Lieferadresse',
          intro: 'Wohin sollen wir dein Buch schicken?',
          labelRecipient: 'Vor- und Nachname',
          placeholderRecipient: '',
          labelLine1: 'Straße und Hausnummer',
          placeholderLine1: '',
          labelLine2: 'Adresszusatz (optional)',
          placeholderLine2: 'z. B. Etage, c/o …',
          labelPostalCode: 'PLZ',
          placeholderPostal: '',
          labelCity: 'Ort',
          placeholderCity: '',
          labelCountry: 'Land',
          countryDeutschland: 'Deutschland',
          countryOesterreich: 'Österreich',
          countryNote: 'Wir liefern nach Deutschland und Österreich.',
          errorRecipientRequired: 'Bitte Vor- und Nachname eintragen.',
          errorLine1Required: 'Bitte Straße und Hausnummer eintragen.',
          errorPostalRequired: 'Bitte PLZ eintragen.',
          errorPostalFormat: 'Bitte eine PLZ mit fünf Ziffern eintragen.',
          errorPostalFormatAt: 'Bitte eine PLZ mit vier Ziffern eintragen.',
          errorCityRequired: 'Bitte Ort eintragen.',
        },
        errorPaidWithBalanceUnexpected: 'Etwas ist schiefgegangen. Bitte lade die Seite neu.',
      },
      // Success page after Stripe redirect (BestellungErfolgRoute).
      erfolgHeading: 'Bezahlung erhalten. Wir drucken dein Buch.',
      erfolgWaitingNote: 'Einen Moment, wir bestätigen die Zahlung.',
      erfolgExpiredHeading: 'Der Bezahlvorgang ist abgelaufen.',
      erfolgExpiredNote: 'Kein Problem — starte die Bestellung neu.',
      erfolgExpiredCta: 'Zur Bestellung',
      // Honest end state after the 60s poll timeout (Sebastian ruling
      // 2026-08-15). Payment did NOT fail — the customer landed here
      // from Stripe's success_url. What is slow is our webhook chain.
      // Copy must not suggest failure and must give a pointer.
      erfolgTimeoutHeading: 'Bezahlung erhalten. Wir bereiten dein Buch vor.',
      erfolgTimeoutBody:
        'Sobald dein Buch zur Freigabe bereit ist, bekommst du eine E-Mail. Deinen Fortschritt kannst du jederzeit hier ansehen.',
      erfolgTimeoutCta: 'Zum Buch',

      // FAQ — Sebastian authorship 2026-08-15. Eight entries,
      // adapted from DreamStories' order modal in Sebastian's voice.
      // Verbatim per his message; DO NOT rephrase per Root-CLAUDE.md
      // Verbatim Content Rule.
      //
      // `wie-funktioniert-das` contains the literal marker `[●]` where
      // the review-deadline number is still undecided (same gap as
      // the Nutzungsbedingungen). FaqAccordion treats any entry whose
      // `a` contains `[●]` as INCOMPLETE and does not render it —
      // otherwise the bracket would ship to the customer. Tracker:
      // `[book-faq-review-deadline-marker]`. When the number lands,
      // replace `[●]` with the digit + "Tage" and the entry appears.
      //
      // The empty-entry suppression (q.length > 0 && a.length > 0)
      // still applies to any future question added before its answer.
      // Block 6 · 6.4 · FAQ rework (Sebastian ruling 2026-09-09).
      // Removed four questions entirely (fuer-welches-alter,
      // wer-kann-vorkommen, welche-fotos, haustier) — all answered
      // by this point in the flow. The remaining four questions
      // KEEP their slugs + q strings; all four answers are
      // REPLACED verbatim per Sebastian's brief. The pre-Block-6
      // answers were factually wrong (28 pages, three formats,
      // 44/54/64 €, "all layflat" — none of which is true post
      // launch-catalog decisions).
      //
      // wie-funktioniert-das → NEW q "Wo ist der Unterschied
      // zwischen Hardcover, Layflat und Pappbuch?" (Sebastian's
      // spec order). Slug retained so the DOM id / anchor id
      // stays stable across releases; the label is what the
      // customer sees.
      faq: {
        'wie-gross-ist-das-buch': {
          q: 'Wie groß ist das Buch?',
          // Sebastian ruling 2026-09-25 (Webflow shipping-price
          // update) — the `{shipping}` placeholder is substituted at
          // render time in FaqAccordion with the formatted euro
          // string from `envelope.shippingCents`. Same discipline as
          // `tileShippingHint` above — a future shipping raise
          // updates the config value and the FAQ line follows,
          // rather than leaving the FAQ behind.
          a: 'Vierundzwanzig Seiten im Format 21 × 21 cm, davon zwanzig Bildseiten. Du wählst zwischen Hardcover für 49 € und Premium Layflat für 59 €, jeweils zuzüglich {shipping} Versand.',
        },
        'wie-funktioniert-das': {
          q: 'Wo ist der Unterschied zwischen Hardcover, Layflat und Pappbuch?',
          a: 'Beim Hardcover sind die Seiten klassisch gebunden, das Buch schließt sich leicht von selbst. Beim Layflat liegt jede Doppelseite flach auf, auch in der Mitte, ohne dass ihr das Buch aufdrücken müsst. Das Pappbuch hat dicke, feste Seiten, die auch kleine Hände nicht knicken. Layflat ist die Wahl, wenn ihr das Buch zusammen auf dem Schoß anschaut.',
        },
        lieferzeit: {
          q: 'Wie lange dauert es, bis das Buch da ist?',
          a: 'Wir drucken, sobald du dein Buch freigegeben hast. Danach ist es in Deutschland in 4 bis 8 und in Österreich in 4 bis 11 Werktagen (Montag bis Freitag) bei dir.',
        },
        'nicht-zufrieden': {
          q: 'Was, wenn mir das Buch nicht gefällt?',
          a: 'Schreib uns. Weil du vor dem Druck jede Seite siehst und freigibst, lässt sich das meiste vorher klären. Sollte danach etwas nicht stimmen, finden wir eine Lösung, und deine gesetzlichen Gewährleistungsrechte bleiben davon unberührt.',
        },
      },
    },

    // Wait strings for Schritte 1 and 2 (light_gate_pending,
    // anchor_pending). Preview_rendering has its own subset under
    // `preview.wait*` because that wait carries a page-count position
    // line and this one doesn't.
    //
    // Anchor heading + teaser lead verbatim from
    // `docs/geschichtenbuch/P2_Text_und_Prompt_Entscheidungen_2026-08-05.md`
    // lines 214-216 (§Schritt 2 · Wartebildschirm).
    //
    // Beruhigungszeile verbatim from same doc line 186 (§Schritt 1
    // · Beruhigungszeile). Per v3.1 §6 line 274 it renders "nur hier
    // und in den Wartemomenten, nicht auf Entscheidungsbildschirmen".
    //
    // For `light_gate_pending` there is no authored text; per
    // Sebastian ruling 2026-08-05 the anchor heading is used until a
    // ruling on visible distinction — tracked as
    // `[book-wait-light-gate-heading]`.
    wait: {
      // Block 4 · 4.1 · Anchor-render wait screen (Sebastian ruling
      // 2026-09-09). Replaces "Wir zeichnen dein Titelbild." which
      // was already out of sync with the Figuren step rename at
      // 6b.4 on 2026-09-02. Consumer at BookRoute anchor_pending
      // passes this alongside `body: anchorBody`, `showSpinner=true`,
      // and drops teaserLead. Only fires on initial render
      // (remainingAnchor === 2); regenHeading below still runs for
      // remainingAnchor < 2.
      anchorHeading: 'Wir zeichnen eure Figuren',
      anchorBody:
        'Damit euer Buch auf jeder Seite gleich aussieht, zeichnen wir zuerst eure Figuren. Das dauert ein bis drei Minuten.',
      teaserLead: 'Einen Moment noch. Wusstest du das schon?',
      beruhigungszeile: 'Alles ist gespeichert. Du kannst jederzeit später weitermachen.',
      // State 5 heading per v3.1 §6 line 291 — a running regen
      // (attemptNo >= 2 pending). Distinct from the initial-render
      // heading. Derivable from status=anchor_pending AND
      // remainingAnchor < 2 (Sebastian ruling 2026-08-05 §2).
      regenHeading: 'Wir zeichnen neu.',
      // Wait heading for status=story_generating AND anchor_approved
      // (Sebastian ruling 2026-09-10 late evening). Both dispatch to
      // the same WaitScreen because anchor_approved is the transient
      // state between anchor.accept and the story processor picking
      // the job off Redis — customer's mental model is "story is
      // coming" in both. Duration figure deliberately omitted per
      // Sebastian: the real wait depends on how long the customer
      // looked at the anchor image (someone seeing his child drawn
      // for the first time looks long enough that the scenes are
      // nearly done; someone who clicks straight through waits ~40s).
      // A fixed number would be wrong half the time.
      storyHeading: 'Wir schreiben eure Geschichte',
      // Body copy matches the wording already released for the post-
      // Personen wait screen (STRINGS.book.summaryReview.generatingBody).
      // Kept as a separate key so per-context copy can drift later.
      storyBody: 'Das dauert einen kurzen Moment.',
      // Sebastian ruling 2026-09-08 — slow-copy line, wired only on
      // the story-generation waits (story_generating + anchor_approved)
      // with a 240000 ms threshold measured against production
      // durations (anchor image 1:25-2:30, story 0:45-1:58; 4 min
      // reads as "definitely longer than usual"). WaitScreen reveals
      // this line after the timer fires; it is undefined and silent
      // on anchor_pending / light_gate_pending.
      storySlowCopy:
        'Das dauert gerade länger als sonst. Wir arbeiten weiter — du kannst die Seite offen lassen oder später wiederkommen.',
    },
    // Block 2 commit 2a (Sebastian ruling 2026-08-31 evening) — the
    // eleven post-render + post-checkout book states that previously
    // fell to BookRoute's default fallback (WaitScreen with "Wir
    // zeichnen dein Titelbild.") now render BookStatusInfoScreen with
    // the two Sebastian-authored strings below. All read-only —
    // heading + subtitle, no CTA. Keys are named after the primary
    // status of each screen; screens shared across multiple states
    // list every one they serve in the per-key JSDoc.
    status: {
      // print_approved, prepress, handoff_pod — same screen for all
      // three. Post-release, pre-shipped-notification phase.
      printApproved: {
        heading: 'Viel Freude mit Deinem Buch!',
        subtitle: 'Wir senden Dir eine E-Mail, sobald es versendet ist und auf dem Weg zu dir ist.',
      },
      shipped: {
        heading: 'Dein Buch ist unterwegs.',
        subtitle: 'Die Sendungsverfolgung steht in der E-Mail, die wir dir geschickt haben.',
      },
      delivered: {
        heading: 'Dein Buch ist angekommen.',
        subtitle: 'Wir hoffen, es gefällt euch. Schreib uns gern, wie es ankommt.',
      },
      // qa_regen, qa_human_queue — same screen for both.
      qaRegen: {
        heading: 'Wir schauen dein Buch noch einmal durch.',
        subtitle:
          'Eine Seite gefällt uns noch nicht. Wir zeichnen sie neu und melden uns, sobald das Buch fertig ist.',
      },
      cancelled: {
        heading: 'Diese Bestellung wurde storniert.',
        subtitle: 'Wenn das nicht stimmt, schreib uns kurz.',
      },
      // paused_billing, reclamation_open — same screen for both.
      pausedBilling: {
        heading: 'Wir kümmern uns um deine Bestellung.',
        subtitle: 'Du hörst von uns per E-Mail.',
      },
      // Sebastian ruling 2026-09-08 evening — release-chain
      // terminal-fail screen (Commit 2 of A+B+C). Reached when
      // BookReleaseChainService.run exhausts its BullMQ attempts
      // (typically an Anthropic 529 that outlasted Commit 1's
      // extended retry window, or a persistent SDK error). Copy
      // released verbatim by Sebastian; heading + three body
      // paragraphs. Rendered via the array-subtitle branch of
      // BookStatusInfoScreen.
      releaseChainError: {
        heading: 'Da ist etwas schiefgelaufen.',
        paragraphs: [
          'Bei der Herstellung deines Buches gab es ein Problem. Deine Bestellung und alles, was du eingegeben hast, sind gespeichert — es geht nichts verloren.',
          'Wir haben eine Nachricht bekommen und kümmern uns darum. Sobald dein Buch weitergeht, melden wir uns per E-Mail bei dir. Das dauert in der Regel weniger als einen Tag.',
          'Du musst nichts tun. Fragen? support@dreamable.kids',
        ],
      },
      // Block 2 commit 2b Part 2 (Sebastian ruling 2026-08-31 evening)
      // — the BookRoute default-branch line. Fires only when a future
      // BookOrderStatus enum value ships without a matching switch
      // case (after c2a covers all 25 current values). Chosen for
      // being true in every past-render AND pre-render state; replaces
      // the misleading "Wir zeichnen dein Titelbild." fallback that
      // was true only on pre-render states.
      unknown: {
        heading: 'Wir arbeiten an deinem Buch.',
        subtitle: 'Sobald es weitergeht, hörst du von uns.',
      },
      // Sebastian ruling 2026-09-10 (Paket 3) — image-refusal copy.
      // Fires when the backend flips the order to `image_refused`
      // after OpenAI's image endpoint returned a 2xx-with-error
      // content refusal. Three stage-specific string pairs (heading +
      // body) selected by `envelope.intake.derived.imageRefusal.stage`;
      // shared support line under all three. Copy released verbatim by
      // Sebastian 2026-09-10 — do NOT rephrase. Only the book_anchor
      // stage fires today; cover + scene are forward-compat for when
      // the release-time moderation Sammellauf lands.
      imageRefused: {
        book_anchor: {
          heading: 'Das Bild konnte nicht gezeichnet werden',
          body: 'Beim Zeichnen ist etwas schiefgegangen. Das kann an einem der Fotos liegen. Probier ein anderes Foto.',
          // Sebastian correction slice 2026-09-10 (Part 2.1) — technical-
          // branch copy. RELEASED verbatim; do NOT rephrase. Fires when
          // `envelope.intake.derived.imageRefusal.cause === 'technical'`
          // — a transport / provider failure that BullMQ retries could
          // not recover. The retryButton triggers POST /anchor-retry.
          technical: {
            heading: 'Das hat gerade nicht geklappt',
            body: 'Bei uns ist etwas schiefgegangen. Das lag nicht an dir. Probier es noch einmal.',
            // Sebastian ruling 2026-09-24 follow-up — customer-triggered
            // action: rename from "Nochmal zeichnen" to "Nochmal
            // versuchen" so the button reads as retry, not draw-task.
            retryButtonLabel: 'Nochmal versuchen',
          },
        },
        cover: {
          heading: 'Das Titelbild konnte nicht gezeichnet werden',
          body: 'Beim Zeichnen ist etwas schiefgegangen. Das kann an deinem Titel liegen. Probier einen anderen.',
        },
        scene: {
          heading: 'Die Seite konnte nicht neu gezeichnet werden',
          body: 'Beim Zeichnen ist etwas schiefgegangen. Das kann an dem liegen, was du geschrieben hast. Probier eine andere Beschreibung.',
        },
        supportLine: 'Wenn es weiter nicht klappt, schreib uns: support@dreamable.kids',
      },
    },
    // Paket 3 Item 1 (Sebastian ruling 2026-09-10) — image_refused
    // book_anchor recovery screen strings. Rooted at
    // `book.imageRefused` (peer of `book.photoRejected`). Reads the
    // shared header copy from `book.status.imageRefused[stage]`
    // (kept there because `BookRoute`'s pre-item-1 fallback rendered
    // BookStatusInfoScreen and the strings must not move — the
    // released copy stays at its original location).
    //
    // TWO PLACEHOLDER STRINGS below (`swapSectionHeading` +
    // `swapButtonLabel`). Marked UNRELEASED. Sebastian authors the
    // final wording after item 1 lands and is reviewed live.
    // The FotoUploadSlot aria labels + upload-in-progress copy +
    // retry error copy are REUSED VERBATIM from `book.photoRejected`
    // (see ImageRefusedScreen.tsx). No copy invention.
    imageRefused: {
      // UNRELEASED PLACEHOLDER — Sebastian authors final wording.
      swapSectionHeading: 'Welches Foto möchtest du austauschen?',
      // UNRELEASED PLACEHOLDER — Sebastian authors final wording.
      swapButtonLabel: 'Foto ändern',
    },
    // Sebastian ruling 2026-09-11 SLICE D — voluntary photo swap on the
    // anchor-review screen. Loading line rendered while the backend
    // re-derives the changed cast entry's block + fires a fresh anchor
    // render (status flips anchor_review → anchor_pending → anchor_review).
    // RELEASED verbatim — do NOT rephrase.
    //
    // Sebastian ruling 2026-09-24 (batch photo-swap slice, ruling 4)
    // — this wait-screen line stays UNCHANGED. Customer-triggered
    // ACTION copy switches from "zeichnen" to "erstellen"; the wait
    // screen "Wir zeichnen ..." remains as-is (the system describing
    // what IT is doing, not a task the customer performs).
    voluntaryPhotoSwap: {
      loadingLine: 'Wir zeichnen die Figur mit dem neuen Foto neu. Das dauert einen Moment.',
    },
    // Sebastian ruling 2026-09-24 (batch photo-swap slice) — verbatim
    // copy for the "Neue Figuren erstellen" flow. Customer stages
    // multiple photo swaps, then one button fires ONE batched
    // redraw. Naming rule (ruling 4): customer-triggered ACTIONS use
    // "erstellen" (not "zeichnen") because "zeichnen" reads as a
    // task for the customer. The wait screen "Wir zeichnen eure
    // Figuren" (see voluntaryPhotoSwap.loadingLine above) stays
    // unchanged — that's the system describing its work.
    // Sebastian ruling 2026-09-24 follow-up round 2 — the batch flow
    // lives entirely INSIDE the picker dialog. Closing the dialog
    // (Abbrechen / X / Escape / outside click) discards every staged
    // swap and returns the page to its normal shape. The only way to
    // apply staged photos is the gold "Jetzt neues Figurenbild
    // erstellen" button INSIDE the dialog. Page-level pending UI
    // (introLine + bottom button + approval gate replacing "Passt
    // so") REMOVED. Counter moved into the dialog, below the gold
    // button. The "Schließen" close-link variant was removed; the
    // dialog's close link is always "Abbrechen" now.
    batchPhotoSwap: {
      // Badge rendered on any cast entry that has at least one staged
      // photo swap (face and/or fullBody).
      pendingBadge: 'Neues Foto ausgewählt',
      // Discard affordance next to the pending badge — "Altes Foto
      // behalten". Same button per pending slot; the whichPhoto is
      // implicit from the row identity.
      discardButton: 'Altes Foto behalten',
      // Primary CTA INSIDE the picker dialog when at least one photo
      // is staged. Fires the redraw endpoint.
      redrawButton: 'Jetzt neues Figurenbild erstellen',
      // Counter line rendered BELOW the redraw button, inside the
      // dialog. Reads voluntaryPhotoSwapsRemaining — same value the
      // anchor-regen path reads. `{n}` = remaining redraws.
      counterLine: (n: number) => `Noch ${n} Mal möglich`,
      // Error line rendered under the redraw button when the redraw
      // API call fails (rollback restored anchor_review + pending
      // list; the customer can retry inside the still-open dialog).
      redrawError:
        'Das Erstellen des neuen Figurenbilds hat nicht geklappt. Bitte versuche es gleich noch einmal.',
    },
    // Sebastian correction slice 2026-09-10 (Part 2.4) — shared photo-
    // swap dialog. Fires BEFORE the file picker opens on BOTH the
    // ImageRefusedScreen swap flow (Part 2.2) AND the AnchorReviewScreen
    // voluntary-swap flow (Part 2.3). Customer confirms by clicking
    // "Verstanden"; cancel closes without opening the picker.
    // RELEASED verbatim; do NOT rephrase.
    photoSwapDialog: {
      heading: 'So wird die Figur gut',
      bullets: [
        'Eine Person pro Bild.',
        'Gesicht frei und gut erkennbar.',
        'Keine Sonnenbrille, keine Kappe.',
        'Alltagsfotos sind besser als Studiofotos.',
      ],
      confirmButton: 'Verstanden',
    },
    // Sebastian ruling 2026-09-24 — read-only book viewer. Fires from
    // "Meine Bücher" on any post-approval status (print_approved,
    // prepress, handoff_pod, shipped, delivered). Reuses the release-
    // screen page renderer with the mutation controls hidden; the
    // strings below cover the wrapper header, the status line above
    // the pages, and two asset-availability fallbacks.
    //
    // `status.inPrint` covers print_approved + prepress + handoff_pod
    // (three internal states, one customer-facing wording — the
    // customer does not care about the internal split).
    //
    // `assets.deletedAfterRetention` covers the retention cliff
    // documented in the Datenschutzerklärung §3.5: character images,
    // book pages, and the print file are deleted 24 months after
    // delivery so the customer can reorder up to that point. Today
    // the backend never deletes; this fallback is defensive so once
    // the retention job lands (`[book-assets-24m-deletion]`), the
    // view degrades cleanly instead of showing broken image icons.
    // `assets.previewExpired` covers the 1-hour signed-URL TTL
    // (`src/book/preview-read/preview-read.service.ts`) — a customer
    // who leaves the tab open for hours will hit 403 on the img tags
    // until PostOrderView's mount-refetch re-signs them.
    postOrder: {
      title: 'Dein Buch',
      subtitle:
        'So sieht dein fertiges Buch aus. Ansehen kannst du es jederzeit, ändern nicht mehr.',
      // Sebastian ruling 2026-09-24 — BookRoute cold-load spinner
      // copy. Wording is generic ("Dein Buch", not "Dein fertiges
      // Buch") because BookRoute doesn't know the status until the
      // envelope arrives, so a cold reload of a mid-flow book would
      // otherwise read wrong. Lives under `postOrder` because Meine
      // Bücher → finished book is the dominant cold-load scenario
      // post-launch (the reason this string exists), but the copy
      // is safe for every cold-load path.
      opening: 'Dein Buch wird geöffnet …',
      status: {
        inPrint: 'Im Druck',
        shipped: 'Versendet',
        delivered: 'Zugestellt',
      },
      assets: {
        previewExpired: 'Die Vorschau ist gerade nicht verfügbar. Bitte lade die Seite neu.',
        deletedAfterRetention:
          'Die Seiten dieses Buchs wurden 24 Monate nach der Lieferung gelöscht, wie in der Datenschutzerklärung beschrieben.',
      },
    },
    // 8a post-payment waiting (Sebastian ruling 2026-08-17). One
    // screen for paid / rendering_full / qa_running. Three-dot stage
    // indicator, no countdown. Duration line names the honest
    // 30–60 min window promised in the pre-payment preview footer.
    // Beruhigungszeile promises a mail — the BookReviewReadyEmail-
    // Listener fires on the customer_review transition and MUST be
    // live before this screen ships.
    // Block 6 · 6.7 (Sebastian ruling 2026-09-09) — post-payment
    // wait screen fully reworked. Same layout on phone + desktop,
    // everything stacked vertically. Heading + two body paragraphs
    // at the top, then the anchor image, then the four preview
    // tiles as a 2×2 grid with the same captions as Screen 4
    // ("Eure Figuren" / "Seite N im Buch"), then three plain lines.
    // NO progress bar (over ~5 min a bar is either dishonest or
    // motionless; the tiles carry the screen).
    //
    // Also fires from BestellungErfolgRoute so the customer sees ONE
    // continuous wait screen from Stripe-return through the paid
    // status flip — no flash-Vielen-Dank intermediate (Block 6.6).
    postPaymentWait: {
      heading: 'Vielen Dank für eure Bestellung',
      body1:
        'Gleich seht ihr euer Buch zum ersten Mal fertig, mit euren eigenen Helden auf jeder Seite.',
      body2:
        'In etwa fünf Minuten liegt es hier zur Freigabe bereit. Bei Andrang kann es länger dauern. Du kannst auf dieser Seite warten, sie öffnet sich von selbst, sobald das Buch fertig ist. Oder du schließt sie einfach. Wir schicken dir eine Mail.',
      // Block 6.7 review correction (Sebastian ruling 2026-09-09) —
      // three static lines RETIRED. The pre-correction lines
      // ("Wir schreiben / Wir zeichnen / Wir kontrollieren jede
      // Seite") were dead scaffolding and "Wir schreiben" was wrong
      // at this point in the flow anyway (the story was written long
      // before payment). Replaced with two lines that reflect the
      // actual post-payment backend stages, gated on
      // `envelope.status`:
      //   paid / rendering_full → Stage 1 (Zeichnen)  — bold + spinner
      //   qa_running            → Stage 2 (Kontrollieren) — bold + spinner
      // The non-active line renders thin (dimmed). No timer, no fake
      // progression — the switch fires when the backend flips
      // rendering_full → qa_running.
      stageRendering: 'Wir zeichnen dein Buch',
      stageQa: 'Wir kontrollieren dein Buch',
      // Retained (kept in place under retired keys for orphan-
      // tolerance while the pre-Block-6 references drop). Consumers
      // migrated to the shape above; these do not render post-Block-6.
      stages: {
        paid: 'Vorbereitung',
        rendering: 'Wir zeichnen dein Buch',
        qa: 'Wir kontrollieren jede Seite',
      },
      stageAriaLabel: 'Fortschritt',
    },
    // 8a customer review screen (Sebastian ruling 2026-08-17). 21
    // slots (cover + 20 pages) with horizontal chevron paging.
    // Release button appears only at the last slot per Sebastian's
    // placement ruling (no jump chip for returning customers).
    review: {
      slotAriaLabel: 'Buchvorschau',
      navPrev: 'Vorige Seite',
      navNext: 'Nächste Seite',
      // Freigabe 26-slot expansion (Sebastian ruling 2026-09-07):
      // the front cover is one of two "cover" slots (front + back)
      // and gets its own label distinct from the "Titelseite"
      // (interior title page, slot 1). Sebastian: `frontCoverLabel =
      // 'Titelbild'`, `titlePageLabel = 'Titelseite'`. Rename touches
      // every consumer of the pre-expansion `coverLabel`. The five
      // structure-slot labels below feed both slot-title rendering
      // and the ThumbStrip's aria labels; no verbatim page content is
      // stored here — Über Dreamable and Impressum bodies come from
      // `envelope.structurePages` per Sebastian's single-source-of-
      // truth rule.
      frontCoverLabel: 'Titelbild',
      titlePageLabel: 'Titelseite',
      aboutBookLabel: 'Über dieses Buch',
      aboutDreamableLabel: 'Über Dreamable',
      imprintLabel: 'Impressum',
      backCoverLabel: 'Rückseite',
      // Sebastian ruling 2026-09-14 — page 1 crew heading. Mirrors the
      // printer's `drawTitlePage` literal `'Die Crew'`. Sebastian
      // ruling 2026-09-16 — surfaces on the Freigabe screen's title-
      // page mirror.
      crewHeading: 'Die Crew',
      // Sebastian ruling 2026-09-14 — page 22 dedication headline.
      // Mirrors the printer's `drawDedicationBlankPage` literal
      // `'Diese Seite gehört euch.'`. Sebastian ruling 2026-09-16 —
      // surfaces on the Freigabe screen's dedication-blank mirror
      // (replaces the pre-2026-09-14 auto-composed "Dieses Buch ist
      // am ... entstanden" block that yesterday's b3fe75a deleted
      // from the printer).
      dedicationBlankHeadline: 'Diese Seite gehört euch.',
      pageLabel: (pageNo: number, totalPages: number) => `Seite ${pageNo} von ${totalPages}`,
      // Block 7 · 7.1 · Eyebrow + heading for the Freigabe screen
      // (Sebastian ruling 2026-09-09). Rendered above the big stage
      // via the Eyebrow primitive; the book title (`envelope.title`)
      // sits in bold below the heading as the section identifier.
      eyebrow: 'EUER FERTIGES BUCH',
      heading: 'Schau es dir an, bevor wir drucken',
      // Clause 3.3 change-scope block (Sebastian ruling 2026-09-13).
      // Verbatim, Sebastian-released. Placed directly below the screen
      // heading. Overrides the earlier "name a limit only once one has
      // been spent" rule for THIS BLOCK ONLY — clause 3.3 of the
      // Nutzungsbedingungen requires the scope of permitted changes to
      // be named at preview-provision time. The per-image remaining
      // hints (regenRemainingPageOne/Two below) stay unchanged.
      changeScopeIntro: 'Wenn etwas nicht stimmt, kannst du es ändern:',
      changeScopeItemText: 'Jeden Text frei bearbeiten, so oft du willst.',
      changeScopeItemPanel: 'Die Texttafel auf jeder Seite verschieben, wohin du willst.',
      changeScopeItemImage: 'Bis zu 10 Bilder neu zeichnen lassen, je Bild bis zu 3 Versuche.',
      // The "14 Tage" clause is legally accurate for every book WHOSE
      // CLOCK IS RUNNING (customerReviewReachedAt IS NOT NULL). For
      // pre-slice-2 books with a NULL timestamp — Option A backfill
      // per Sebastian ruling 2026-09-13 — the sweeper ignores them
      // entirely, so this sentence is technically untrue for those
      // books. That is harmless today because only Sebastian's own
      // test books are in that state; every post-slice-2 customer has
      // a real timestamp from their status flip. Do NOT read this as
      // a bug the next time you see a NULL-clock book render this
      // deadline sentence.
      changeScopeDeadline:
        'Du hast dafür 14 Tage Zeit. Danach drucken wir das Buch so, wie du es hier siehst, und schicken es dir zu.',
      // Urgency line — rendered ABOVE the change-scope block when
      // daysUntilAutoprint ∈ {1, 2, 3}. Hidden above 3 days, hidden
      // when the envelope carries undefined or null. Singular at
      // 1 day per Sebastian ruling 2026-09-13. Factory shape mirrors
      // the existing `pageLabel(pageNo, totalPages)` pattern above.
      // The STRING lands in slice 1 so the copy is complete in one
      // place; the countdown STAYS DARK until slice 2 populates
      // envelope.daysUntilAutoprint (Sebastian ruling 2026-09-13:
      // "the countdown is comfort").
      urgencyLine: (daysLeft: number): string =>
        daysLeft === 1
          ? 'Noch 1 Tag, dann geht dein Buch in den Druck.'
          : `Noch ${daysLeft} Tage, dann geht dein Buch in den Druck.`,
      // Block 7 · 7.3 · Release notice — sits DIRECTLY ABOVE the
      // release button (Sebastian ruling 2026-09-09). Names the
      // irreversibility of the release + the print-what-you-see
      // guarantee. Verbatim.
      releaseNotice:
        'Nach der Freigabe können wir nichts mehr ändern. Das Buch wird genau so gedruckt, wie du es hier siehst.',
      // Block 7 · 7.3 · Button label REPLACED (Sebastian ruling
      // 2026-09-09). Pre-Block-7 read "Zum Druck freigeben"; new
      // wording names both actions (freigeben + drucken) explicitly.
      releaseCta: 'Buch freigeben und drucken',
      releaseBusy: 'Wird freigegeben …',
      releasedHeading: 'Danke! Wir drucken dein Buch.',
      releasedBody: 'Du bekommst eine Mail, sobald es unterwegs zu dir ist.',
      // Error surfaces
      releaseErrorNotReady:
        'Der aktuelle Schritt für dieses Buch hat sich verändert. Wir laden ihn neu.',
      releaseErrorGeneric: 'Freigabe hat nicht geklappt. Bitte versuche es gleich noch einmal.',
      // Sebastian ruling 2026-09-08 (launch-slice B) — hint rendered
      // above the release button when it is disabled because a slot
      // still shows renderState !== 'ready'. Short + names why. The
      // full support-address copy lives on the tile itself
      // (`review.tileFailed`); this line is purely the disabled-
      // button explanation.
      releaseGateHint: 'Sobald alle Bilder fertig sind, kannst du freigeben.',
      // Sebastian ruling 2026-09-08 (launch-slice, Group 1 #11) —
      // post-payment tile-failed wording. StageView renders this as
      // a static overlay when `slot.renderState === 'retry_available'`
      // AND the caller supplied `retryLabel`. Distinct from the
      // pre-payment `book.preview.tileFailed` (which says "Versuch es
      // nochmal") — post-payment we handle the retry, the customer
      // does not; the support address anchors the "we're on it"
      // promise.
      tileFailed:
        'Ein Bild ist noch nicht fertig. Wir kümmern uns darum und melden uns bei dir. Fragen? support@dreamable.kids',

      // Sebastian ruling 2026-09-17 fix-5 — two-option chooser on
      // the Freigabe screen (was three-option pre-fix-5). Text edit
      // and panel move/resize collapsed into one combined editor
      // (option 1). Wording released verbatim; DO NOT alter.
      pageChangeCta: 'Diese Seite ändern',
      changeDialogHeading: 'Was möchtest Du an dieser Seite ändern?',
      // Option 1 — SPLIT per Sebastian ruling 2026-09-04: page slots
      // open the combined text + panel editor; the cover slot opens
      // the cover-regen two-field editor.
      changeOption1Page: 'Text und Texttafel',
      changeOption1Cover: 'Den Buchtitel oder das Bild',
      // Option 2 — page slots open the scene re-render (was option 3
      // pre-fix-5). Cover slot has no option 2.
      changeOption2: 'Das Bild neu erstellen',
      // Combined editor labels (Sebastian ruling 2026-09-17 fix-5,
      // items A + B). Verbatim.
      combinedEditorHeading: 'Text und Texttafel anpassen',
      combinedEditorHint:
        'Verschiebe die Tafel an die Stelle, an der sie Dich am wenigsten stört. Mit den Griffen links und rechts machst Du sie breiter oder schmaler. Die Höhe passt sich dem Text an.',
      combinedEditorTextLabel: 'Dein Text',
      combinedEditorOverflowMessage: 'Der Text passt nicht in die Tafel. Bitte kürze ihn.',

      // Editor surface — inline text-edit inside the same dialog when
      // option 1 fires on a page slot. All six labels reused verbatim
      // from Commit-1's retired strings (Sebastian ruling 2026-09-04).
      editorSaveButton: 'Speichern',
      editorCancelButton: 'Abbrechen',
      editorAriaLabel: 'Neuer Text für Seite {n}',
      // Titelwunsch + cover-button slice (Sebastian ruling
      // 2026-09-04) — aria-label for the cover title editor. Retained
      // for accessibility on the cover-regen title input; also
      // referenced by the input's aria-label.
      coverEditorAriaLabel: 'Neuer Buchtitel',
      editorSaveError: 'Das konnten wir gerade nicht speichern. Versuch es bitte noch einmal.',

      // ─── Cover-regen slice — nine keys released 2026-09-05 ────
      // Verbatim from Sebastian. DO NOT alter. Server-catalog
      // messages for cover_regen_nothing_to_change / title_too_long /
      // cover_regen_cap_reached share strings 6, 7, 8 exactly; if
      // either surface changes, update the other in the same commit
      // (single source-of-truth is the wording, not the file).
      //
      // KEY 8 (coverRegenCapReachedMessage) is DELIBERATELY AHEAD
      // OF THE BUILD: the sentence references "unten das Motiv aus",
      // pointing at the attempts picker under the image that ships
      // in the pick-earlier slice (before 2026-09-15). Do not
      // rewrite. Tracker: [cover-regen-cap-message-ahead-of-picker].
      //
      // KEY 7 (coverRegenTitleTooLongError) is defense-in-depth
      // server-only. The title input hard-stops at 40 characters via
      // HTML `maxLength`, so this message cannot surface in the UI
      // under normal use. Kept as the server-side fallback for a
      // programmatic-bypass request.
      coverRegenDialogHeading: 'Titel oder Bild ändern',
      coverRegenTitleFieldLabel: 'Buchtitel',
      coverRegenTitleFieldHint:
        'Der Titel steht auf dem Cover und auf dem Buchrücken. Deshalb höchstens 40 Zeichen.',
      coverRegenCommentFieldLabel: 'Was soll am Coverbild anders sein?',
      coverRegenCommentFieldHint:
        'Am besten funktioniert, was weg soll oder was dazukommen soll. Zum Beispiel: keine Blumen im Vordergrund.',
      coverRegenBothEmptyError:
        'Trag einen neuen Titel ein oder schreib, was am Bild anders sein soll.',
      coverRegenTitleTooLongError: 'Der Titel darf höchstens 40 Zeichen haben.',
      // Pick-earlier-attempt slice (Sebastian ruling 2026-09-06) —
      // replacement wording. The pre-slice text pointed at "unten das
      // Motiv" (a picker below the image). The picker is a dialog
      // reached from the Change menu, not a strip under the image, so
      // the pre-slice wording was wrong for the surface. New text names
      // the menu entry the customer is looking for. Server-side mirror
      // at `src/common/errors/error-catalog.ts:cover_regen_cap_reached`
      // stays byte-identical — single source of truth.
      coverRegenCapReachedMessage:
        'Du hast das Cover dreimal neu zeichnen lassen. Über „Ein früheres Bild wählen" kannst du dich für eines der Motive entscheiden.',
      // Sebastian rule: "a limit is only named once one has been
      // spent. Nothing tells the customer about a cap before he has
      // used a re-render." This line renders only when
      // `regen.used >= 1 && regen.remaining >= 1`. Not shown before
      // the first re-render; the coverRegenCapReachedMessage takes
      // over at remaining === 0.
      coverRegenRemainingHint: 'Du kannst das Cover noch zweimal neu zeichnen lassen.',

      // ─── Pick-earlier-attempt slice — four keys released 2026-09-06 ─
      // Verbatim from Sebastian. DO NOT alter. `attemptPickMenuOption`
      // is shared between the cover menu and the page menu (one label,
      // both places). The picker dialog carries no per-image caption
      // by ruling — numbering attempts makes the customer count, and
      // whoever is happy should never think in attempts.
      attemptPickMenuOption: 'Ein früheres Bild wählen',
      attemptPickHeading: 'Welches Bild soll es sein?',
      attemptPickActiveBadge: 'Aktuell',
      attemptPickHint:
        'Du kannst jederzeit wechseln. Zeichnest du neu, ist wieder das neueste Bild aktiv.',

      // Move-panel drag surface — Position-1 Commit-3, Sebastian
      // rulings 2026-09-03. Save error string reuses `editorSaveError`
      // verbatim (single source across dialogs). No hint line: the
      // margin cage walls sit at four different distances for reasons
      // the customer cannot see; he feels the wall when he reaches it
      // and that is enough (Sebastian 2026-09-03).
      movePanelHeading: 'Zieh die Texttafel dorthin, wo Du sie haben möchtest.',
      movePanelSave: 'Speichern',
      movePanelCancel: 'Abbrechen',
      movePanelAriaLabel: 'Texttafel bewegen',
      // Resizable-panel slice (Sebastian ruling 2026-09-17). Aria
      // labels for the two side-edge resize handles + the transient
      // status bubble shown while the server computes the new drawn
      // geometry on pointer release.
      resizeHandleLeftAriaLabel: 'Linken Rand der Texttafel ziehen',
      resizeHandleRightAriaLabel: 'Rechten Rand der Texttafel ziehen',
      resizeSaving: 'Neue Breite wird berechnet…',

      // Re-render (option 3) — Position-1 Commit-4, Sebastian rulings
      // 2026-09-04. Comment is free text, moderated server-side; the
      // rendering wait is measured at ~90s (p50 87.4s on 40 samples
      // 2026-09-04) so "anderthalb Minuten" is honest. Third
      // sentence deliberately dropped — the thumbnail strip + arrows
      // already tell the customer he can navigate away.
      regenCommentHeading: 'Was soll an diesem Bild anders sein?',
      regenCommentPlaceholder:
        'Zum Beispiel: heller, weniger Personen, ein Regenschirm in der Hand.',
      // Sebastian ruling 2026-09-24 follow-up — customer-triggered
      // action: rename from "Neu zeichnen" to "Seite neu erstellen".
      regenSaveButton: 'Seite neu erstellen',
      regenCancelButton: 'Abbrechen',
      // Rendering-in-progress running label. Shown on the affected
      // page's tile while renderState==='rendering' via envelope
      // polling. Two sentences, no third.
      regenRunningLabel: 'Wir zeichnen dein Bild neu. Das dauert etwa anderthalb Minuten.',
      // Second-regen-while-rendering — dialog opens, option 3
      // disabled, inline note (Sebastian released 2026-09-04).
      regenPendingNote: 'Warte kurz, dein Bild wird noch gezeichnet.',
      // Per-page remaining — shown ONLY after the customer has used
      // at least one regen (Sebastian ruling: nothing before, then
      // ability-framing not balance-framing).
      regenRemainingPageOne: 'Du kannst dieses Bild noch einmal neu zeichnen lassen.',
      regenRemainingPageTwo: 'Du kannst dieses Bild noch zweimal neu zeichnen lassen.',

      // Jugendschutz failure-path release (Sebastian ruling 2026-09-10).
      // Character-for-character; do NOT rephrase. The banner sits at
      // the top of the Freigabe screen when at least one slot has
      // renderState === 'retry_available' AND no slot is rendering
      // (suppressed while any slot is still rendering — motion would
      // outdate the wording). The tile-level line + retry button
      // render on the failed tile itself. The cap-exhausted line
      // replaces both retry buttons when the customer has spent all
      // three attempts without ever seeing a working image
      // (server-side gate at scene_retry_exhausted /
      // cover_retry_exhausted).
      failureState: {
        bannerSingle:
          'Ein Bild konnte nicht gezeichnet werden: {SLOT}. Öffne es und zeichne es noch einmal.',
        bannerMultiple:
          'Einige Bilder konnten nicht gezeichnet werden: {SLOTS}. Öffne sie und zeichne sie noch einmal.',
        slotSceneLabel: (n: number) => `Seite ${n}`,
        slotCoverLabel: 'Das Titelbild',
        tileFailedLine: 'Konnte nicht gezeichnet werden',
        // Sebastian ruling 2026-09-24 follow-up — customer-triggered
        // action: rename from "Nochmal zeichnen" to "Nochmal versuchen".
        retryButton: 'Nochmal versuchen',
        coverAnotherTitleButton: 'Anderer Titel',
        capExhaustedLine:
          'Das hat mehrfach nicht geklappt. Wir kümmern uns darum und melden uns bei dir.',
        // ══════════════════════════════════════════════════════════
        // Paket 2 Jugendschutz release-time Sammellauf (Sebastian
        // ruling 2026-09-10). The following strings support the
        // content-refusal path fired by the release endpoint when
        // customer-edited surfaces (scene texts / Widmung /
        // Rückseitentext) fail the release-time content check.
        //
        // All seven strings below are RELEASED as of Sebastian's
        // 2026-09-10 evening ruling (Paket 2 copy release). Character-
        // for-character. DO NOT rephrase.
        // ══════════════════════════════════════════════════════════

        /** RELEASED verbatim 2026-09-10. Ten-refusal ceiling copy —
         *  server returns `release_content_refused_exhausted`, PWA
         *  renders this line in place of the retry button. */
        capExhaustedContentLine:
          'Das hat mehrfach nicht geklappt. Schreib uns kurz, dann schauen wir gemeinsam drauf: support@dreamable.kids',

        /** RELEASED (Sebastian 2026-09-10 evening) — banner for a
         *  single content-refused slot. `{SLOT}` substitutes with the
         *  slot label (`Seite {n}` / `Die Widmung` / `Der Text auf
         *  der Rückseite`). */
        bannerContentSingle:
          'Ein Text passt so nicht in ein Kinderbuch: {SLOT}. Öffne die Stelle und schreib sie um.',

        /** RELEASED (Sebastian 2026-09-10 evening) — banner for
         *  multiple content-refused slots. `{SLOTS}` substitutes with
         *  a comma-joined slot label list. */
        bannerContentMultiple:
          'Einige Texte passen so nicht in ein Kinderbuch: {SLOTS}. Öffne die Stellen und schreib sie um.',

        /** RELEASED (Sebastian 2026-09-10 evening) — Widmung slot
         *  label inside the content-refusal banner. Article included
         *  ("Die Widmung", not bare "Widmung") so the banner reads
         *  naturally with the `{SLOT}` substitution. */
        slotDedicationLabel: 'Die Widmung',

        /** RELEASED (Sebastian 2026-09-10 evening) — Rückseitentext
         *  slot label inside the content-refusal banner. Reads
         *  naturally with the banner article structure. */
        slotBackCoverLabel: 'Der Text auf der Rückseite',

        /** RELEASED (Sebastian 2026-09-10 evening) — tile-level line
         *  under a content-refused image, mirroring `tileFailedLine`
         *  for the image-failure path. */
        tileContentRefusedLine: 'Dieser Text passt so nicht',

        // Sebastian correction slice 2026-09-10 (Part 3.3) — cover
        // CONTENT-REFUSED strings. Fire when
        // `envelope.cover.contentRefused === true` (backend projects
        // it from the latest cover AnchorAttempt's errorClass). The
        // review-screen failure branch swaps the standard "Nochmal
        // zeichnen" + "Anderer Titel" pair for a sharpened tile line
        // + inline title input pre-filled with the current title +
        // rebuild button that fires regenCover({title: newTitle,
        // comment: null}). RELEASED verbatim; do NOT rephrase.
        coverContentRefused: {
          tileLine: 'Konnte nicht gezeichnet werden. Das kann an eurem Titel liegen.',
          titleInputLabel: 'Wie soll euer Buch heißen?',
          // Sebastian ruling 2026-09-24 follow-up — customer-triggered
          // action: rename from "Titel ändern und neu zeichnen" to
          // "Titel ändern und neu erstellen".
          rebuildButtonLabel: 'Titel ändern und neu erstellen',
        },
      },
    },
    // Schritt 2 (Anker-Gate) — Textdatei §Schritt 2 lines 213-238.
    //
    // Foto-Pfad text ONLY. Sebastian ruling 2026-08-05 §1: the
    // Text-Pfad cannot be reached today because CreateBookOrderDto
    // requires facePhotoKey per cast entry; tracker
    // [book-create-order-dto-blocks-text-path] records the DTO gap.
    // When the DTO opens and the envelope grows a path signal, add
    // the Text-Pfad variant (2nd sentence "So haben wir deine
    // Beschreibung umgesetzt." per §AF7-2, Textdatei :222).
    anchor: {
      // Block 4 · 4.2 · Eyebrow line for the Figuren screen (Sebastian
      // ruling 2026-09-09). Renders above the anchor image, beside
      // the gold rule (Eyebrow primitive). The book title
      // (envelope.title, now sourced from titelwunsch fallback per
      // Block 3 corrections) sits below the eyebrow in bold as the
      // main heading of the screen.
      eyebrow: 'EURE FIGUREN',

      // Block 4 · 4.3 · Explanatory text BELOW the anchor image
      // (Sebastian ruling 2026-09-09). Two variants — singular vs
      // plural — gated by figure count. Persons and animals count
      // the same in the input.
      //
      // GATING SIGNAL GAP: the envelope carries no cast-count field
      // today. `envelope.entitiesCount` is post-extraction (0 at
      // anchor_review). `intake.cast` is not surfaced through
      // `resolveIntakeProjection` (only the derived.rejection block
      // reaches the wire). Until the backend adds a figure-count
      // signal — probably `envelope.figureCount` from
      // `intake.cast.length` — AnchorReviewScreen renders the PLURAL
      // variant unconditionally. Plural is grammatically the honest
      // default when the count is unknown; a 1-cast book will show
      // "Hauptfiguren" (mild plural for a singleton) rather than
      // silently mislead. The singular string ships now so the
      // switch is one line the day the signal lands.
      frozenTextSingular:
        'Das ist die Hauptfigur eures Buchs. Schau sie dir genau an: Gesicht, Haare, Augen, Statur. Wenn du zufrieden bist, sieht sie auf jeder Seite genauso aus. Die Kleidung wechselt, wenn die Szene es verlangt.',
      frozenTextPlural:
        'Das sind die Hauptfiguren eures Buchs. Schau sie dir genau an: Gesicht, Haare, Augen, Statur. Wenn du zufrieden bist, sehen sie auf jeder Seite genauso aus. Die Kleidung wechselt, wenn die Szene es verlangt.',
      // Pre-Block-4 wording — retained under the same key for tests
      // that still reference `frozenText`. Not consumed by the screen
      // after Block 4.
      frozenText:
        'Das ist das Titelbild deines Buchs. Schau es dir genau an: Gesicht, Haare, Augen, Statur. Wenn du zufrieden bist, sehen alle auf jeder Seite genauso aus. Die Kleidung wechselt, wenn die Szene es verlangt.',

      // Buttons — Textdatei :227 verbatim (buttonAccept +
      // buttonZurueckPersonen). Sebastian ruling 2026-09-24 (batch
      // photo-swap slice, follow-up) — buttonRegen renamed from
      // "Neu zeichnen" to "Figurenbild neu erstellen" so the
      // customer-triggered action reads as "create", not "draw"
      // (task-for-the-customer connotation).
      // K-04: NO Klammerzusatz zum Kontingent (v3.1 §6 line 301).
      buttonAccept: 'Passt so',
      buttonRegen: 'Figurenbild neu erstellen',
      buttonZurueckPersonen: 'Zurück zu den Personen',

      // Staffelter Regen-Hinweis — Textdatei :237. Rendered under the
      // outline button when remainingAnchor === 1 (state 3).
      regenHintAfterFirst: 'Noch ein Versuch mit dieser Beschreibung.',

      // State 4 HintBox — Textdatei :238. Replaces the outline button
      // when remainingAnchor === 0. K-04 discipline: no count, no
      // "aufgebraucht", no gray button.
      //
      // W35 (Sebastian ruling 2026-10-14): the pre-fix single text
      // ("Wenn es noch nicht passt, ändere die Beschreibungen deiner
      // Personen. Danach zeichnen wir neu.") referenced a non-existent
      // Personen field — the step has no description per person, only
      // name/age/gender/relation/photo. Two variants now, chosen by
      // whether a voluntary photo swap is still available (same signal
      // that drives the "Foto ändern" outline button —
      // `voluntaryPhotoSwapsRemaining > 0` at
      // AnchorReviewScreen.tsx:151-152). References to "Foto ändern"
      // and "Ein früheres Bild wählen" are the current button labels
      // (`imageRefused.swapButtonLabel` at :3450 +
      // `anchorPickerMenuEntry` at :4057).
      regenExhaustedBodyWithSwap:
        'Wenn es noch nicht passt, tausche über „Foto ändern“ das Foto der Person aus. Danach zeichnen wir neu. Oder nimm über „Ein früheres Bild wählen“ eins der vorherigen Bilder.',
      regenExhaustedBodyNoSwap:
        'Über „Ein früheres Bild wählen“ kannst du eins der vorherigen Bilder nehmen.',

      // State 6 (anchor_attempt_stale) — v3.1 §6 line 292: "Toastzeile
      // über der Fläche, Bild und Knöpfe auf Deckkraft 40 %. Kein Rot,
      // kein Kasten, kein Modal." Sebastian ruling 2026-08-05 §3:
      // inline element, not a toast framework. Text from ERROR_CATALOG
      // (`src/common/errors/error-catalog.ts:828` — anchor_attempt_stale
      // user_message) so the customer sees the same wording whether
      // the error surfaces here or via the mutation envelope.
      staleLine:
        'Von deinem Titelbild gibt es schon eine neuere Version. Wir laden sie dir gerade.',

      // State 7 (anchor_not_in_review) — v3.1 §6 line 293. Text from
      // ERROR_CATALOG (:810 — anchor_not_in_review user_message).
      // Sebastian ruling 2026-08-05 §4: refetch → route from the
      // fresh envelope status (G12 discipline), never a hardcoded
      // target. Bar holds ≥ 800 ms so it does not flash.
      notInReviewBody:
        'Der aktuelle Schritt für dieses Buch hat sich verändert. Wir laden ihn neu für dich.',

      // A11y labels for the StageView reuse (title-overlay big view).
      bigViewAriaLabel: 'Titelbild',
      imageAlt: 'Titelbild deines Buchs',

      // Anchor picker (Sebastian ruling 2026-09-08) — verbatim, per
      // Sebastian's release. Menu entry + per-tile label (interpolates
      // 1-indexed attempt number) + badge on the currently-active tile.
      // Rationale for shipping the picker at anchor_review: "Cover and
      // scenes both let the customer choose between his earlier
      // attempts. The anchor does not. Three re-draws and he gets the
      // third, even if the first was the best — and the anchor decides
      // what the figures look like on every page of the book."
      anchorPickerMenuEntry: 'Ein früheres Bild wählen',
      anchorPickerVersionLabel: (n: number) => `Version ${n}`,
      anchorPickerActiveBadge: 'Aktuell',
    },
    // Position-5 Commit 6b (Sebastian ruling 2026-09-02) — summary
    // review gate. Verbatim slots supplied by Sebastian; nothing is
    // hard-coded at a call site (including aria-labels). Rendered by
    // SummaryReviewScreen at status = summary_review; the attempt
    // indicator interpolates {n}; capWarningBeforeThirdRun fires
    // BEFORE the third run (customer sees it while runCount === 2);
    // approveNotice sits inline above the release button, not after.
    summaryReview: {
      // Block 3 · 3.1 · Eyebrow line (Sebastian ruling 2026-09-09).
      // First screen where the Eyebrow primitive from Block 1 renders.
      // Uppercase small caps beside a gold rule; sits above the
      // heading in State 2 (generation-failed) and State 4 (ready-to-
      // review). NOT rendered on State 1 (self-write, own heading) or
      // State 3 (loader).
      eyebrow: 'EURE GANZ PERSÖNLICHE GESCHICHTE',
      // Block 2 · 2.10 · Post-Weiter loading screen (Vormodell writing
      // the summary, 10-20 s). Sebastian ruling 2026-09-09: replace
      // whatever stands there today with heading + short body,
      // centred, spinner BELOW the text (visual order enforced by
      // SummaryReviewScreen State 3 render).
      generatingHeading: 'Wir lesen, was du uns erzählt hast',
      generatingBody: 'Das dauert einen kurzen Moment.',
      heading: 'Das wird euer Buch',
      // Block 3 · 3.3 · Sub-line REPLACE (Sebastian ruling 2026-09-09).
      // The dropped second sentence ("Alles, was hier steht, kommt so
      // ins Buch.") is no longer true — the customer can change texts
      // later on the finished book.
      subheading: 'Lies in Ruhe, ob das die Geschichte ist, die du dir vorgestellt hast.',
      // Sebastian ruling 2026-09-21 (follow-up) — shared 3-attempt
      // budget across direct edits AND change requests. One counter
      // line for both ways. Interpolates remaining count (3, 2, 1);
      // separate `capExhaustedLine` covers the after-third state.
      attemptsRemainingLine: (remaining: number): string =>
        remaining === 1
          ? 'Du kannst die Zusammenfassung noch einmal anpassen.'
          : `Du kannst die Zusammenfassung noch ${remaining}-mal anpassen.`,
      capExhaustedLine: 'Das war die letzte Anpassung. Wenn alles passt, geht es weiter.',
      // Sebastian ruling 2026-09-21 (follow-up) — collapsed change-
      // request toggle. Opens the "Was soll anders sein?" area
      // beneath the primary edit button.
      openChangeRequestButton: 'Oder sag uns, was anders sein soll',
      // Retired attempt indicator (pre-2026-09-21 "Änderung X von 3")
      // kept for the refused screen which reuses `attemptIndicator`
      // downstream; also kept for backwards-compat with existing
      // consumers in the refused branch below.
      attemptIndicator: (n: number) => `Änderung ${n} von 3`,
      changeFieldLabel: 'Was soll anders sein?',
      // Block 3 · 3.5 · Change-field examples (Sebastian ruling
      // 2026-09-09). Three examples ABOVE the field, stay visible
      // while typing. Placeholder inside the field is short.
      changeFieldExamplesLead: 'Zum Beispiel:',
      changeFieldExample1: '„Bitte ausführlicher, mit mehr Details zu jeder Station."',
      changeFieldExample2: '„Statt der Katze soll es ein Hund sein."',
      changeFieldExample3: '„Der Tag im Zoo soll raus, dafür lieber der Nachmittag am See."',
      changeFieldPlaceholder: 'Schreib, was anders sein soll.',
      retryButton: 'Neu schreiben',
      approveButton: 'Passt so',
      // Block 3 · 3.6 · Info line REPLACE (Sebastian ruling 2026-09-09).
      // The pre-follow-up wording ("Ab hier bleibt die Geschichte, wie
      // sie ist.") was no longer true — customer can edit the texts
      // later on the finished book.
      approveNotice:
        'Danach entstehen die Bilder. Die Texte kannst du später am fertigen Buch noch ändern.',
      retryLoading: 'Wir schreiben die Geschichte neu',
      releaseLoading: 'Wir legen los',
      selfWriteLoading: 'Wir übernehmen deinen Text',
      retryErrorGeneric:
        'Das hat gerade nicht geklappt. Versuch es noch einmal — dieser Versuch zählt nicht mit.',
      retryErrorProviderDown:
        'Wir kommen im Moment nicht durch. Warte einen Augenblick und versuch es noch einmal, dieser Versuch zählt nicht mit.',
      generationFailed: 'Beim Schreiben ist etwas schiefgegangen. Versuch es noch einmal.',
      // Paket 1 (Sebastian ruling 2026-09-10) — V7 pre-model refusal
      // banner + edit-and-retry surface. Fires when
      // `envelope.summaryReview.refused === true` (INHALTSFILTER
      // inappropriate-content or VERSTÄNDLICHKEIT invalid-input). The
      // OpenAI refusal reason is NEVER surfaced — backend logs it,
      // never projects it. Customer-facing wording is PLACEHOLDER
      // pending Sebastian's authorship (same pattern as
      // `photoRejected.*Placeholder` strings released later). Passt
      // so is not rendered on this screen at all — the customer's
      // only affordance is edit + retry (existing three-run limit +
      // self-write fallback stay unchanged per Paket 1 §4).
      // Paket 1 amendment (Sebastian ruling 2026-09-10 evening) —
      // released customer-facing copy for the refused state. Replaces
      // the placeholder heading + body from 93efe58; adds four
      // refused-specific keys that override the reused State-4
      // wording so the screen reads correctly when there IS no
      // summary to review yet (no reused eyebrow-heading claiming
      // "Deine Zusammenfassung"; no reused "Was soll anders sein?"
      // label that presumes a prior summary to change; no reused
      // "Änderung X von 3" indicator; three fresh example lines
      // framed as descriptions instead of change requests).
      refused: {
        screenHeading: 'Das lässt sich so nicht erzählen',
        // Sebastian ruling 2026-09-10 late evening — banner heading +
        // body updated to name BOTH fields (Beschreibung + Titel). The
        // refused screen now renders a title input above the
        // description textarea; the customer sees both his inputs side
        // by side and edits whichever caused the refusal. V7's refusal
        // signal stays field-agnostic — the model does not have to say
        // which field failed.
        heading: 'Daraus können wir kein Kinderbuch machen',
        body: 'Schau dir deine Beschreibung und deinen Titel noch mal an. Ein Anlass, ein Erlebnis oder eine Idee reicht schon.',
        // Label above the new title input, rendered above the
        // description textarea on the refused screen.
        titleFieldLabel: 'Wie soll euer Buch heißen?',
        changeFieldLabel: 'Worum soll es gehen?',
        changeFieldPlaceholder: 'Beschreib, worum es in eurem Buch gehen soll.',
        changeFieldExample1: '„Unser Urlaub am Meer, Mia hat den ganzen Tag Muscheln gesammelt."',
        changeFieldExample2: '„Levs erster Tag in der Kita."',
        changeFieldExample3: '„Jona als Feuerwehrmann, der eine Katze vom Dach rettet."',
        attemptIndicator: (n: number) => `Versuch ${n} von 3`,
      },
      capWarningBeforeThirdRun:
        'Das ist die letzte von drei Änderungen. Danach kannst du die Zusammenfassung selbst schreiben.',
      // Jugendschutz cap-exhaustion support line (Sebastian ruling
      // 2026-09-10 late evening — released copy). One message + one
      // address wherever a customer runs out of attempts. Reused by:
      //   - selfWriteBlockedByTitle (title still in play + last
      //     refusal was inappropriate_content at cap-reached)
      //   - selfWriteRefusalCapReached (two fatal V7 refusals in
      //     self-write mode)
      // Same string value as backend catalog
      // release_content_refused_exhausted / self_write_blocked_by_title
      // / self_write_refusal_cap_reached userMessage — deliberate
      // duplication so per-surface copy can drift later without a
      // rename here, at the cost of one authoring point to keep in
      // sync (Sebastian curates both).
      runOutOfAttemptsSupportLine:
        'Das hat mehrfach nicht geklappt. Schreib uns kurz, dann schauen wir gemeinsam drauf: support@dreamable.kids',
      selfWrite: {
        // Sebastian ruling 2026-09-21 (follow-up) — primary edit
        // button on the summary screen; opens the prefilled editor.
        openEditorButton: 'Zusammenfassung bearbeiten',
        heading: 'Zusammenfassung bearbeiten',
        instruction:
          'Bearbeite die Zusammenfassung so, wie sie sein soll. Sie ist die Grundlage für dein Buch.',
        textareaLabel: 'Was passiert in eurem Buch?',
        textareaPlaceholder: 'Wer dabei ist, wo ihr seid, was ihr macht, und wie der Tag ausgeht.',
        submitButton: 'Änderung speichern',
        cancelButton: 'Abbrechen',
        charHint: 'Fünf bis sieben Sätze',
        // V7-fatal-on-self-write refusal line (Sebastian ruling
        // 2026-09-10 late evening — reuses the exact blocklist copy so
        // "the customer sees one consistent message and never learns
        // which check caught him"). Rendered under the textarea when
        // the projection carries selfWriteRefused === true — the
        // worker refused on kind='inappropriate_content' between
        // submit and this render. Same string value as backend catalog
        // self_write_blocked userMessage so the two paths (immediate
        // blocklist ApiError + polling-driven V7 refusal) display
        // identical text; Sebastian curates both.
        contentRefusedLine: 'Diesen Text können wir nicht verwenden. Schreib ihn bitte anders.',
      },
      storyScreen: {
        readOnlyNote: 'Die Geschichte folgt der Zusammenfassung, die du freigegeben hast.',
      },
      // Sebastian ruling 2026-09-17 — verification-pending block on
      // the summary screen. Sits next to a disabled "Passt so" button
      // when `envelope.customerEmailVerified === false`. Consumer:
      // `VerificationPendingBlock.tsx`. Wording released
      // 2026-09-17 evening (headline + two-line body + spam hint +
      // resend CTA + pending/ack/429/generic states).
      verificationPending: {
        headline: 'Bitte bestätige deine E-Mail-Adresse',
        bodyLine1: (email: string) => `Wir haben eine Bestätigungs-Mail an ${email} geschickt.`,
        bodyLine2: 'Klicke auf den Link darin, damit es weitergeht.',
        spamHint: 'Schau bitte auch im Spam-Ordner nach.',
        resendCta: 'E-Mail nochmal senden',
        resendPending: 'Wird versendet …',
        resendAck: 'E-Mail wurde erneut versendet.',
        throttled:
          'Du hast in der letzten Stunde schon oft neu angefragt. Bitte warte einen Moment.',
        genericError: 'Das hat nicht geklappt. Bitte versuche es später erneut.',
      },
    },
    // Schritt 3 (Geschichte) — Textdatei §Schritt 3 lines 278-285
    // (v2, 2026-08-05 Abend). Position-1 Commit-1 rewire (Sebastian
    // ruling 2026-09-03): the per-scene editor is retired here;
    // scene-text edits move to the finished book (customer_review).
    // The screen is read-only — page rows expand to show the text.
    // Weiter still fires leaveTextEditing to advance state (see
    // GeschichteScreen JSDoc).
    geschichte: {
      // Block 5 review correction (Sebastian 2026-09-09): possessive
      // switched from "deinem" (singular) to "eurem" (plural) to
      // match "So steht es in eurem Buch" and the rest of this wave.
      // The body line under this heading keeps "du" — "du" for the
      // person acting is correct; only the possessive changes.
      erwartungskarteHeading: 'Was in eurem Buch passiert',

      // Block 5 · 5.1 (Sebastian ruling 2026-09-09) — eyebrow +
      // heading for the text_editing screen. Rendered via the
      // Eyebrow primitive above the h1 main heading. Book title
      // (`envelope.title`) renders in bold BELOW the heading as the
      // section identifier.
      eyebrow: 'EURE GESCHICHTE',
      heading: 'So steht es in eurem Buch',

      // Block 5 · 5.2 (Sebastian ruling 2026-09-09) — each of the
      // twenty scene rows is labelled with its page number ("Seite
      // N"), not "Szene A/B/C/D". All 20 rows render OPEN one below
      // the other; no per-scene expander. `pageRowHeading` = the
      // visible label above each scene text.
      pageRowHeading: 'Seite {n}',

      // Klappzeile 1 — Position-1 Commit-1 rewire (Sebastian ruling
      // 2026-09-03): the "und ändern" clause dropped when the row
      // became read-only. Trailing count is `envelope.pages.length`
      // (20 per D-27). RETIRED at Block 5 c1 (Sebastian ruling
      // 2026-09-09) — the row wrap is gone; all twenty scenes now
      // render open inline. Kept here for the trailing-count string
      // pattern only; `klappSeitenLabel` is orphaned.
      klappSeitenLabel: 'Seite für Seite lesen',
      klappSeitenTrailing: '{seiten} Seiten',

      // Klappzeile 2 — Textdatei :285 verbatim. Hidden until
      // `envelope.entitiesCount` ships per Sebastian ruling
      // 2026-08-05 §5c-2. Open state deferred per §5c-3 —
      // decision E-03 + Amendment §AF7-6 make it an editable list,
      // out of scope for this commit.
      klappDingeLabel: 'Dinge, die im Buch vorkommen',
      klappDingeTrailing: '{count}',

      // Block 5 · 5.3 (Sebastian ruling 2026-09-09) — button label
      // REPLACED: pre-Block-5 "Weiter zur Vorschau" → new
      // "Weiter zu euren Vorschaubildern". Names what the customer
      // actually sees next (four preview images), not the abstract
      // step name.
      weiterCta: 'Weiter zu euren Vorschaubildern',

      // Per-page aria label for the row toggle button.
      pageRowAriaLabel: 'Seite {n}',

      // Weiter error — above the Weiter button when the leave-text-
      // editing call throws (transition failure). Post-Commit-1 the
      // two-phase flush is gone; only this single variant remains.
      weiterPhase2Error: 'Das hat gerade nicht geklappt. Versuch es bitte noch einmal.',

      // Position-1 Commit-1 (Sebastian ruling 2026-09-03) — the two
      // counter labels below are RETAINED as dead strings alongside
      // the orphaned CharacterCounter component. Both leave the tree
      // with CharacterCounter in the dead-code follow-up commit
      // named on `[book-post-p1c1-deadcode-scene-edit-hook]`. Kept
      // here so this commit stays about one theme (Geschichte
      // read-only + endpoint swap) instead of two.
      counterLabel: '{n} / {max}',
      counterLabelOverLimit: '{n} / {max} — etwas zu lang',
    },
    // fix/text-field-limits (Sebastian ruling 2026-09-29 verbatim) —
    // two shared strings used by every free-text input in the book
    // flow when the value overshoots its cap.
    //
    // - overLimitBlockingMessage fires client-side when the current
    //   value exceeds the field's cap. This can only happen on
    //   prefilled surfaces (summary self-write reads the model's
    //   zusammenfassung, which the pre-model does not bound); the
    //   input's `maxLength` prevents typing/pasting past the cap, so
    //   customers who start empty never see this message. `{max}` is
    //   substituted from the field's own limit constant — no
    //   hardcoded number here.
    //
    // - backendLengthErrorFallback fires when the server still
    //   returns a length error (client-side maxLength somehow
    //   bypassed). No cap number in the copy because the fallback
    //   applies across fields with different caps.
    textLimits: {
      overLimitBlockingMessage: 'Dein Text ist zu lang. Kürze ihn auf höchstens {max} Zeichen.',
      backendLengthErrorFallback: 'Dein Text ist zu lang. Bitte kürze ihn etwas.',
    },
    // Probecover Stage 1 — lead-magnet "Probe-Figurenbild".
    //
    // EVERY string below is a PLACEHOLDER of the form
    // `[PLACEHOLDER book.probe.<key>]`. Sebastian releases final
    // German wording later; the orchestrator + endpoints are
    // production-shaped, only the customer-facing copy is parked. The
    // tracker row `[probe-wording]` collects the location list for the
    // wording session.
    //
    // Reuses existing strings verbatim where they fit:
    //   book.personen.qGenderMale / qGenderFemale / qGenderNone
    //       — gender labels (identical semantics on the probe form).
    //   book.personen.lightgate* keys — email/password/terms/
    //       Turnstile/Weiter copy on the LightGate dialog.
    //   book.personen.beruhigungszeile — Datenschutz-reassurance line.
    //   account.password.requirements.{minLength,uppercase,digit}
    //       — password checklist.
    //   book.textLimits.overLimitBlockingMessage — form-level length
    //       validation fallback.
    // Probecover Stage 2 — approved customer-facing wording (Sebastian
    // release 2026-10-15). Everything in this block ships verbatim.
    // Reused booth-verbatim labels for the card controls live in
    // `ProbeCard.tsx` directly (Stage 1 commit `7fd24a9`); the
    // following keys cover the surrounding route + overlays.
    //
    // Keys that STAY placeholders — Sebastian did not release
    // wording for these in Stage 2:
    //   form.addPersonCta         (ProbeCard uses the literal "+ Person",
    //                              key is unused and kept as a slot for
    //                              a possible re-styled CTA later)
    //   form.cardPersonHeading / .cardTierHeading / .personTypePerson /
    //     .personTypeTier / .photoHint / .ageLabel / .genderLabel /
    //     .speciesLabel / .removeCardCta
    //                             (ProbeCard uses the booth-verbatim
    //                              literals Person · Tier · Person N ·
    //                              FOTO · ALTER · GESCHLECHT · TIERART ·
    //                              Entfernen · Andere (z.B. Pony)
    //                              inline — same discipline as Stage 1).
    probe: {
      routeHeading: 'Seht euch als gezeichnete Figuren',
      routeSubheading:
        'Lade für jede Person ein Foto hoch. Wir zeichnen euch so, wie ihr im Buch ausseht.',
      form: {
        sectionTitle: 'Wer soll mit aufs Bild?',
        // PLACEHOLDER — Sebastian release pending
        addPersonCta: '[PLACEHOLDER book.probe.form.addPersonCta]',
        // PLACEHOLDER — Sebastian release pending
        cardPersonHeading: '[PLACEHOLDER book.probe.form.cardPersonHeading]',
        // PLACEHOLDER — Sebastian release pending
        cardTierHeading: '[PLACEHOLDER book.probe.form.cardTierHeading]',
        // PLACEHOLDER — Sebastian release pending
        personTypePerson: '[PLACEHOLDER book.probe.form.personTypePerson]',
        // PLACEHOLDER — Sebastian release pending
        personTypeTier: '[PLACEHOLDER book.probe.form.personTypeTier]',
        // PLACEHOLDER — Sebastian release pending
        photoHint: '[PLACEHOLDER book.probe.form.photoHint]',
        // PLACEHOLDER — Sebastian release pending
        ageLabel: '[PLACEHOLDER book.probe.form.ageLabel]',
        // PLACEHOLDER — Sebastian release pending
        genderLabel: '[PLACEHOLDER book.probe.form.genderLabel]',
        // PLACEHOLDER — Sebastian release pending
        speciesLabel: '[PLACEHOLDER book.probe.form.speciesLabel]',
        // PLACEHOLDER — Sebastian release pending
        removeCardCta: '[PLACEHOLDER book.probe.form.removeCardCta]',
        submitCta: 'Figurenbild erstellen',
        capReachedNote: 'Mehr als 9 Personen passen nicht auf ein Bild.',
      },
      lightGate: {
        // Sebastian release 2026-10-15 — probe-specific primary CTA
        // on the LightGate register form. Replaces the book-catalog
        // key `lightgateRegisterCta` ("Weiter zur Geschichte") which
        // read wrong in the probe funnel.
        registerCta: 'Figurenbild erstellen',
        // Sebastian release 2026-10-15 — probe-specific sr-only
        // DialogDescription for the register + login modes.
        // Replaces the book-catalog key `lightgateSubtitle`
        // ("Damit dein Buch gespeichert bleibt …") which mentioned
        // "Buch" in a probe context.
        description:
          'Mit deinem Konto erstellen wir dein Figurenbild und schicken es dir per E-Mail.',
      },
      waiting: {
        heading: 'Wir zeichnen euch gerade',
        body: 'Das dauert ein bis zwei Minuten. Bleib gern auf dieser Seite.',
      },
      result: {
        heading: 'So seht ihr gezeichnet aus',
        watermarkCaption:
          'Das ist eine Vorschau. Im Buch zeichnen wir euch passend zu eurer Geschichte, ohne Logo und in Druckqualität.',
        anotherCta: 'Noch ein Figurenbild',
        // Stage 2 — primary CTA on the result screen that routes the
        // customer into /book with the Personen step pre-filled from
        // the probe's cast + photos. See
        // `pwa/src/features/probe/routes/FigurenRoute.tsx` + the
        // transfer handler.
        toBookCta: 'Daraus ein Buch machen',
        capReachedBody:
          'Du hast beide Vorschauen genutzt. Im Buch kannst du dein Figurenbild neu zeichnen lassen, wenn es dir noch nicht gefällt.',
        // F15 (Sebastian ruling 2026-10-17 Option B) — client-
        // side mirror of the backend error catalog entry
        // `probe_cap_reached.userMessage` at
        // `src/common/errors/error-catalog.ts:2090-2091`. Shown
        // on the probe result screen when the "Noch ein
        // Figurenbild" button is clicked while the user is at
        // the 2-per-account cap, so the click is never a silent
        // no-op. Byte-identical to the backend entry — keep in
        // lockstep: if the backend text changes, update this
        // mirror in the same commit.
        capReachedInline:
          'Du kannst maximal zwei Figurenbilder erstellen. Aus deinem Figurenbild kannst du jetzt euer Buch machen.',
      },
      failure: {
        heading: 'Das hat leider nicht geklappt',
        body: 'Beim Zeichnen ist etwas schiefgegangen. Das zählt nicht zu deinen Vorschauen. Versuch es bitte noch einmal, gern auch mit anderen Fotos.',
      },
    },
  },
  // Book-arrived review + share landing (/book/:bookOrderId/review).
  // All strings are PLACEHOLDERS pending Sebastian's wording approval.
  // Do NOT replace a placeholder with ad-hoc copy — wait for the
  // approved German text.
  bookReview: {
    page: {
      headline: 'Wie gefällt euch euer Buch?',
      subtitle: 'Wir freuen uns über deine Meinung. Und wenn du magst, zeig das Buch auch anderen.',
      shareHeadline: 'Euer Buch mit anderen teilen',
      shareButton: 'Teilen',
      notFound: 'Dieses Buch können wir gerade nicht finden.',
    },
    share: {
      sheetTitle: 'Euer Buch teilen',
      sheetSubtitle: 'Wie möchtest du teilen?',
      optionCoverLabel: 'Mit Cover',
      optionCoverSubtitle: 'Das Titelbild eures Buchs und deine Nachricht',
      optionTextLabel: 'Nur Nachricht',
      optionTextSubtitle: 'Ein paar eigene Worte mit dem Link zu Dreamable',
      customLabel: 'Deine Nachricht',
      customPlaceholder: 'Schreib ein paar Worte dazu …',
      customEmailSubject: 'Schau mal, unser Buch',
      coverDownloadButton: 'Cover herunterladen',
      coverDownloadDone: 'Das Cover ist in deinem Download-Ordner.',
      // Default accompanying text for the "Mit Cover" option. Prefills
      // the textarea on cover-select and acts as a defense-in-depth
      // default inside the shared payloadText builder — guarantees
      // non-empty text goes with the link on every channel even if the
      // user clears the textarea. Also surfaces on the mobile
      // navigator.share({files, text}) call so the file + accompanying
      // text travel together.
      coverMessage:
        'Schau mal, das ist unser Buch von Dreamable. Eine Geschichte, in der unser Kind selbst die Hauptrolle spielt. Hier kannst du auch eins machen:',
      // Desktop-only hint shown next to the download button + toast
      // after the cover is downloaded. The channel buttons on desktop
      // can only share text + link; the customer must drag/attach the
      // downloaded cover file to the chat / mail themselves.
      coverDesktopAttachHint: 'Du kannst es jetzt an deine Nachricht anhängen.',
    },
    ratingQuestion: 'Wie viele Sterne gibst du eurem Buch?',
  },
} as const;
